'use strict';

var jsxRuntime = require('react/jsx-runtime');
var cauldronReact = require('@deque/cauldron-react');
var React = require('react');
var reactI18next = require('react-i18next');
var i18n = require('i18next');
var classnames = require('classnames');
var utils = require('@react-aria/utils');
var accessibilityStandards = require('@deque/accessibility-standards');
var dateFns = require('date-fns');
var history$1 = require('history');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var React__default = /*#__PURE__*/_interopDefault(React);
var i18n__default = /*#__PURE__*/_interopDefault(i18n);
var classnames__default = /*#__PURE__*/_interopDefault(classnames);

const instance = i18n__default.default.createInstance({
    lng: 'en-US',
    fallbackLng: 'en-US',
    debug: process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test',
    ns: ['launchpad'],
    defaultNS: 'launchpad',
    interpolation: {
        escapeValue: false // react is already xss-safe
    },
    nsSeparator: false,
    keySeparator: false
});
instance.init();

// The below functions are mirroring the exported Trans and useTranslation functions
// from react-i18next in order to set the default namespace and i18n instance
function Trans({ ns = 'launchpad', ...props }) {
    return jsxRuntime.jsx(reactI18next.Trans, { ns: ns, i18n: instance, ...props });
}
function useTranslation(...args) {
    const [ns = 'launchpad', options] = args;
    return reactI18next.useTranslation(ns, { i18n: instance, ...options });
}

const AxeClean = React.forwardRef(({ headingLevel = 2, ...props }, ref) => {
    const { t } = useTranslation();
    const Heading = (typeof headingLevel === 'number' && headingLevel >= 1 && headingLevel <= 6
        ? `h${headingLevel}`
        : 'h2');
    return (jsxRuntime.jsx(cauldronReact.EmptyState, { ref: ref, ...props, heading: jsxRuntime.jsx(Heading, { children: t('No automated accessibility issues detected!') }), description: t('Your scan is axe Clean. Remember that comprehensive accessibility also requires manual testing.') }));
});
AxeClean.displayName = 'AxeClean';

const defaultConfiguration = {
    screenshots: false,
    integrations: false
};
exports.ConfigurationContext = React.createContext(defaultConfiguration);
function configure(options) {
    exports.ConfigurationContext = React.createContext({
        ...defaultConfiguration,
        ...options
    });
}
const useConfiguration = () => {
    return {
        ...defaultConfiguration,
        ...React.useContext(exports.ConfigurationContext)
    };
};

/**
 * When a component needs to track an element's resize, useResizeObserver
 * via a ref or callback function will track the first resize entry invoking
 * the callback function and/or setting the value of the returned ref.
 *
 * @example
 * const elementRef = useRef<HTMLElement>()
 * useResizeObserver<HTMLElement>({ element: elementRef, root: document.body, callback })
 * return <span ref={elementRef}>...</span>
 */
function useResizeObserver(options, dependencyList = []) {
    const { element, callback, active = true } = options;
    const ref = React.createRef();
    const resolveElement = () => element && 'current' in element
        ? element.current
        : element;
    const [resolvedElement, setResolvedElement] = React.useState(resolveElement());
    React.useEffect(() => {
        setResolvedElement(resolveElement());
    });
    React.useEffect(() => {
        if (!('ResizeObserver' in globalThis) ||
            typeof ResizeObserver !== 'function') {
            return;
        }
        if (!active || typeof element === 'undefined' || element === null) {
            return;
        }
        // If the resolved element does not currently match, we don't want run the resize observer yet
        if (element instanceof HTMLElement && element !== resolvedElement) {
            return;
        }
        if (!(element instanceof HTMLElement) &&
            !(element.current instanceof HTMLElement)) {
            // eslint-disable-next-line no-console
            console.warn('An element or ref was provided to useResizeObserver that was not an HTMLElement.');
            return;
        }
        const handleResize = ([entry]) => {
            ref.current = entry;
            if (typeof callback === 'function') {
                callback(entry);
            }
        };
        const observer = new ResizeObserver(handleResize);
        observer.observe(resolveElement());
        return () => {
            observer.disconnect();
        };
    }, [element, resolvedElement, callback, active, ...dependencyList]);
    return ref;
}

// used for components that need to access and set the ref
function setRef(ref, element) {
    if (!ref) {
        return;
    }
    if (typeof ref === 'function') {
        ref(element);
    }
    else {
        // eslint-disable-next-line
        // @ts-ignore
        // we need to overwrite the existing value in some special cases
        ref.current = element;
    }
}

/**
 * When a component needs to track an internal ref on a component that has a
 * forwarded ref, useSharedRef will return a MutableRefObject<T> that will
 * correctly invoke the parent ref as well providing an internal ref.
 *
 * @example
 * React.forwardRef(function Component({ children }, ref) {
 *   const internalRef = useSharedRef<HTMLElement>(ref)
 *   if (internalRef.current) {
 *     // do something with the internal ref...
 *   }
 *   return (<div ref={internalRef}>...</div>)
 * })
 */
function useSharedRef(ref) {
    const internalRef = React.useRef(null);
    React.useEffect(() => {
        setRef(ref, internalRef.current);
    }, [ref]);
    return internalRef;
}

const isCheckpointTagInfo$1 = (tagInfo) => {
    return 'checkpoint' in tagInfo && !!tagInfo.checkpoint;
};
const getCheckpoints = (tags) => {
    if (!Array.isArray(tags)) {
        return [];
    }
    const checkpoints = [];
    for (const tag of tags) {
        const tagInfo = accessibilityStandards.getTagInfo(tag);
        if (tagInfo && isCheckpointTagInfo$1(tagInfo)) {
            checkpoints.push(tagInfo);
        }
    }
    return checkpoints;
};
/**
 * Returns a list of all matching  checkpoint tags with tag info from a list of axe-core tags.
 *
 * @example
 * const checkpointsTagInfo = useCheckpointsTagInfo(['cat.color', 'wcag21aa', 'wcag143', 'en-301-549'])
 */
function useCheckpointsTagInfo(tags) {
    return React.useMemo(() => getCheckpoints(tags), [JSON.stringify(tags)]);
}

const isWCAGCheckpointTagInfo = (tagInfo) => {
    return 'checkpoint' in tagInfo && tagInfo.standard === 'WCAG';
};
function useWCAGCheckpointsTagInfo(tags) {
    const checkpoints = useCheckpointsTagInfo(tags);
    return React.useMemo(() => checkpoints.filter(isWCAGCheckpointTagInfo), [checkpoints]);
}

const isRulesetTag$1 = (tag) => tag in accessibilityStandards.rulesets;
/**
 * Returns the first matching WCAG standard ruleset from a list of axe-core tags.
 *
 * @example
 * const wcagStandard = useWCAGStandard(['cat.color', 'wcag21aa', 'wcag143', 'en-301-549'])
 */
function useWCAGStandard(tags) {
    return React.useMemo(() => {
        if (!Array.isArray(tags)) {
            return null;
        }
        // There should only be a single WCAG standard tag in the list of tags, so
        // return the first one. If there's multiple wcag standard tags it's likely
        // an implementation bug.
        for (const tag of tags) {
            const tagInfo = accessibilityStandards.getTagInfo(tag);
            if (tagInfo?.standard === 'WCAG' && isRulesetTag$1(tag)) {
                return accessibilityStandards.rulesets[tag];
            }
        }
        return null;
    }, [JSON.stringify(tags)]);
}

const IS_NOT_PRODUCTION = process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test';
function formatSelector({ selector }) {
    if (Array.isArray(selector)) {
        /* c8 ignore start */
        if (IS_NOT_PRODUCTION && !selector.length) {
            // eslint-disable-next-line no-console
            console.warn('Selector array is empty');
        }
        /* c8 ignore stop */
        return selector.join(', ');
    }
    if (typeof selector !== 'string') {
        /* c8 ignore start */
        if (IS_NOT_PRODUCTION) {
            // eslint-disable-next-line no-console
            console.warn(`Selector must be a string or array. Received: ${typeof selector}`);
        }
        /* c8 ignore stop */
        return '';
    }
    /* c8 ignore start */
    if (IS_NOT_PRODUCTION && !selector) {
        // eslint-disable-next-line no-console
        console.warn('Selector must be provided');
    }
    /* c8 ignore stop */
    return selector || '';
}

const DQU_ORIGIN = 'dequeuniversity.com';
const DQDOCS_ORIGIN = 'docs.deque.com';
const HelpLink = React.forwardRef(({ href, ...props }, ref) => {
    const { t } = useTranslation();
    const defaultText = t('Issue help (opens in new tab)');
    const linkText = React.useMemo(() => {
        try {
            const url = new URL(href);
            switch (url.host) {
                case DQU_ORIGIN:
                    return t(`{{product}} (opens in new tab)`, {
                        product: 'Deque University'
                    });
                case DQDOCS_ORIGIN:
                    return t(`{{product}} issue help (opens in new tab)`, {
                        product: 'Deque Docs'
                    });
                default:
                    return defaultText;
            }
        }
        catch {
            return defaultText;
        }
    }, [href, t]);
    return (jsxRuntime.jsxs(cauldronReact.Link, { ref: ref, href: href, target: "_blank", rel: "noopener noreferrer", ...props, children: [linkText, jsxRuntime.jsx(cauldronReact.Icon, { type: "external-link" })] }));
});
HelpLink.displayName = 'HelpLink';

var modules_6e4b5f81 = {"HowToFix":"_HowToFix_78fe3734","header":"_header_78fe3734","relatedNode":"_relatedNode_78fe3734","relatedNodeCode":"_relatedNodeCode_78fe3734"};

const RemediationDetails = ({ title, remediations }) => {
    const remediationsWithAMessage = remediations.filter(({ message }) => Boolean(message));
    if (!remediationsWithAMessage.length) {
        return jsxRuntime.jsx(jsxRuntime.Fragment, {});
    }
    return (jsxRuntime.jsxs("div", { children: [jsxRuntime.jsx("h3", { children: title }), remediationsWithAMessage.length === 1 ? (jsxRuntime.jsx("p", { children: remediationsWithAMessage[0].message })) : (jsxRuntime.jsx("ul", { children: remediationsWithAMessage.map(({ message }) => (jsxRuntime.jsx("li", { children: message }, message))) }))] }));
};
const HowToFix = React.forwardRef(({ issue, className }, ref) => {
    const { t } = useTranslation();
    const { remediation, summary } = issue;
    const hasMustFixRemediations = !!remediation?.all?.length || !!remediation?.none?.length;
    const hasBothRemediationTypes = !!remediation?.any?.length && hasMustFixRemediations;
    const hasRemediations = hasMustFixRemediations || !!remediation?.any?.length;
    const mustFixRemediations = [
        ...(remediation?.none || []),
        ...(remediation?.all || [])
    ];
    const relatedNodes = remediation
        ? Object.values(remediation)
            .filter(r => !!r)
            .flatMap(r => r)
            .flatMap(r => r.relatedNodes || [])
        : [];
    return (jsxRuntime.jsxs("div", { ref: ref, className: classnames__default.default(modules_6e4b5f81.HowToFix, className), children: [hasBothRemediationTypes && (jsxRuntime.jsx("p", { className: modules_6e4b5f81.header, children: t('There are some items that must be fixed and some items that require you to fix at least one.') })), hasRemediations ? (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx(RemediationDetails, { remediations: mustFixRemediations, title: t('Must fix the following:') }), jsxRuntime.jsx(RemediationDetails, { remediations: remediation?.any || [], title: hasMustFixRemediations ||
                            (remediation?.any?.length && remediation.any.length > 1)
                            ? t('Must fix at least one of the following:')
                            : t('Must fix the following:') }), relatedNodes.map((node, index) => (jsxRuntime.jsx("div", { className: modules_6e4b5f81.relatedNode, children: jsxRuntime.jsx(cauldronReact.Code, { allowCopy: true, className: modules_6e4b5f81.relatedNodeCode, language: "html", label: t(`Related node {{ index }}`, { index: index + 1 }), copyButtonProps: {
                                children: t(`Copy related node {{ index }}`, {
                                    index: index + 1
                                })
                            }, children: node.html }) }, node.target.join(','))))] })) : (jsxRuntime.jsx("p", { children: summary ||
                    t('We are working on getting automatic fix advice for this rule. Please feel free to contact a Deque accessibility specialist for support.') }))] }));
});
HowToFix.displayName = 'HowToFix';

function calculateImageRatio(element) {
    if (!element || !(element instanceof HTMLImageElement)) {
        return 1;
    }
    const originalWidth = element.naturalWidth || 1;
    return element.width / originalWidth || 1;
}
/**
 * When a component needs to calculate the scale ratio between an image's displayed size
 * and its natural size, useImageScaleRatio will track the ratio for properly scaling
 * overlays or annotations on responsive images.
 *
 * @example
 * const imageRef = useRef<HTMLImageElement>(null)
 * const scaleRatio = useImageScaleRatio(imageRef.current)
 * const overlayStyle = {
 *   width: `${boundingBox.width * scaleRatio}px`,
 *   height: `${boundingBox.height * scaleRatio}px`
 * }
 * return <img ref={imageRef} src={url} />
 */
function useImageScaleRatio(element, dependencyList = []) {
    const [ratio, setRatio] = React.useState(1);
    const isValidElement = () => !!(element &&
        (element instanceof HTMLImageElement ||
            element.current instanceof HTMLImageElement));
    const resolvedElement = () => element instanceof HTMLImageElement ? element : element.current;
    useResizeObserver({
        element,
        callback: () => setRatio(calculateImageRatio(resolvedElement())),
        active: isValidElement()
    }, dependencyList);
    React.useEffect(() => {
        if (!isValidElement()) {
            return;
        }
        const updateRatio = () => setRatio(calculateImageRatio(resolvedElement()));
        if (!resolvedElement()?.complete) {
            resolvedElement()?.addEventListener('load', updateRatio);
        }
        else {
            updateRatio();
        }
        return () => {
            resolvedElement()?.removeEventListener('load', updateRatio);
        };
    }, [element, isValidElement(), ...dependencyList]);
    return ratio;
}

var modules_81647ace = {"screenshot":"_screenshot_cee75e2d","screenshotEmptyState":"_screenshotEmptyState_cee75e2d","statusIcon":"_statusIcon_cee75e2d","screenshotLoading":"_screenshotLoading_cee75e2d","screenshotThumbnail":"_screenshotThumbnail_cee75e2d","screenshotImageLoading":"_screenshotImageLoading_cee75e2d","leadingActions":"_leadingActions_cee75e2d","trailingActions":"_trailingActions_cee75e2d","screenshotDialog":"_screenshotDialog_cee75e2d","screenshotDialogWrapper":"_screenshotDialogWrapper_cee75e2d","highlight":"_highlight_cee75e2d","highlightButton":"_highlightButton_cee75e2d"};

const ScreenshotImage = React.forwardRef(({ screenshotURL, ...props }, ref) => {
    const { t } = useTranslation();
    return (jsxRuntime.jsx("img", { src: screenshotURL, alt: t('screenshot of the page at the time this issue was generated'), ref: ref, ...props }));
});
ScreenshotImage.displayName = 'ScreenshotImage';

const ScreenshotDialog = ({ issue, onClose, show, ...props }) => {
    const highlightButtonRef = React.useRef(null);
    const { screenshotURL, boundingBox, help } = issue;
    const screenshotRef = React.useRef(null);
    const [showHighlight, setShowHighlight] = React.useState(!!boundingBox);
    const { t } = useTranslation();
    const screenshotIsOffscreen = boundingBox?.width === 0 && boundingBox?.height === 0;
    const imageRatio = useImageScaleRatio(screenshotRef, [show]);
    const highlightStyles = React.useMemo(() => {
        if (!boundingBox || !imageRatio) {
            return;
        }
        return {
            width: `${boundingBox.width * imageRatio}px`,
            height: `${boundingBox.height * imageRatio}px`,
            top: `${boundingBox.y * imageRatio}px`,
            left: `${boundingBox.x * imageRatio}px`
        };
    }, [boundingBox, imageRatio]);
    const highlightRef = React.useCallback(function getInitialHighlight(ref) {
        if (!ref || screenshotIsOffscreen) {
            return;
        }
        ref.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
            inline: 'nearest'
        });
    }, [screenshotIsOffscreen]);
    return (jsxRuntime.jsxs(cauldronReact.Modal, { className: modules_81647ace.screenshotDialog, scrollable: true, show: show, heading: t(`Screenshot: {{ruleHelpText}}`, {
            ruleHelpText: help
        }), onClose: onClose, ...props, children: [jsxRuntime.jsx(cauldronReact.ModalContent, { tabIndex: 0, children: jsxRuntime.jsxs("div", { className: modules_81647ace.screenshotDialogWrapper, children: [jsxRuntime.jsx(ScreenshotImage, { screenshotURL: screenshotURL, ref: screenshotRef }), showHighlight && highlightStyles && !screenshotIsOffscreen && (jsxRuntime.jsx("div", { "data-testid": "screenshot-highlight", className: modules_81647ace.highlight, style: highlightStyles, ref: highlightRef }))] }) }), jsxRuntime.jsxs(cauldronReact.ModalFooter, { children: [boundingBox && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [screenshotIsOffscreen && (jsxRuntime.jsx(cauldronReact.Tooltip, { variant: "info", target: highlightButtonRef, placement: "top", children: t('We are unable to highlight the element because it is scrolled out of view in the screenshot. This can happen when elements are offscreen or within scrollable panes.') })), jsxRuntime.jsxs(cauldronReact.Button, { ref: highlightButtonRef, variant: "secondary", role: "switch", "aria-checked": !screenshotIsOffscreen && showHighlight, className: modules_81647ace.highlightButton, onClick: () => !screenshotIsOffscreen && setShowHighlight(!showHighlight), "aria-disabled": screenshotIsOffscreen, children: [jsxRuntime.jsx(cauldronReact.Icon, { type: "highlight" }), t('Highlight Issue')] })] })), jsxRuntime.jsx(cauldronReact.Button, { variant: "secondary", onClick: onClose, children: t('Close') })] })] }));
};

const isCheckpointTagInfo = (tagInfo) => {
    return 'checkpoint' in tagInfo && !!tagInfo.checkpoint;
};
const isWCAGCheckpoint = (tagInfo) => isCheckpointTagInfo(tagInfo) && tagInfo.standard === 'WCAG';
/**
 * Screenshot configuration is used to indicate the state of the current
 * screenshot configuration via org-wide settings and what the configuration
 * was at the time the issue was captured. Each of the specified states
 * names always specifies what the current state is, and how that value
 * is relative to the historical org-wide settings with regards to screenshots.
 */
const ScreenshotConfiguration = {
    /**
     * When both the current screenshot configuration, and the screenshot
     * configuration at the time the issue was created was both "on"
     */
    On: 'on',
    /**
     * When the current screenshot configuration is "on", and the screenshot
     * configuration at the time the issue was created was "off"
     */
    MixedOn: 'mixed-on',
    /**
     * When the current screenshot configuration is "off", and the screenshot
     * configuration at the time the issue was created was "on"
     */
    MixedOff: 'mixed-off',
    /**
     * When both the current screenshot configuration, and the screenshot
     * configuration at the time the issue was created was both "off" and the
     * the user is allowed to change the value
     */
    Off: 'off',
    /**
     * When both the current screenshot configuration, and the screenshot
     * configuration at the time the issue was created was both "off" and the
     * the user is not allowed to change the value
     */
    OffLocked: 'off-locked'
};

const ContactSupport = () => {
    return (jsxRuntime.jsx("p", { children: jsxRuntime.jsxs(Trans, { children: ["If the problem continues,", ' ', jsxRuntime.jsx("a", { href: "mailto:helpdesk@deque.com", children: "contact support" })] }) }));
};
const ScreenshotConfigurationError = ({ configuration }) => {
    const { t } = useTranslation();
    const configurationError = React.useMemo(() => {
        switch (configuration) {
            case ScreenshotConfiguration.MixedOn:
                return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("strong", { children: t('Screenshot was not captured because configuration was not turned on during the time of this scan run.') }), jsxRuntime.jsx(ContactSupport, {})] }));
            case ScreenshotConfiguration.Off:
                return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("strong", { children: t('Screenshots configuration was not turned on during the time of this scan run.') }), jsxRuntime.jsx(ContactSupport, {})] }));
            case ScreenshotConfiguration.OffLocked:
                return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("strong", { children: t('Screenshots configuration was not turned on during the time of this scan run.') }), jsxRuntime.jsx("p", { children: t('Contact your organization admin.') })] }));
            case 'missing-configuration':
                return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("strong", { children: t('No screenshot was found for this issue.') }), jsxRuntime.jsx(ContactSupport, {})] }));
            // This is indicative of an error state where the screenshot is supposed
            // to exist and was unable to be displayed for one reason or another
            default: {
                return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("strong", { children: t('There was an issue rendering the screenshot.') }), jsxRuntime.jsx(ContactSupport, {})] }));
            }
        }
    }, [t, configuration]);
    return jsxRuntime.jsx("div", { children: configurationError });
};

const ScreenshotEmptyState = ({ className, screenshotStatus, screenshotConfiguration, ...props }) => {
    const isLoading = screenshotStatus === 'loading';
    const hasError = screenshotStatus === 'error';
    const unknownConfiguration = !screenshotConfiguration;
    return (jsxRuntime.jsx("div", { className: classnames__default.default(modules_81647ace.screenshotEmptyState, className, {
            [modules_81647ace.screenshotLoading]: isLoading
        }), ...props, children: !isLoading && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("span", { className: modules_81647ace.statusIcon, children: jsxRuntime.jsx(cauldronReact.Icon, { type: hasError || unknownConfiguration ? 'images' : 'gears' }) }), jsxRuntime.jsx(ScreenshotConfigurationError, { configuration: hasError
                        ? null
                        : unknownConfiguration
                            ? 'missing-configuration'
                            : screenshotConfiguration })] })) }));
};

const Screenshot = React.forwardRef(({ className, issue, leadingScreenshotActions, screenshotStatus, screenshotConfiguration, hideThumbnailHighlight = false, ...props }, ref) => {
    const { screenshotURL, boundingBox } = issue;
    const { t } = useTranslation();
    const [onLoadError, setOnLoadError] = React.useState(false);
    const [imageLoaded, setImageLoaded] = React.useState(!screenshotURL);
    const imageRef = React.useRef(null);
    const [showFullScreenshotDialog, setShowFullScreenshotDialog] = React.useState(false);
    const isLoading = (!imageLoaded && !imageRef.current?.complete) ||
        screenshotStatus === 'loading';
    const hasError = screenshotStatus === 'error' || onLoadError;
    const status = hasError
        ? 'error'
        : isLoading
            ? 'loading'
            : screenshotStatus || 'complete';
    const canRenderScreenshot = (!screenshotStatus || screenshotStatus === 'complete') &&
        !!screenshotURL &&
        !hasError;
    const showEmptyState = isLoading || hasError || !screenshotURL;
    React.useEffect(() => {
        if (screenshotURL) {
            setOnLoadError(false);
            setImageLoaded(false);
        }
    }, [screenshotURL]);
    const imageRatio = useImageScaleRatio(imageRef);
    const highlightStyles = React.useMemo(() => {
        if (!boundingBox || !imageRatio || !canRenderScreenshot) {
            return;
        }
        return {
            width: `${boundingBox.width * imageRatio}px`,
            height: `${boundingBox.height * imageRatio}px`,
            top: `${boundingBox.y * imageRatio}px`,
            left: `${boundingBox.x * imageRatio}px`
        };
    }, [boundingBox, imageRatio]);
    return (jsxRuntime.jsxs("div", { ref: ref, className: classnames__default.default(modules_81647ace.screenshot, className), ...props, children: [!isLoading && leadingScreenshotActions && (jsxRuntime.jsx("div", { className: modules_81647ace.leadingActions, children: leadingScreenshotActions })), showEmptyState && (jsxRuntime.jsx(ScreenshotEmptyState, { screenshotConfiguration: screenshotConfiguration, screenshotStatus: status })), canRenderScreenshot && (jsxRuntime.jsxs("div", { className: classnames__default.default(modules_81647ace.screenshotThumbnail, {
                    [modules_81647ace.screenshotImageLoading]: isLoading
                }), children: [jsxRuntime.jsx(ScreenshotImage, { ref: imageRef, screenshotURL: screenshotURL, onError: () => setOnLoadError(true), onLoad: () => setImageLoaded(true) }, screenshotURL), !hideThumbnailHighlight && highlightStyles && (jsxRuntime.jsx("div", { className: modules_81647ace.highlight, style: highlightStyles }))] })), !isLoading && canRenderScreenshot && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("div", { className: modules_81647ace.trailingActions, children: jsxRuntime.jsx(cauldronReact.Button, { variant: "secondary", onClick: () => setShowFullScreenshotDialog(true), children: t('View full screenshot') }) }), jsxRuntime.jsx(ScreenshotDialog, { issue: { ...issue, screenshotURL }, show: showFullScreenshotDialog, onClose: () => setShowFullScreenshotDialog(false) })] }))] }));
});
Screenshot.displayName = 'Screenshot';

var modules_76c20546 = {"IssueDetails":"_IssueDetails_ef58419d","sectionLinks":"_sectionLinks_ef58419d","resourceLinks":"_resourceLinks_ef58419d","pageUrl":"_pageUrl_ef58419d","toggleIcon":"_toggleIcon_ef58419d","testUrl":"_testUrl_ef58419d","testUrlExpanded":"_testUrlExpanded_ef58419d","testLocation":"_testLocation_ef58419d","breakAnywhere":"_breakAnywhere_ef58419d","analyzedValues":"_analyzedValues_ef58419d"};

const IssueDetails = React.forwardRef(({ issue, hideIssueDetailsScreenshot, leadingScreenshotActions, screenshotStatus, screenshotConfiguration, className, ...props }, ref) => {
    const { t } = useTranslation();
    const { testPageTitle, testUrl, testFilePath, testLocation, scanName, screenTitle, mobilePlatform, appIdentifier, analyzedValues, ancestry, source, selector, xpathSelector, tags = [] } = issue;
    const { screenshots: allowsScreenshots } = useConfiguration();
    const wcagStandard = useWCAGStandard(tags);
    const wcagCheckpoints = useWCAGCheckpointsTagInfo(tags);
    const testUrlRef = React.useRef(null);
    const [showTestUrlToggle, setShowTestUrlToggle] = React.useState(false);
    const [testUrlExpanded, setTestUrlExpanded] = React.useState(false);
    const sectionLinks = `section-links-${utils.useId()}`;
    const testUrlId = `test-url-${utils.useId()}`;
    useResizeObserver({
        element: testUrlRef,
        callback: entry => {
            const { target: overflowElement } = entry;
            const hasOverflow = overflowElement.clientHeight < overflowElement.scrollHeight;
            setShowTestUrlToggle(hasOverflow || showTestUrlToggle);
        },
        active: !!testUrl
    });
    const handleToggleTestUrlExpanded = () => setTestUrlExpanded(!testUrlExpanded);
    return (jsxRuntime.jsxs("div", { ref: ref, className: classnames__default.default(modules_76c20546.IssueDetails, className), ...props, children: [jsxRuntime.jsxs("nav", { "aria-labelledby": sectionLinks, className: modules_76c20546.sectionLinks, children: [jsxRuntime.jsx("strong", { id: sectionLinks, children: t('Jump to section:') }), jsxRuntime.jsxs("ol", { children: [jsxRuntime.jsx("li", { children: jsxRuntime.jsx(cauldronReact.Link, { href: "#issue-location", children: t('Issue location') }) }), jsxRuntime.jsx("li", { children: jsxRuntime.jsx(cauldronReact.Link, { href: "#how-to-fix", children: t('How to fix') }) })] })] }), jsxRuntime.jsx("h2", { id: "issue-location", tabIndex: -1, children: t('Issue location') }), testPageTitle && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "page-title", children: t('Page title') }), testPageTitle] })), testUrl && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "page-url", children: t('Page URL') }), jsxRuntime.jsxs("div", { className: modules_76c20546.pageUrl, children: [jsxRuntime.jsx("div", { id: testUrlId, ref: testUrlRef, className: classnames__default.default(modules_76c20546.testUrl, {
                                    [modules_76c20546.testUrlExpanded]: testUrlExpanded
                                }), children: testUrl }), showTestUrlToggle && (jsxRuntime.jsxs(cauldronReact.Button, { variant: "tertiary", "aria-expanded": testUrlExpanded, "aria-controls": testUrlId, onClick: handleToggleTestUrlExpanded, children: [jsxRuntime.jsx(cauldronReact.Icon, { className: modules_76c20546.toggleIcon, type: testUrlExpanded ? 'minus' : 'plus' }), testUrlExpanded ? t('Show less URL') : t('Show full URL')] })), jsxRuntime.jsxs("a", { href: testUrl, target: "_blank", rel: "noopener noreferrer", children: [t('Page URL (opens in new tab)'), " ", jsxRuntime.jsx(cauldronReact.Icon, { type: "external-link" })] })] })] })), scanName && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "scan-name", children: t('Scan name') }), scanName] })), screenTitle && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "screen-title", children: t('Screen title') }), screenTitle] })), mobilePlatform && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "operating-system-os", children: t('Operating system (OS)') }), mobilePlatform !== 'Android' ? 'Apple' : mobilePlatform] })), appIdentifier && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "app-identifier", children: t('App identifier') }), jsxRuntime.jsx("span", { className: modules_76c20546.breakAnywhere, children: appIdentifier })] })), allowsScreenshots && !hideIssueDetailsScreenshot && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "screenshot", children: t('Page screenshot') }), jsxRuntime.jsx(Screenshot, { issue: issue, leadingScreenshotActions: leadingScreenshotActions, screenshotStatus: screenshotStatus, screenshotConfiguration: screenshotConfiguration })] })), testFilePath && (jsxRuntime.jsx(cauldronReact.Code, { label: jsxRuntime.jsx("h3", { id: "test-file", children: t('Test file path') }), copyButtonProps: {
                    children: t('Copy test file path'),
                    notificationLabel: t('Copied')
                }, allowCopy: true, children: testFilePath })), testLocation && testLocation.length > 0 && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "test-location", children: t('Test location') }), jsxRuntime.jsx("div", { className: modules_76c20546.testLocation, children: testLocation.join(' > ') })] })), analyzedValues && Object.keys(analyzedValues).length && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("h3", { id: "analyzed-values", children: t('Analyzed values') }), jsxRuntime.jsx("div", { className: classnames__default.default('Code', 'hljs', modules_76c20546.analyzedValues), children: jsxRuntime.jsx("ul", { children: Object.entries(analyzedValues)
                                .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                                .map(([key, value]) => {
                                // If the value is an object, we stringify it for display.
                                // to avoid `[object Object]` output.
                                // Otherwise, it's a string, we display it directly.
                                const displayValue = typeof value === 'object'
                                    ? JSON.stringify(value, null, 2)
                                    : value;
                                return (jsxRuntime.jsxs("li", { children: [jsxRuntime.jsx("strong", { children: `${key}:` }), " ", `${displayValue}`] }, key));
                            }) }) })] })), ancestry && (jsxRuntime.jsx(cauldronReact.Code, { language: "css", label: jsxRuntime.jsx("h3", { id: "element-ancestry", children: t('Ancestry') }), copyButtonProps: {
                    children: t('Copy ancestry'),
                    notificationLabel: t('Copied')
                }, allowCopy: true, children: ancestry.join(', ') })), source && (jsxRuntime.jsx(cauldronReact.Code, { language: "html", label: jsxRuntime.jsx("h3", { id: "element-source", children: t('Element source') }), copyButtonProps: {
                    children: t('Copy element source'),
                    notificationLabel: t('Copied')
                }, allowCopy: true, children: source })), selector && (jsxRuntime.jsx(cauldronReact.Code, { language: "css", label: jsxRuntime.jsx("h3", { id: "css-selector", children: t('CSS selector') }), copyButtonProps: {
                    children: t('Copy CSS selector'),
                    notificationLabel: t('Copied')
                }, allowCopy: true, children: formatSelector({ selector }) })), xpathSelector && (jsxRuntime.jsx(cauldronReact.Code, { label: jsxRuntime.jsx("h3", { id: "xpath", children: t('XPath') }), copyButtonProps: {
                    children: t('Copy XPath'),
                    notificationLabel: t('Copied')
                }, allowCopy: true, children: xpathSelector.join(', ') })), jsxRuntime.jsx("hr", {}), jsxRuntime.jsx("h2", { id: "how-to-fix", tabIndex: -1, children: t('How to fix') }), jsxRuntime.jsx(HowToFix, { issue: issue }), jsxRuntime.jsxs("div", { className: modules_76c20546.resourceLinks, children: [jsxRuntime.jsx("h3", { children: t('Additional resources:') }), wcagCheckpoints.length ? (jsxRuntime.jsxs("ul", { children: [jsxRuntime.jsx("li", { children: jsxRuntime.jsx(HelpLink, { href: issue.helpUrl }) }), wcagCheckpoints.map(tagInfo => (jsxRuntime.jsxs("li", { children: [wcagStandard && jsxRuntime.jsxs("strong", { children: [wcagStandard.name, ":"] }), ' ', jsxRuntime.jsxs(cauldronReact.Link, { href: tagInfo.understandingUrl, target: "_blank", rel: "noopener noreferrer", children: [tagInfo.checkpoint, " ", tagInfo.checkpointDescription, jsxRuntime.jsx(cauldronReact.Icon, { label: t('Opens in new tab'), type: "external-link" })] })] }, tagInfo.value)))] })) : (jsxRuntime.jsx(HelpLink, { href: issue.helpUrl }))] })] }));
});
IssueDetails.displayName = 'IssueDetails';

const isRulesetTag = (tag) => tag in accessibilityStandards.rulesets;
/**
 * Returns array of accessibility standards from a list of tags
 *
 *
 * example: const standardTags = useAccessibilityStandards(['foo', 'bar', 'wcag142', 'EN-301-549', 'TTv5', 'wcag2a']);
 * returns
 * [
      { value: 'EN-301-549', name: 'EN 301 549', tags: [ 'EN-301-549' ] },
      { value: 'TTv5', name: 'Trusted Tester v5', tags: [ 'TTv5' ] },
      { value: 'wcag2a', name: 'WCAG 2.0 A', tags: [ 'wcag2a' ] }
    ]
 */
function useAccessibilityStandards(tags) {
    return React.useMemo(() => {
        if (!Array.isArray(tags)) {
            return [];
        }
        const standards = [];
        for (const tag of tags) {
            const tagInfo = accessibilityStandards.getTagInfo(tag);
            if (tagInfo?.standard && isRulesetTag(tag)) {
                standards.push(accessibilityStandards.rulesets[tag]);
            }
        }
        return standards;
    }, [JSON.stringify(tags)]);
}

var modules_e80d9c32 = {"IssueMetadata":"_IssueMetadata_594c364a","IssueMetadata__tags":"_IssueMetadata__tags_594c364a","IssueMetadata__standardsList":"_IssueMetadata__standardsList_594c364a"};

const IssueMetadata = React.forwardRef(({ issue, className, ...props }, ref) => {
    const { isManual, igt, createdAt, metadata, tags: allTags = [], userTags = [] } = issue;
    const { t } = useTranslation();
    const discoveryMethod = React.useMemo(() => {
        if (isManual) {
            return t('Manual');
        }
        if (igt) {
            return `${igt} Intelligent Guided Test (IGT)`;
        }
        return t('Automatic');
    }, [isManual, igt, t]);
    const standards = useAccessibilityStandards(allTags);
    const checkpoints = useCheckpointsTagInfo(allTags);
    const getStandardName = (checkpoint) => {
        // Section 508 isn't included in accessibility standards, but we continue to show it here
        if (checkpoint.standard === 'Section 508') {
            return 'Section 508';
        }
        return standards.find(s => s.type === checkpoint.standard)?.name || '';
    };
    const checkpointsWithStandard = React.useMemo(() => checkpoints
        .map(checkpoint => ({
        ...checkpoint,
        standardName: getStandardName(checkpoint)
    }))
        .sort((a, b) => {
        const asn = a.standardName;
        const bsn = b.standardName;
        return asn.localeCompare(bsn);
    }), [checkpoints, standards]);
    const nonCategorizedTags = React.useMemo(() => {
        const _tags = [...userTags];
        for (const tag of allTags) {
            if (!accessibilityStandards.getTagInfo(tag) &&
                tag !== 'best-practice' &&
                tag !== 'experimental') {
                // tag info implies it's a standard or checkpoint tag and can exclude it
                _tags.push(tag);
            }
        }
        return _tags;
    }, [JSON.stringify(allTags), JSON.stringify(userTags)]);
    const renderStandardTag = (tagInfo) => {
        const standardName = tagInfo.standardName;
        const isWCAG = isWCAGCheckpoint(tagInfo);
        if (isWCAG) {
            return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [standardName || tagInfo.standard, ": ", tagInfo.checkpoint, ' ', tagInfo.checkpointDescription] }));
        }
        return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [standardName, " ", tagInfo.checkpoint] }));
    };
    return (jsxRuntime.jsx("div", { className: classnames__default.default(modules_e80d9c32.IssueMetadata, className), ref: ref, ...props, children: jsxRuntime.jsxs("dl", { className: modules_e80d9c32.IssueMetadata__list, children: [createdAt && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("dt", { children: t('Date discovered') }), jsxRuntime.jsx("dd", { children: dateFns.format(new Date(createdAt), 'MMMM d, yyyy h:mm a') })] })), jsxRuntime.jsx("dt", { children: t('Discovery method') }), jsxRuntime.jsx("dd", { children: discoveryMethod }), metadata?.platform?.name && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("dt", { children: t('Platform') }), jsxRuntime.jsxs("dd", { children: [metadata.platform.name, " ", metadata.platform.version] })] })), metadata?.device?.name && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("dt", { children: t('Device') }), jsxRuntime.jsxs("dd", { children: [metadata.device.name, " ", metadata.device.version] })] })), metadata?.browser?.name && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("dt", { children: t('Browser') }), jsxRuntime.jsxs("dd", { children: [metadata.browser.name, " ", metadata.browser.version] })] })), metadata?.viewport &&
                    metadata.viewport.width &&
                    metadata.viewport.height && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("dt", { children: t('Viewport size') }), jsxRuntime.jsx("dd", { children: t('{{width}} by {{height}}', {
                                width: metadata.viewport.width,
                                height: metadata.viewport.height
                            }) })] })), metadata?.viewport?.devicePixelRatio && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("dt", { children: t('Device pixel ratio (DPR)') }), jsxRuntime.jsx("dd", { children: metadata.viewport.devicePixelRatio })] })), checkpointsWithStandard.length > 0 && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("dt", { children: t('All standards') }), jsxRuntime.jsx("dd", { children: checkpointsWithStandard.length === 1 ? (jsxRuntime.jsx(jsxRuntime.Fragment, { children: renderStandardTag(checkpointsWithStandard[0]) })) : (jsxRuntime.jsx("ul", { className: modules_e80d9c32.IssueMetadata__standardsList, children: checkpointsWithStandard.map(tagInfo => (jsxRuntime.jsx("li", { children: renderStandardTag(tagInfo) }, tagInfo.value))) })) })] })), nonCategorizedTags.length > 0 && (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("dt", { children: t('Tags') }), jsxRuntime.jsx("dd", { className: modules_e80d9c32.IssueMetadata__tags, children: jsxRuntime.jsx("ul", { children: nonCategorizedTags.map(tag => (jsxRuntime.jsx("li", { children: jsxRuntime.jsx(cauldronReact.Tag, { children: jsxRuntime.jsx(cauldronReact.TagLabel, { children: tag }) }) }, tag))) }) })] }))] }) }));
});
IssueMetadata.displayName = 'IssueMetadata';

var modules_d02d545f = {"issueLayoutRoot":"_issueLayoutRoot_e06183aa","columnLayout":"_columnLayout_e06183aa","issueDetails":"_issueDetails_e06183aa","issueMetadata":"_issueMetadata_e06183aa","columnRight":"_columnRight_e06183aa"};

const LAYOUT_BREAKPOINT_PX = 1100;
const IssueLayout = React.forwardRef(({ issue, rightColumnChildren, leadingScreenshotActions, screenshotStatus, screenshotConfiguration, className, ...props }, ref) => {
    const layoutRef = useSharedRef(ref);
    const [isOneColumnLayout, setIsOneColumnLayout] = React.useState(true);
    const { screenshots: allowsScreenshots } = useConfiguration();
    const handleResize = React.useCallback((entry) => {
        const containerWidth = entry.target.clientWidth;
        setIsOneColumnLayout(containerWidth < LAYOUT_BREAKPOINT_PX);
    }, []);
    useResizeObserver({
        element: layoutRef,
        callback: handleResize,
        active: allowsScreenshots
    });
    const showScreenshotOnRightSide = !isOneColumnLayout && allowsScreenshots && !rightColumnChildren;
    const showMetadataOnRightSide = !showScreenshotOnRightSide && !rightColumnChildren;
    return (jsxRuntime.jsx("div", { className: classnames__default.default(modules_d02d545f.issueLayoutRoot, className), ref: layoutRef, ...props, children: jsxRuntime.jsxs("div", { className: modules_d02d545f.columnLayout, children: [jsxRuntime.jsxs("div", { className: modules_d02d545f.columnLeft, children: [jsxRuntime.jsx(IssueDetails, { issue: issue, hideIssueDetailsScreenshot: showScreenshotOnRightSide, className: modules_d02d545f.issueDetails, leadingScreenshotActions: leadingScreenshotActions, screenshotStatus: screenshotStatus, screenshotConfiguration: screenshotConfiguration }), !showMetadataOnRightSide && (jsxRuntime.jsx(IssueMetadata, { className: modules_d02d545f.issueMetadata, issue: issue }))] }), jsxRuntime.jsx("div", { className: modules_d02d545f.columnRight, children: rightColumnChildren ||
                        (showScreenshotOnRightSide ? (jsxRuntime.jsx(Screenshot, { issue: issue, leadingScreenshotActions: leadingScreenshotActions, screenshotConfiguration: screenshotConfiguration, screenshotStatus: screenshotStatus })) : (jsxRuntime.jsx(IssueMetadata, { className: modules_d02d545f.issueMetadata, issue: issue }))) })] }) }));
});
IssueLayout.displayName = 'IssueLayout';

const ACTION_SELECTOR = 'a[href], button:not([disabled])';
const actionsIn = (root) => Array.from(root?.querySelectorAll(ACTION_SELECTOR) ?? []);
function focusedActionIndex(root) {
    return actionsIn(root).findIndex(action => action.contains(document.activeElement));
}
function focusActionAt(root, index) {
    const actions = actionsIn(root);
    (actions[index] ?? actions[0])?.focus();
}

function useCollapsedActions(sectionHeaderRef, actionsRef, active = true) {
    const [collapsed, setCollapsed] = React.useState(false);
    const collapsedRef = React.useRef(false);
    const refocusIndex = React.useRef(-1);
    const callback = React.useCallback(({ contentRect }) => {
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) ||
            /* c8 ignore next */ 16;
        const next = contentRect.width < 48 * rem;
        if (next === collapsedRef.current) {
            return;
        }
        collapsedRef.current = next;
        refocusIndex.current = focusedActionIndex(actionsRef?.current);
        setCollapsed(next);
    }, [actionsRef]);
    useResizeObserver({ element: sectionHeaderRef, callback, active });
    React.useLayoutEffect(() => {
        if (refocusIndex.current < 0) {
            return;
        }
        const index = refocusIndex.current;
        refocusIndex.current = -1;
        focusActionAt(actionsRef?.current, index);
    }, [collapsed, actionsRef]);
    return collapsed;
}

const BestPracticeBadge = () => {
    return (jsxRuntime.jsx(cauldronReact.Badge, { children: jsxRuntime.jsx(Trans, { children: "Best Practice" }) }));
};
BestPracticeBadge.displayName = 'BestPracticeBadge';

const IssuePageContext = React.createContext({
    titleRef: React.createRef()
});
const TitleActionsCollapsedContext = React.createContext(false);

function SuccessCriteriaBadge({ checkpoint, ruleset }) {
    const { t } = useTranslation();
    const showWCAGUnderstandingLink = isWCAGCheckpoint(checkpoint);
    return (jsxRuntime.jsxs(cauldronReact.Badge, { children: [jsxRuntime.jsxs(cauldronReact.BadgeLabel, { children: [ruleset.name, ":"] }), showWCAGUnderstandingLink ? (jsxRuntime.jsxs(cauldronReact.Link, { href: checkpoint.understandingUrl, target: "_blank", rel: "noopener noreferrer", children: [checkpoint.checkpoint, " ", checkpoint.checkpointDescription, jsxRuntime.jsx(cauldronReact.Icon, { label: t('Opens in new tab'), type: "external-link" })] })) : (checkpoint.checkpoint)] }));
}
SuccessCriteriaBadge.displayName = 'SuccessCriteriaBadge';

function assertNever$1(x) {
    throw new Error(`Unexpected value: ${x}`);
}
const ImpactBadgeLabel = ({ type, hideLabel }) => {
    const { t } = useTranslation();
    if (hideLabel) {
        switch (type) {
            case 'critical':
                return t('Critical');
            case 'serious':
                return t('Serious');
            case 'moderate':
                return t('Moderate');
            case 'minor':
                return t('Minor');
            default: {
                assertNever$1(type);
            }
        }
    }
    switch (type) {
        case 'critical':
            return (jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Impact:" }), "Critical"] }));
        case 'serious':
            return (jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Impact:" }), "Serious"] }));
        case 'moderate':
            return (jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Impact:" }), "Moderate"] }));
        case 'minor':
            return (jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Impact:" }), "Minor"] }));
        default: {
            assertNever$1(type);
        }
    }
};

function assertNever(x) {
    throw new Error(`Unexpected value: ${x}`);
}
function ResultBadgeLabel({ result }) {
    switch (result) {
        case 'failed':
            return (jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Result:" }), "Failed"] }));
        case 'passed':
            return (jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Result:" }), "Passed"] }));
        case 'not-applicable':
            return (jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Result:" }), "Not Applicable"] }));
        case 'issue-failed':
            return (jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Result:" }), "Issue"] }));
        default:
            assertNever(result);
    }
}

var modules_e9940ca4 = {"RuleDetails":"_RuleDetails_d16ea7df","headerSection":"_headerSection_d16ea7df","titleRow":"_titleRow_d16ea7df","title":"_title_d16ea7df","headerDescription":"_headerDescription_d16ea7df","badgeSection":"_badgeSection_d16ea7df"};

const RuleDetails = React.forwardRef(({ issue, className, ...props }, ref) => {
    const { ruleName, ruleId, help, description, helpUrl, impact, isExperimental, needsReview, tags = [], ruleResults } = issue;
    const { titleRef, titleActions } = React.useContext(IssuePageContext);
    const sectionHeaderRef = React.useRef(null);
    const actionsCollapsed = useCollapsedActions(sectionHeaderRef, titleActions?.ref, !!titleActions);
    const wcagStandard = useWCAGStandard(tags);
    const checkpointsTagInfo = useWCAGCheckpointsTagInfo(tags);
    const standards = useAccessibilityStandards(tags);
    const checkpointsWithStandardInfo = React.useMemo(() => {
        return checkpointsTagInfo.map(checkpoint => ({
            ...checkpoint,
            standardInfo: standards.find(s => s.type === checkpoint.standard)
        }));
    }, [checkpointsTagInfo, standards]);
    const isBestPractice = React.useMemo(() => tags.includes('best-practice'), [JSON.stringify(tags)]);
    // Note: We're temporarily preventing display of the header overline until
    // we determine how we need to structure and organize issue data
    // see: https://github.com/dequelabs/launchpad/issues/907
    const displayOverline = false;
    return (jsxRuntime.jsxs("div", { className: classnames__default.default(modules_e9940ca4.RuleDetails, className), ref: ref, ...props, children: [jsxRuntime.jsxs("div", { className: modules_e9940ca4.headerSection, children: [displayOverline, jsxRuntime.jsx(cauldronReact.SectionHeader, { ref: sectionHeaderRef, className: modules_e9940ca4.titleRow, heading: jsxRuntime.jsx("h1", { ref: titleRef, className: modules_e9940ca4.title, children: help }), children: jsxRuntime.jsx(TitleActionsCollapsedContext.Provider, { value: actionsCollapsed, children: titleActions?.node }) }), description && (jsxRuntime.jsx("p", { className: modules_e9940ca4.headerDescription, children: description })), jsxRuntime.jsx(HelpLink, { href: helpUrl })] }), jsxRuntime.jsxs("div", { className: modules_e9940ca4.badgeSection, children: [impact && (jsxRuntime.jsx(cauldronReact.ImpactBadge, { type: impact, label: jsxRuntime.jsx(ImpactBadgeLabel, { type: impact }) })), isExperimental && (jsxRuntime.jsx(cauldronReact.Badge, { children: jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Rule:" }), " Experimental"] }) })), wcagStandard &&
                        (checkpointsWithStandardInfo.length ? (checkpointsWithStandardInfo.map(checkpointInfo => (jsxRuntime.jsx(SuccessCriteriaBadge, { ruleset: wcagStandard, checkpoint: checkpointInfo }, checkpointInfo?.value)))) : (jsxRuntime.jsx(cauldronReact.Badge, { children: jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: wcagStandard.name }) }))), needsReview && (jsxRuntime.jsx(cauldronReact.Badge, { children: jsxRuntime.jsxs(Trans, { children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { children: "Result:" }), " Needs Review"] }) })), isBestPractice && jsxRuntime.jsx(BestPracticeBadge, {}), ruleResults && (jsxRuntime.jsx(cauldronReact.Badge, { children: jsxRuntime.jsx(ResultBadgeLabel, { result: ruleResults }) }))] })] }));
});
RuleDetails.displayName = 'RuleDetails';

var modules_62d27841 = {"Issue":"_Issue_09744d87","IssueContainer":"_IssueContainer_09744d87","issueRuleDetails":"_issueRuleDetails_09744d87"};

const Issue = React.forwardRef(({ issue, rightColumnChildren, leadingScreenshotActions, screenshotStatus, screenshotConfiguration, className, ...props }, ref) => {
    return (jsxRuntime.jsx("div", { className: classnames__default.default(modules_62d27841.Issue, className), ref: ref, ...props, children: jsxRuntime.jsxs("div", { className: modules_62d27841.IssueContainer, children: [jsxRuntime.jsx(RuleDetails, { className: modules_62d27841.issueRuleDetails, issue: issue }), jsxRuntime.jsx(IssueLayout, { issue: issue, rightColumnChildren: rightColumnChildren, leadingScreenshotActions: leadingScreenshotActions, screenshotStatus: screenshotStatus, screenshotConfiguration: screenshotConfiguration })] }) }));
});
Issue.displayName = 'Issue';

/**
 * Hook to provide the computed style property of an optionally provided
 * element or element ref. Defaults to `document.body` when no element is
 * provided.
 *
 * `getComputedStyle` can force layout or reflow calculations depending on the
 * CSS property being accessed. By default, the value is memoized to prevent
 * excessive layout thrashing. If the property needs to be updated based on some
 * other state, additional dependencies can be provided.
 *
 * @example
 * const height = useCSSPropertyValue('--some-css-height-var')
 *
 * If a property name and element that is not `document.body`. needs to be used,
 * passing in an object is necessary:
 *
 * @example
 * const height = useCSSPropertyValue({ property: '--some-css-height-var', element: document.getElementById('foo') })
 *
 */
function useCSSPropertyValue(options, dependencies = []) {
    let element;
    let property;
    if (typeof options === 'string') {
        property = options;
    }
    else {
        property = options.property;
        element = options.element;
    }
    const resolvedElement = (element instanceof HTMLElement ? element : element?.current) ||
        document.body;
    return React.useMemo(() => getComputedStyle(resolvedElement).getPropertyValue(property), [element, ...dependencies]);
}

/**
 * When a component needs to track an element's intersection, useIntersectionObserver
 * via a ref or callback function will track the first intersection entry invoking
 * the callback function and/or setting the value of the returned ref.
 *
 * @example
 * const elementRef = useRef<HTMLElement>()
 * useIntersectionObserver<HTMLElement>({ element: elementRef, root: document.body, callback })
 * return <span ref={elementRef}>...</span>
 */
function useIntersectionObserver(options, dependencyList = []) {
    const { element, callback, active = true, root = null, rootMargin, threshold = 1.0 } = options;
    const ref = React.createRef();
    const resolveElement = () => element && 'current' in element
        ? element.current
        : element;
    const [resolvedElement, setResolvedElement] = React.useState(resolveElement());
    React.useEffect(() => {
        setResolvedElement(resolveElement());
    });
    React.useEffect(() => {
        if (!('IntersectionObserver' in globalThis) ||
            typeof IntersectionObserver !== 'function') {
            return;
        }
        if (!active || typeof element === 'undefined' || element === null) {
            return;
        }
        // If the resolved element does not currently match, we don't want run the intersection observer yet
        if (element instanceof HTMLElement && element !== resolvedElement) {
            return;
        }
        if (!(element instanceof HTMLElement) &&
            !(element.current instanceof HTMLElement)) {
            // eslint-disable-next-line no-console
            console.warn('An element or ref was provided to useIntersectionObserver that was not an HTMLElement.');
            return;
        }
        const handleIntersection = ([entry]) => {
            ref.current = entry;
            if (typeof callback === 'function') {
                callback(entry);
            }
        };
        const observer = new IntersectionObserver(handleIntersection, {
            root,
            rootMargin,
            threshold
        });
        observer.observe(resolveElement());
        return () => {
            observer.disconnect();
        };
    }, [
        element,
        resolvedElement,
        callback,
        active,
        root,
        rootMargin,
        JSON.stringify(threshold),
        ...dependencyList
    ]);
    return ref;
}

/**
 * The mirror is tabbable, so it can hold focus, and it unmounts the moment the
 * heading row comes back. Left alone that drops focus onto `document.body`,
 * which inside an `aria-modal` dialog restarts the tab sequence at the
 * container, so focus is handed to the heading row's copy instead.
 */
function useMirroredActions(actionsRef) {
    const mirrorRef = React.useRef(null);
    const handOffIndex = React.useRef(-1);
    const [actionsHidden, setActionsHidden] = React.useState(false);
    const setActionsHiddenWithHandoff = React.useCallback((hidden) => {
        if (!hidden) {
            handOffIndex.current = focusedActionIndex(mirrorRef.current);
        }
        setActionsHidden(hidden);
    }, []);
    React.useEffect(() => {
        if (actionsHidden || handOffIndex.current < 0) {
            return;
        }
        const index = handOffIndex.current;
        handOffIndex.current = -1;
        focusActionAt(actionsRef?.current, index);
    }, [actionsHidden, actionsRef]);
    return {
        mirrorRef,
        actionsHidden,
        setActionsHidden: setActionsHiddenWithHandoff
    };
}

const Integrations = {
    jira: { name: 'Jira' }
};

var modules_d567bfdd = {"integrationIcon":"_integrationIcon_11dfd04f","linkAsButton":"_linkAsButton_11dfd04f"};

const integrationTypes = Object.keys(Integrations);
const OpenInNewTabAction = ({ href, onAction, collapse, elementProps }) => {
    const { t } = useTranslation();
    const label = t('Open in new tab');
    // `auxclick` too: a middle-click opens the tab without firing `click`.
    const handleOpen = (event) => onAction({ key: 'open-in-new-tab' }, event);
    return collapse ? (jsxRuntime.jsx(cauldronReact.IconButton, { as: "a", href: href, target: "_blank", rel: "noopener noreferrer", variant: "secondary", icon: "external-link", label: label, 
        // Collapsed actions are a tight row of icon buttons, and Cauldron's
        // default `auto` placement is free to drop a label over the next button.
        tooltipProps: { placement: 'bottom' }, large: true, onClick: handleOpen, onAuxClick: handleOpen, ...elementProps })) : (jsxRuntime.jsxs(cauldronReact.Link, { href: href, target: "_blank", rel: "noopener noreferrer", variant: "button-secondary", className: modules_d567bfdd.linkAsButton, onClick: handleOpen, onAuxClick: handleOpen, ...elementProps, children: [jsxRuntime.jsx(cauldronReact.Icon, { type: "external-link" }), label] }));
};
const IntegrationAction = ({ slug, name, viewUrl, issueKey, status, iconUrl, onSend, onAction, collapse, elementProps }) => {
    const { t } = useTranslation();
    const icon = iconUrl ? (jsxRuntime.jsx("img", { src: iconUrl, alt: "", referrerPolicy: "no-referrer", className: modules_d567bfdd.integrationIcon })) : (jsxRuntime.jsx(cauldronReact.Icon, { type: "share" }));
    if (viewUrl) {
        const key = `open-in-${slug}`;
        const label = issueKey
            ? t('Open {{issueKey}} in {{integration}}', {
                issueKey,
                integration: name
            })
            : t('Open in {{integration}}', { integration: name });
        // The expanded <Link> exposes status via a sibling <Tag>, which screen
        // readers concatenate into the link's accessible name. The collapsed
        // <IconButton> has no such sibling, so fold status into `label` to keep
        // the announced name consistent across viewports.
        const collapsedLabel = status
            ? t('{{label}} ({{status}})', { label, status })
            : label;
        return collapse ? (jsxRuntime.jsx(cauldronReact.IconButton, { as: "a", href: viewUrl, target: "_blank", rel: "noopener noreferrer", variant: "secondary", icon: "share", label: collapsedLabel, tooltipProps: { placement: 'bottom' }, large: true, onClick: (event) => onAction({ key }, event), ...elementProps })) : (jsxRuntime.jsxs(cauldronReact.Link, { href: viewUrl, target: "_blank", rel: "noopener noreferrer", variant: "button-secondary", className: modules_d567bfdd.linkAsButton, onClick: event => onAction({ key }, event), ...elementProps, children: [icon, label, status && jsxRuntime.jsx(cauldronReact.Tag, { size: "small", children: status })] }));
    }
    if (!onSend) {
        return null;
    }
    const label = t('Add to {{integration}}', { integration: name });
    return collapse ? (jsxRuntime.jsx(cauldronReact.IconButton, { variant: "secondary", onClick: onSend, icon: "share", label: label, tooltipProps: { placement: 'bottom' }, large: true, ...elementProps })) : (jsxRuntime.jsxs(cauldronReact.Button, { variant: "secondary", onClick: onSend, ...elementProps, children: [icon, label] }));
};
const IntegrationsActions = ({ integrations, onAction, collapse, elementProps /* c8 ignore next: default fallback value */ = {} }) => {
    const { integrations: allowsIntegrations } = useConfiguration();
    if (!allowsIntegrations) {
        return null;
    }
    return (jsxRuntime.jsx(jsxRuntime.Fragment, { children: integrationTypes.map(integrationType => {
            const existing = integrations?.find(_integration => _integration.type === integrationType && _integration.url);
            const name = Integrations[integrationType]
                ?.name /* c8 ignore next: default fallback value */ ||
                /* c8 ignore next: default fallback value */ integrationType;
            return (jsxRuntime.jsx(IntegrationAction, { slug: integrationType, name: name, viewUrl: existing?.url ?? null, onSend: existing
                    ? undefined
                    : event => onAction({ key: `add-to-${integrationType}` }, event), onAction: onAction, collapse: collapse, elementProps: elementProps }, integrationType));
        }) }));
};
const ConnectedIntegrationActions = ({ integrationProps: { integrations, loading, error, openModal }, onAction, collapse, elementProps /* c8 ignore next: default fallback value */ = {} }) => {
    if (error || loading) {
        return null;
    }
    return (jsxRuntime.jsx(jsxRuntime.Fragment, { children: integrations.map(item => {
            if (!item.canSend && !item.viewUrl) {
                return null;
            }
            return (jsxRuntime.jsx(IntegrationAction, { slug: item.productSlug, name: item.productName, viewUrl: item.viewUrl, issueKey: item.issueKey, status: item.status, iconUrl: item.iconUrl, onSend: event => {
                    onAction({ key: `add-to-${item.productSlug}` }, event);
                    openModal(item.productSlug);
                }, onAction: onAction, collapse: collapse, elementProps: elementProps }, item.productSlug));
        }) }));
};
const Actions = ({ integrations, integrationProps, shareURL, openInNewTabHref, onAction, collapse = false, elementProps /* c8 ignore next: default fallback value */ = {} }) => {
    const { t } = useTranslation();
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [openInNewTabHref && (jsxRuntime.jsx(OpenInNewTabAction, { href: openInNewTabHref, onAction: onAction, collapse: collapse, elementProps: elementProps })), integrationProps ? (jsxRuntime.jsx(ConnectedIntegrationActions, { integrationProps: integrationProps, onAction: onAction, collapse: collapse, elementProps: elementProps })) : (jsxRuntime.jsx(IntegrationsActions, { integrations: integrations, onAction: onAction, collapse: collapse, elementProps: elementProps })), jsxRuntime.jsx(cauldronReact.CopyButton, { value: shareURL || location.href, variant: "secondary", onCopy: () => onAction({ key: 'copy-issue-link' }), hideVisibleLabel: collapse, tooltipPlacement: collapse ? 'bottom' : undefined, ...elementProps, children: t('Copy issue link') })] }));
};

var modules_1eab8564 = {"IssueHeader":"_IssueHeader_914daa24","containerContext":"_containerContext_914daa24","stickyTopBar":"_stickyTopBar_914daa24","title":"_title_914daa24","actions":"_actions_914daa24","sticky":"_sticky_914daa24","layoutContainer":"_layoutContainer_914daa24","noticeContainer":"_noticeContainer_914daa24","leadingHeaderActionContainer":"_leadingHeaderActionContainer_914daa24","subHeader":"_subHeader_914daa24","TopBar__paginationContainer":"_TopBar__paginationContainer_914daa24","TopBar__actions":"_TopBar__actions_914daa24","ownActions":"_ownActions_914daa24"};

const IssueHeader = React.forwardRef(({ issue, leadingHeaderActions, headerBreadcrumb, totalIssueCount, currentIssueNumber, issuePaginationLabel, onAction = /* c8 ignore next: default fallback value */ () => null, onNextIssue = /* c8 ignore next: default fallback value */ () => null, onPreviousIssue = /* c8 ignore next: default fallback value */ () => null, integrationProps, shareURL, showActions = true, showTopBar = true, className, ...props }, ref) => {
    const { t } = useTranslation();
    const { titleRef, titleActions } = React.useContext(IssuePageContext);
    const ownActionsRef = React.useRef(null);
    const ownsActions = !titleActions;
    const actionsRef = titleActions?.ref ?? ownActionsRef;
    const paginationRef = React.useRef(null);
    const [showTopBarTitle, setShowTopBarTitle] = React.useState(false);
    const [showTopBarPagination, setShowTopBarPagination] = React.useState(false);
    const { mirrorRef, actionsHidden: showTopBarActions, setActionsHidden: setShowTopBarActions } = useMirroredActions(actionsRef);
    const isFirstPage = currentIssueNumber === 1;
    const isLastPage = currentIssueNumber === totalIssueCount;
    const showPagination = typeof totalIssueCount === 'number' &&
        typeof currentIssueNumber === 'number' &&
        !!totalIssueCount &&
        !!currentIssueNumber;
    const showNotices = issue.needsReview || issue.duplicates;
    // Detect intersection of issue actions to determine if actions need
    // to be shown within the topbar
    const topBarHeight = useCSSPropertyValue('--top-bar-height');
    useIntersectionObserver({
        element: actionsRef,
        root: null,
        rootMargin: `-${topBarHeight} 0px 0px 0px`,
        threshold: 0.0,
        callback: entry => setShowTopBarActions(!entry.isIntersecting),
        active: showTopBar && showActions
    });
    // Detect intersection of title to determine if the title should be
    // be shown within the topbar
    useIntersectionObserver({
        element: titleRef,
        root: null,
        rootMargin: `-${topBarHeight} 0px 0px 0px`,
        threshold: 0.0,
        callback: entry => setShowTopBarTitle(!entry.isIntersecting),
        active: showTopBar && typeof titleRef !== 'undefined'
    });
    // Detect intersection of pagination to determine if the pagination
    // should be shown within the topbar
    useIntersectionObserver({
        element: paginationRef,
        root: null,
        rootMargin: `-${topBarHeight} 0px 0px 0px`,
        threshold: 0.0,
        callback: entry => setShowTopBarPagination(!entry.isIntersecting),
        active: showTopBar && showPagination
    });
    return (jsxRuntime.jsxs("div", { ref: ref, className: classnames__default.default(modules_1eab8564.IssueHeader, className), ...props, children: [showTopBar && (jsxRuntime.jsx(cauldronReact.TopBar, { className: modules_1eab8564.stickyTopBar, children: jsxRuntime.jsxs("div", { className: classnames__default.default(modules_1eab8564.sticky, modules_1eab8564.containerContext), children: [jsxRuntime.jsxs("div", { className: modules_1eab8564.leadingHeaderActionContainer, children: [leadingHeaderActions, showPagination && showTopBarPagination && (jsxRuntime.jsx("div", { className: modules_1eab8564.TopBar__paginationContainer, children: jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx(cauldronReact.IconButton, { icon: "chevron-left", label: t('Previous issue'), onClick: !isFirstPage ? onPreviousIssue : undefined, large: true, "aria-disabled": isFirstPage }), jsxRuntime.jsx(cauldronReact.IconButton, { icon: "chevron-right", label: t('Next issue'), onClick: !isLastPage ? onNextIssue : undefined, large: true, "aria-disabled": isLastPage })] }) }))] }), jsxRuntime.jsx("div", { className: modules_1eab8564.title, style: { visibility: showTopBarTitle ? undefined : 'hidden' }, children: issue.help }), jsxRuntime.jsx("div", { className: modules_1eab8564.TopBar__actions, ref: mirrorRef, children: showActions && showTopBarActions && (jsxRuntime.jsx(Actions, { integrations: issue.integrations, integrationProps: integrationProps, shareURL: shareURL, onAction: onAction, collapse: true })) })] }) })), jsxRuntime.jsxs("div", { className: modules_1eab8564.containerContext, children: [showPagination && (jsxRuntime.jsx("nav", { "aria-label": t('Pagination'), className: modules_1eab8564.subHeader, ref: paginationRef, children: jsxRuntime.jsx(cauldronReact.Pagination, { className: modules_1eab8564.layoutContainer, itemsPerPage: 1, currentPage: currentIssueNumber, totalItems: totalIssueCount, previousPageLabel: t('Previous issue'), nextPageLabel: t('Next issue'), onPreviousPageClick: onPreviousIssue, onNextPageClick: onNextIssue, statusLabel: issuePaginationLabel || (jsxRuntime.jsx(Trans, { defaults: "Viewing <strong>{{ currentIssueNumber, number }}</strong> of <strong>{{ totalIssueCount, number }}</strong> of this rule", values: { currentIssueNumber, totalIssueCount } })), hideStartEndPagination: true }) })), (headerBreadcrumb || (ownsActions && showActions)) && (jsxRuntime.jsxs("div", { className: modules_1eab8564.actions, children: [jsxRuntime.jsx("div", { children: headerBreadcrumb }), ownsActions && showActions && (jsxRuntime.jsx("div", { ref: ownActionsRef, className: modules_1eab8564.ownActions, children: jsxRuntime.jsx(Actions, { integrations: issue.integrations, integrationProps: integrationProps, shareURL: shareURL, onAction: onAction }) }))] })), showNotices && (jsxRuntime.jsxs("div", { className: modules_1eab8564.noticeContainer, children: [issue.needsReview && (jsxRuntime.jsx(cauldronReact.Notice, { type: "caution", title: t('Potential issue that needs review'), children: t('An issue requires inspection by a human to determine if the issue is an actual violation of accessibility standards.') })), issue.duplicates && (jsxRuntime.jsx(cauldronReact.Notice, { type: "info", title: typeof issue.duplicates !== 'number'
                                    ? t('This page has related duplicates')
                                    : issue.duplicates === 1
                                        ? t('This page has one related duplicate')
                                        : t('This page has {{ duplicateCount, number }} related duplicates', { duplicateCount: issue.duplicates }), children: t('Identical accessibility issues on the same element (can be within the same page/state or across multiple).') }))] }))] }), integrationProps && jsxRuntime.jsx(integrationProps.SendToIntegrationModal, {})] }));
});
IssueHeader.displayName = 'IssueHeader';

var modules_45e4b328 = {"HeaderActions":"_HeaderActions_9cb54939","row":"_row_9cb54939"};

const HeaderActions = React.forwardRef(({ issue, integrationProps, shareURL, openInNewTabHref, onAction = /* c8 ignore next: default fallback value */ () => null, className, ...props }, ref) => {
    const collapsed = React.useContext(TitleActionsCollapsedContext);
    return (jsxRuntime.jsx("div", { ref: ref, className: classnames__default.default(modules_45e4b328.HeaderActions, className), ...props, children: jsxRuntime.jsx("div", { className: modules_45e4b328.row, children: jsxRuntime.jsx(Actions, { integrations: issue.integrations, integrationProps: integrationProps, shareURL: shareURL, openInNewTabHref: openInNewTabHref, onAction: onAction, collapse: collapsed }) }) }));
});
HeaderActions.displayName = 'HeaderActions';

var modules_8fc13905 = {"IssueFlyoutDrawer":"_IssueFlyoutDrawer_b5568ed4","IssueFlyoutContent":"_IssueFlyoutContent_b5568ed4","IssueFlyoutHeaderActions":"_IssueFlyoutHeaderActions_b5568ed4","IssueFlyoutHeaderTitle":"_IssueFlyoutHeaderTitle_b5568ed4 _title_914daa24","IssueFlyoutMirroredActions":"_IssueFlyoutMirroredActions_b5568ed4"};

const IssueFlyout = ({ issue, open, totalIssueCount, currentIssueNumber, issuePaginationLabel, rightColumnChildren, leadingScreenshotActions, screenshotConfiguration, screenshotStatus, headerActions, openInNewTabHref, integrationProps, shareURL, focusOptions, onNextIssue, onPreviousIssue, onAction, onOpenChange, className, children }) => {
    const { t } = useTranslation();
    const titleRef = React.useRef(null);
    const actionsRef = React.useRef(null);
    const [titleHidden, setTitleHidden] = React.useState(false);
    const { mirrorRef, actionsHidden, setActionsHidden } = useMirroredActions(actionsRef);
    const topBarHeight = useCSSPropertyValue('--top-bar-height');
    useIntersectionObserver({
        element: titleRef,
        root: null,
        rootMargin: `-${topBarHeight} 0px 0px 0px`,
        threshold: 0,
        callback: entry => setTitleHidden(!entry.isIntersecting),
        active: !!headerActions
    }, [topBarHeight]);
    useIntersectionObserver({
        element: actionsRef,
        root: null,
        rootMargin: `-${topBarHeight} 0px 0px 0px`,
        threshold: 0,
        callback: entry => setActionsHidden(!entry.isIntersecting),
        active: !!headerActions
    }, [topBarHeight]);
    const newTabHref = openInNewTabHref ?? shareURL;
    const titleActions = {
        ref: actionsRef,
        node: (jsxRuntime.jsx(HeaderActions, { ref: actionsRef, issue: issue, integrationProps: integrationProps, shareURL: shareURL, onAction: onAction, openInNewTabHref: newTabHref }))
    };
    const mirroredActions = actionsHidden ? (jsxRuntime.jsx("div", { ref: mirrorRef, className: modules_8fc13905.IssueFlyoutMirroredActions, children: jsxRuntime.jsx(Actions, { integrations: issue.integrations, integrationProps: integrationProps, shareURL: shareURL, openInNewTabHref: newTabHref, onAction: onAction || /* c8 ignore next: default fallback value */ (() => null), collapse: true }) })) : null;
    const handleClose = () => {
        onOpenChange(false);
    };
    return (jsxRuntime.jsx(cauldronReact.Drawer, { position: "right", open: open, onClose: handleClose, className: classnames__default.default(modules_8fc13905.IssueFlyoutDrawer, className), "aria-label": issue.help || t('Issue details'), role: "dialog", "aria-modal": "true", focusOptions: focusOptions, children: jsxRuntime.jsx("div", { className: modules_8fc13905.IssueFlyoutContent, children: jsxRuntime.jsxs(IssuePageContext.Provider, { value: { titleRef, titleActions }, children: [headerActions && (jsxRuntime.jsx(cauldronReact.TopBar, { className: modules_8fc13905.IssueFlyoutHeaderActions, children: typeof headerActions === 'function'
                            ? headerActions({
                                titleHidden,
                                actions: mirroredActions,
                                title: issue.help ? (jsxRuntime.jsx("span", { className: modules_8fc13905.IssueFlyoutHeaderTitle, "aria-hidden": "true", children: issue.help })) : null
                            })
                            : headerActions })), jsxRuntime.jsx(IssueHeader, { issue: issue, totalIssueCount: totalIssueCount, currentIssueNumber: currentIssueNumber, issuePaginationLabel: issuePaginationLabel, onNextIssue: onNextIssue, onPreviousIssue: onPreviousIssue, onAction: onAction, integrationProps: integrationProps, shareURL: shareURL, showTopBar: false }), children, !children && (jsxRuntime.jsx(Issue, { issue: issue, rightColumnChildren: rightColumnChildren, leadingScreenshotActions: leadingScreenshotActions, screenshotConfiguration: screenshotConfiguration, screenshotStatus: screenshotStatus }))] }) }) }));
};
IssueFlyout.displayName = 'IssueFlyout';

var modules_4d37d440 = {"IssuePage":"_IssuePage_9143cc67","scrollPadding":"_scrollPadding_9143cc67","IssuePageContainer":"_IssuePageContainer_9143cc67"};

const IssuePage = React.forwardRef(({ issue, leadingHeaderActions, headerBreadcrumb, rightColumnChildren, totalIssueCount, currentIssueNumber, onNextIssue, onPreviousIssue, issuePaginationLabel, leadingScreenshotActions, screenshotConfiguration, screenshotStatus, onAction, integrationProps, shareURL, showActions = true, className, 
// @ts-expect-error - we do not want children to be exposed in the TypeScript types
children, ...props }, ref) => {
    const titleRef = React.useRef(null);
    const actionsRef = React.useRef(null);
    const { help } = issue;
    React.useEffect(() => {
        document.documentElement.classList.add(modules_4d37d440.scrollPadding);
        return () => {
            document.documentElement.classList.remove(modules_4d37d440.scrollPadding);
        };
    }, []);
    React.useEffect(() => {
        const previousTitle = document.title;
        document.title = help;
        return () => {
            document.title = previousTitle;
        };
    }, [help]);
    const titleActions = showActions
        ? {
            ref: actionsRef,
            node: (jsxRuntime.jsx(HeaderActions, { ref: actionsRef, issue: issue, integrationProps: integrationProps, shareURL: shareURL, onAction: onAction }))
        }
        : null;
    return (jsxRuntime.jsx("div", { className: classnames__default.default(modules_4d37d440.IssuePage, className), ref: ref, ...props, children: jsxRuntime.jsx("div", { className: modules_4d37d440.IssuePageContainer, children: jsxRuntime.jsxs(IssuePageContext.Provider, { value: { titleRef, titleActions }, children: [jsxRuntime.jsx(IssueHeader, { issue: issue, leadingHeaderActions: leadingHeaderActions, headerBreadcrumb: headerBreadcrumb, totalIssueCount: totalIssueCount, currentIssueNumber: currentIssueNumber, issuePaginationLabel: issuePaginationLabel, onNextIssue: onNextIssue, onPreviousIssue: onPreviousIssue, onAction: onAction, integrationProps: integrationProps, shareURL: shareURL, showActions: showActions, className: modules_4d37d440.issueHeader }), children ? (children) : (jsxRuntime.jsx(Issue, { issue: issue, rightColumnChildren: rightColumnChildren, leadingScreenshotActions: leadingScreenshotActions, screenshotConfiguration: screenshotConfiguration, screenshotStatus: screenshotStatus }))] }) }) }));
});
IssuePage.displayName = 'IssuePage';

const Properties = Object.freeze({
    /**
     * Displays a short description of the rule.
     */
    Rule: {
        type: Symbol('Rule'),
        align: 'start',
        width: 'auto',
        maxWidth: '432'
    },
    /**
     * Displays the impact level of the issue (if present).
     */
    Impact: {
        type: Symbol('Impact'),
        align: 'start',
        width: 'max-content'
    },
    /**
     * Displays the date and time of which the issue was found.
     */
    DateTime: {
        type: Symbol('DateTime'),
        align: 'start',
        width: 'max-content'
    },
    /**
     * Displays the page title and page url of the page of which the issue was found (if applicable).
     */
    PageInfo: {
        type: Symbol('PageInfo'),
        align: 'start',
        width: 'auto',
        maxWidth: '432'
    },
    /**
     * Displays the title of the scan that the issue was found on. Mobile product only.
     */
    ScreenTitle: {
        type: Symbol('ScreenTitle'),
        align: 'start',
        width: 'auto',
        maxWidth: '432'
    },
    /**
     * Displays the name of the scan that the issue was found on. Mobile product only.
     */
    ScanName: {
        type: Symbol('ScanName'),
        align: 'start',
        width: 'auto',
        maxWidth: '432'
    },
    /**
     * Displays where in the page the issue was found: the server-generated node
     * label, falling back to the element's CSS selector when no label exists.
     */
    Location: {
        type: Symbol('Location'),
        align: 'start',
        width: 'auto',
        maxWidth: '432'
    },
    /**
     * Displays the name of the test file the issue was found in. Web product only.
     */
    TestName: {
        type: Symbol('TestName'),
        align: 'start',
        width: 'auto',
        maxWidth: '432'
    },
    /**
     * Displays the status of the issue.
     */
    Status: {
        type: Symbol('Status'),
        align: 'start',
        width: 'auto',
        maxWidth: '432'
    },
    /**
     * Displays the criteria the rule is associated with, inside the chosen standard.
     */
    Criteria: {
        type: Symbol('Criteria'),
        align: 'start',
        width: 'max-content'
    },
    ElementSource: {
        type: Symbol('ElementSource'),
        align: 'start',
        width: 'auto',
        maxWidth: '432'
    },
    /**
     * Displays discovery method of the issue.
     */
    DiscoveryMethod: {
        type: Symbol('DiscoveryMethod'),
        align: 'start',
        width: 'max-content',
        maxWidth: '432'
    }
});
/**
 *  A property that displays custom content for the provided label.
 *
 * `type` is the column's sort identity. Spreading this constant gives every
 * custom column the same symbol, so a list with more than one sortable custom
 * column must override `type` with its own module-level `Symbol()`; minting one
 * inside a render or memo instead gives the column a new identity on each
 * recompute.
 */
const CustomProperty = Object.freeze({
    type: Symbol('Custom'),
    align: 'start',
    width: 'auto'
});
const AggregateProperties = Object.freeze({
    /**
     * Displays the aggregated count of issues when the data is grouped by a field or set of fields for a given issue.
     */
    IssueCount: {
        type: Symbol('IssueCount'),
        aggregateKey: 'aggregateIssueCount',
        align: 'end',
        width: 'max-content'
    },
    /**
     * Displays the aggregated count of critical issues when the data is grouped by a field or set of fields for a given issue.
     */
    CriticalIssueCount: {
        type: Symbol('CriticalIssueCount'),
        aggregateKey: 'aggregateCriticalIssueCount',
        align: 'end',
        width: 'max-content'
    },
    /**
     * Displays the aggregated count of new issues when the data is grouped by a field or set of fields for a given issue.
     */
    NewIssueCount: {
        type: Symbol('NewIssueCount'),
        aggregateKey: 'aggregateNewIssueCount',
        align: 'end',
        width: 'max-content'
    },
    /**
     * Displays the aggregated count of serious issues when the data is grouped by a field or set of fields for a given issue.
     */
    SeriousIssueCount: {
        type: Symbol('SeriousIssueCount'),
        aggregateKey: 'aggregateSeriousIssueCount',
        align: 'end',
        width: 'max-content'
    },
    /**
     * A column that displays a number representing an aggregated critical issue count
     */
    PageCount: {
        type: Symbol('PageCount'),
        aggregateKey: 'aggregatePageCount',
        align: 'end',
        width: 'max-content'
    }
});
/**
 * Keys that `groupBy` accepts but that are never displayed as a column, so
 * they carry no render function and are not in `Properties`.
 */
const GroupByProperties = Object.freeze({
    /**
     * One row per element (Type 2 grouping), keyed by `elementFingerprint`.
     */
    ElementFingerprint: {
        type: Symbol('ElementFingerprint')
    }
});
/**
 * Returns the corresponding property key for an aggregate property configuration type.
 */
function getAggregateKey(type) {
    const config = Object.values(AggregateProperties).find((column) => column.type === type);
    if (!config) {
        throw new Error(`Unknown aggregate column type: ${type.toString()}`);
    }
    return config.aggregateKey;
}

const AggregateCountRenderFunction = ({ issue, propertyConfiguration }) => (jsxRuntime.jsx(jsxRuntime.Fragment, { children: issue[propertyConfiguration.aggregateKey] ?? 0 }));

var modules_58b1fda2 = {"CriteriaList":"_CriteriaList_f048979b","CriteriaListStacked":"_CriteriaListStacked_f048979b"};

const useGroupedCheckpoints = (checkpoints, standards) => {
    return React.useMemo(() => {
        const getFirstAvailableCheckpoint = () => {
            const wcagCheckpoints = checkpoints.filter(isWCAGCheckpointTagInfo);
            if (wcagCheckpoints.length > 0) {
                return {
                    selectedCheckpoints: wcagCheckpoints.slice(0, 1),
                    hidden: 0
                };
            }
            const groupedByType = checkpoints.reduce((acc, cp) => {
                const type = cp.standardInfo.type;
                if (!acc[type]) {
                    acc[type] = [];
                }
                acc[type].push(cp);
                return acc;
            }, {});
            const standardTypes = Object.keys(groupedByType);
            if (standardTypes.length === 0) {
                return { selectedCheckpoints: [], hidden: 0 };
            }
            const firstStandard = groupedByType[standardTypes[0]][0];
            return {
                selectedCheckpoints: [firstStandard],
                hidden: Math.max(0, standardTypes.length - 1)
            };
        };
        const getSelectedCheckpoints = () => {
            const selectedCheckpoints = standards.reduce((acc, standard) => {
                // Skip if we already have a WCAG checkpoint and current standard is WCAG-related
                const hasWcagCheckpoint = acc.some(checkpoint => checkpoint.standard === 'WCAG');
                if (hasWcagCheckpoint && standard.toLowerCase().startsWith('wcag')) {
                    return acc;
                }
                const firstMatch = checkpoints.find(checkpoint => {
                    if (standard.toLowerCase().startsWith('wcag')) {
                        return checkpoint.standard === 'WCAG';
                    }
                    return (checkpoint.standardInfo.type === checkpoint.standard &&
                        checkpoint.standardInfo.tags.includes(standard));
                });
                if (firstMatch) {
                    acc.push(firstMatch);
                }
                return acc;
            }, []);
            return {
                selectedCheckpoints,
                hidden: Math.max(0, checkpoints.length - selectedCheckpoints.length)
            };
        };
        if (!standards.length || standards.includes('all')) {
            const result = getFirstAvailableCheckpoint();
            return [result.selectedCheckpoints, result.hidden];
        }
        const result = getSelectedCheckpoints();
        return [result.selectedCheckpoints, result.hidden];
    }, [checkpoints, standards]);
};
const CriteriaToDisplay = ({ checkpoints, hiddenCount }) => {
    if (!checkpoints.length) {
        return null;
    }
    return (jsxRuntime.jsxs("ul", { className: classnames__default.default(modules_58b1fda2.CriteriaList, {
            [modules_58b1fda2.CriteriaListStacked]: checkpoints.length > 1
        }), children: [checkpoints.map(checkpoint => (jsxRuntime.jsx("li", { children: jsxRuntime.jsx(SuccessCriteriaBadge, { ruleset: checkpoint.standardInfo, checkpoint: checkpoint }) }, checkpoint.value))), hiddenCount > 0 && (jsxRuntime.jsx("li", { children: jsxRuntime.jsxs(cauldronReact.Badge, { children: ["+", hiddenCount] }) }))] }));
};
const CriteriaRenderFunction = (({ issue, options }) => {
    const { tags = [] } = issue;
    const { accessibilityStandard } = options;
    const checkpoints = useCheckpointsTagInfo(tags);
    const accessibilityStandards = useAccessibilityStandards(tags);
    const issueAccessibilityStandards = React.useMemo(() => {
        if (!accessibilityStandard ||
            (Array.isArray(accessibilityStandard) && !accessibilityStandard.length)) {
            return ['all'];
        }
        const standardArray = Array.isArray(accessibilityStandard)
            ? accessibilityStandard
            : [accessibilityStandard];
        return standardArray;
    }, [accessibilityStandard]);
    const checkpointWithStandards = React.useMemo(() => {
        return checkpoints.map(checkpoint => ({
            ...checkpoint,
            standardInfo: accessibilityStandards.find(s => s.type === checkpoint.standard)
        }));
    }, [checkpoints, accessibilityStandards]);
    const [shownCheckpoints, hiddenCount] = useGroupedCheckpoints(checkpointWithStandards, issueAccessibilityStandards);
    if (tags.includes('best-practice')) {
        return jsxRuntime.jsx(BestPracticeBadge, {});
    }
    return (jsxRuntime.jsx(CriteriaToDisplay, { checkpoints: shownCheckpoints, hiddenCount: hiddenCount }));
});

const TIME_FORMAT = 'hh:mm:ss aa';
const DATE_FORMAT = 'MMM dd, yyyy';
const DATE_TIME_FORMAT$1 = `${DATE_FORMAT} (${TIME_FORMAT})`;
const DateTimeRenderFunction = (({ issue }) => {
    const { t } = useTranslation();
    const isToday = issue.createdAt && dateFns.isToday(issue.createdAt);
    const isYesterday = issue.createdAt && dateFns.isYesterday(issue.createdAt);
    const isRelative = !!isToday || !!isYesterday;
    const formattedDate = issue.createdAt
        ? dateFns.format(issue.createdAt, DATE_TIME_FORMAT$1)
        : null;
    const relativeDate = isRelative && issue.createdAt
        ? `${isToday ? t('Today') : t('Yesterday')} (${dateFns.format(issue.createdAt, TIME_FORMAT)})`
        : null;
    return formattedDate ? (jsxRuntime.jsx("time", { dateTime: formattedDate, children: isRelative ? relativeDate : formattedDate })) : null;
});

/**
 * getIssueDiscoveryMethod is used to get the discovery method of an issue
 * @param issue - The issue object
 * @param t - The translation function
 * @returns The discovery method of the issue
 */
const getIssueDiscoveryMethod = (issue, t) => {
    if (issue.isManual) {
        return t('Manual');
    }
    if (issue.igt) {
        return `${issue.igt} IGT`;
    }
    return t('Automatic');
};

const DiscoveryMethodRenderFunction = (({ issue }) => {
    const { t } = useTranslation();
    const discoveryMethod = getIssueDiscoveryMethod(issue, t);
    return jsxRuntime.jsx("span", { children: discoveryMethod });
});

const ElementSourceRenderFunction = (({ issue }) => {
    if (issue.source) {
        return (jsxRuntime.jsx(cauldronReact.TextEllipsis, { maxLines: 5, hideTooltip: true, children: issue.source }));
    }
    return null;
});

const ImpactRenderFunction = (({ issue }) => issue.impact ? (jsxRuntime.jsx(cauldronReact.ImpactBadge, { type: issue.impact, label: jsxRuntime.jsx(ImpactBadgeLabel, { type: issue.impact, hideLabel: true }) })) : null);

const resolveLocation = (issue) => {
    if (issue.nodeLabel) {
        return issue.nodeLabel;
    }
    return issue.selector?.length
        ? formatSelector({ selector: issue.selector })
        : '';
};

var modules_980c203e = {"location":"_location_e78ef43d"};

const LocationRenderFunction = (({ issue }) => {
    const location = resolveLocation(issue);
    return location ? jsxRuntime.jsx("span", { className: modules_980c203e.location, children: location }) : null;
});

var modules_cfd1b1a9 = {"pageTitle":"_pageTitle_3ee69462","pageUrl":"_pageUrl_3ee69462"};

const PageInfoRenderFunction = (({ issue }) => (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [issue.testPageTitle && (jsxRuntime.jsx("div", { className: modules_cfd1b1a9.pageTitle, children: issue.testPageTitle })), issue.testUrl && jsxRuntime.jsx("div", { className: modules_cfd1b1a9.pageUrl, children: issue.testUrl })] })));

const RuleRenderFunction = (({ issue, options }) => {
    const { t } = useTranslation();
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [options.renderLink ? (jsxRuntime.jsx(cauldronReact.Link, { href: issue.href, children: issue.help })) : (jsxRuntime.jsx("div", { children: issue.help })), issue.isExperimental && jsxRuntime.jsx(cauldronReact.Badge, { children: t('Experimental') })] }));
});

var modules_368e0595 = {"screenTitle":"_screenTitle_31a5547f"};

const ScreenTitleRenderFunction = (({ issue }) => (jsxRuntime.jsx(jsxRuntime.Fragment, { children: issue.screenTitle ? (jsxRuntime.jsx("span", { className: modules_368e0595.screenTitle, children: issue.screenTitle })) : null })));

const StatusRenderFunction = (({ issue }) => {
    return (jsxRuntime.jsx("div", { children: issue.status?.map((status, index) => (jsxRuntime.jsx("p", { children: status }, `${index}-status`))) }));
});

/**
 * Derives the test name shown by the Test name filter from an issue's
 * `testFilePath`.
 *
 * Issues sharing a file path can come from different tests within that file, so
 * there is no single test title to attribute to the path — the base name is the
 * closest identifier we can show, with the full path kept as a secondary
 * description. Paths reach us from runners on both POSIX and Windows, and
 * ingestion can leave a trailing separator or collapse segments, so any run of
 * `/` or `\` counts as a separator and blank segments are ignored.
 */
const getTestNameFromFilePath = (testFilePath) => {
    const segments = (testFilePath ?? '')
        .split(/[/\\]+/)
        .map(segment => segment.trim())
        .filter(Boolean);
    return segments[segments.length - 1];
};

var modules_d79906b0 = {"testName":"_testName_15a6351d"};

const TestNameRenderFunction = (({ issue }) => {
    const testName = getTestNameFromFilePath(issue.testFilePath);
    return (jsxRuntime.jsx(jsxRuntime.Fragment, { children: testName ? (jsxRuntime.jsx("span", { className: modules_d79906b0.testName, title: issue.testFilePath, children: testName })) : null }));
});

var modules_05160e4d = {"scanName":"_scanName_ab3967d5"};

const ScanNameRenderFunction = (({ issue }) => {
    const { t } = useTranslation();
    if (issue.scanName) {
        return jsxRuntime.jsx("span", { className: modules_05160e4d.scanName, children: issue.scanName });
    }
    return jsxRuntime.jsx("span", { children: t('No scan name') });
});

function throwBadRenderFunction(renderFunction) {
    throw new Error(`Unsupported property type renderFunction value: ${renderFunction}`);
}
function hasValidRenderFunction(propertyConfiguration) {
    return typeof propertyConfiguration.renderFunction === 'function';
}
function isAggregateProperty(propertyConfiguration) {
    return (typeof propertyConfiguration
        .aggregateKey === 'string');
}
const getRequiredPropertyConfiguration = (propertyConfiguration) => {
    if (hasValidRenderFunction(propertyConfiguration)) {
        return propertyConfiguration;
    }
    let renderFunction;
    if (isAggregateProperty(propertyConfiguration)) {
        renderFunction = ({ issue }) => (jsxRuntime.jsx(AggregateCountRenderFunction, { issue: issue, propertyConfiguration: propertyConfiguration }));
    }
    else {
        switch (propertyConfiguration.type) {
            case Properties.Rule.type:
                renderFunction = RuleRenderFunction;
                break;
            case Properties.Impact.type:
                renderFunction = ImpactRenderFunction;
                break;
            case Properties.PageInfo.type:
                renderFunction = PageInfoRenderFunction;
                break;
            case Properties.DateTime.type:
                renderFunction = DateTimeRenderFunction;
                break;
            case Properties.ScreenTitle.type:
                renderFunction = ScreenTitleRenderFunction;
                break;
            case Properties.ScanName.type:
                renderFunction = ScanNameRenderFunction;
                break;
            case Properties.TestName.type:
                renderFunction = TestNameRenderFunction;
                break;
            case Properties.Location.type:
                renderFunction = LocationRenderFunction;
                break;
            case Properties.Status.type:
                renderFunction = StatusRenderFunction;
                break;
            case Properties.Criteria.type:
                renderFunction = CriteriaRenderFunction;
                break;
            case Properties.ElementSource.type:
                renderFunction = ElementSourceRenderFunction;
                break;
            case Properties.DiscoveryMethod.type:
                renderFunction = DiscoveryMethodRenderFunction;
                break;
            default:
                throwBadRenderFunction(propertyConfiguration.renderFunction);
        }
    }
    return { ...propertyConfiguration, renderFunction };
};
function renderProperty(propertyConfiguration, issue, options) {
    const requiredPropertyConfiguration = getRequiredPropertyConfiguration(propertyConfiguration);
    const RenderFunctionComponent = requiredPropertyConfiguration.renderFunction;
    return jsxRuntime.jsx(RenderFunctionComponent, { issue: issue, options: options });
}

/**
 * Calculates aggregate counts for a collection of issues.
 */
function getAggregateCount(issues) {
    const aggregateIssueCount = issues.length;
    let aggregateCriticalIssueCount = 0;
    let aggregateSeriousIssueCount = 0;
    let aggregateNewIssueCount = 0;
    const uniquePages = new Set();
    for (const issue of issues) {
        if (issue.impact === 'critical') {
            aggregateCriticalIssueCount++;
        }
        if (issue.impact === 'serious') {
            aggregateSeriousIssueCount++;
        }
        if (issue.isNewIssue === true) {
            aggregateNewIssueCount++;
        }
        // Pages should have their aggregate totals calculated per each
        // unique set of url + page title.
        uniquePages.add([issue.testPageTitle ?? '', issue.testUrl ?? ''].join(','));
    }
    return {
        aggregateIssueCount,
        aggregateCriticalIssueCount,
        aggregateSeriousIssueCount,
        aggregateNewIssueCount,
        aggregatePageCount: uniquePages.size
    };
}
/**
 * Groups an array of issues by a specified property value. Creates separate
 * arrays for each unique value of the given property.
 */
function groupByProperty(issues, property) {
    return Object.values(issues.reduce((aggregate, issue) => {
        const key = issue[property];
        // @ts-expect-error using string coercion for object keys in reduce aggregation.
        // This is acceptable for now since we're only grouping by ruleId, which is a string
        // and avoiding additional complexity from more complex issue object values
        aggregate[key] ??= [];
        aggregate[key].push(issue);
        return aggregate;
    }, {}));
}
function groupByRule(issues) {
    return groupByProperty(issues, 'ruleId').map(groupedIssues => ({
        issues: groupedIssues,
        groupBy: Properties.Rule,
        groupByValue: groupedIssues[0].ruleId,
        ...getAggregateCount(groupedIssues)
    }));
}
/**
 * One row per element, ignoring the rule and the page address — the Type 2
 * grouping. It keys off `elementFingerprint`, the opaque element identity,
 * rather than the location text the rows display: two labels can read alike
 * and two labels for one element can differ by a uniqueness number, so the
 * text is not an identity. Findings carrying no identity have no shared key,
 * so they are dropped here rather than collapsing into one bogus group; the
 * caller keeps them in its ungrouped half so no issue goes missing.
 */
function groupByElementFingerprint(issues) {
    return groupByProperty(issues.filter(issue => !!issue.elementFingerprint), 'elementFingerprint').map(groupedIssues => ({
        issues: groupedIssues,
        groupBy: GroupByProperties.ElementFingerprint,
        // Every member shares the fingerprint it was grouped on.
        groupByValue: groupedIssues[0].elementFingerprint,
        ...getAggregateCount(groupedIssues)
    }));
}
/**
 * Aggregates and optionally groups issues based on specified properties.
 */
const useAggregateIssues = ({ issues, groupBy }) => {
    return React.useMemo(() => {
        if (!groupBy) {
            return issues;
        }
        switch (groupBy.type) {
            case Properties.Rule.type:
                return groupByRule(issues);
            case GroupByProperties.ElementFingerprint.type:
                return groupByElementFingerprint(issues);
            default:
                // Only rule and element grouping are supported; other groupings may be
                // implemented in the future as needed.
                throw new Error(`Unsupported "groupBy" property: ${groupBy.type.description}`);
        }
    }, [issues, groupBy]);
};

const LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE = Symbol('__LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE__');
const isEmpty = (value) => {
    return (value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === ''));
};
const isObject = (value) => value !== null && typeof value === 'object';
const hasArrayIntersection = (a, b) => {
    if (a.length === 0 && b.length === 0) {
        return true;
    }
    const isObjectsArrayA = a.every(isObject);
    const isObjectsArrayB = b.every(isObject);
    if (isObjectsArrayB && isObjectsArrayA) {
        return b.some(value => a.some(v => hasObjectIntersection(value, v)));
    }
    return a.some(value => b.includes(value));
};
const isNonNullObject = (a) => a !== null && typeof a === 'object';
const hasObjectIntersection = (a, b) => {
    const isNonNullObjectA = isNonNullObject(a);
    const isNonNullObjectB = isNonNullObject(b);
    const isNullOrEmptyA = !isNonNullObjectA || Object.keys(a).length === 0;
    const isNullOrEmptyB = !isNonNullObjectB || Object.keys(b).length === 0;
    if (isNullOrEmptyA && isNullOrEmptyB) {
        return true;
    }
    const deepEqual = (val1, val2) => {
        if (val1 === val2) {
            return true;
        } // handles primitives, same reference or identical values
        // Handle if either value is an object or array (deep comparison)
        if (typeof val1 === 'object' &&
            val1 !== null &&
            typeof val2 === 'object' &&
            val2 !== null) {
            if (Array.isArray(val1) && Array.isArray(val2)) {
                // Arrays check
                return hasArrayIntersection(val1, val2);
            }
            // Object check
            if (!Array.isArray(val1) && !Array.isArray(val2)) {
                const keys1 = Object.keys(val1);
                const keys2 = Object.keys(val2);
                return keys1.some(key => keys2.includes(key) &&
                    deepEqual(val1[key], val2[key]));
            }
        }
        return false; // Return false if no match was found
    };
    return (isNonNullObjectA &&
        Object.entries(a).some(([keyA, valueA]) => 
        // For each key-value pair in `a`, check if any matching key-value pair exists in `b`
        isNonNullObjectB &&
            Object.entries(b).some(([keyB, valueB]) => keyA === keyB && deepEqual(valueA, valueB))));
};
/**
 * Categories combine with AND — an issue must satisfy every active category —
 * while values within one category combine with OR. A category with no
 * selected values is not a constraint.
 */
function matchesIssueFilter(issue, issuesFilter) {
    const activeFilters = Object.entries(issuesFilter).filter(([, filterValues]) => filterValues?.length);
    if (!activeFilters.length) {
        return true;
    }
    return activeFilters.every(([key, value]) => {
        const issueProperty = issue[key];
        const filterValues = value;
        if (Array.isArray(issueProperty) &&
            filterValues.every(filterValue => Array.isArray(filterValue))) {
            return filterValues.some(valuesArray => valuesArray.length
                ? hasArrayIntersection(issueProperty, valuesArray)
                : false);
        }
        if (typeof issueProperty === 'object' &&
            issueProperty !== null &&
            !Array.isArray(issueProperty) &&
            filterValues.every(filterValue => typeof filterValue === 'object')) {
            return filterValues.some(filterValue => filterValue &&
                !Array.isArray(filterValue) &&
                hasObjectIntersection(issueProperty, filterValue));
        }
        let hasEmptyFilter = false;
        let hasValidFilter = false;
        for (const filterValue of filterValues) {
            if (filterValue === LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE) {
                hasEmptyFilter = true;
            }
            else if (issueProperty && filterValue === issueProperty) {
                hasValidFilter = true;
                break; // Early exit when we find a match
            }
        }
        return hasValidFilter || (hasEmptyFilter && isEmpty(issueProperty));
    });
}
/** Counts filter categories, not selected values: two rules are one filter. */
const countAppliedFilters = (filter) => Object.values(filter).filter(values => values?.length).length;

/**
 * This comparison function returns comparison values that ensures that
 * null and undefined always follow defined values.
 */
function nullOrUndefinedComparison(a, b) {
    if (a === b) {
        return 0;
    }
    else if (typeof a === 'undefined' || a === null) {
        return 1;
    }
    else {
        return -1;
    }
}
function notNullOrUndefined(value) {
    return value !== null && typeof value !== 'undefined';
}

/**
 * Sorts issues alphanumerically, including numbers
 */
const sortByAlphaNumeric = (property) => {
    return function (a, b) {
        const issueAProperty = notNullOrUndefined(a[property])
            ? String(a[property])
            : a[property];
        const issueBProperty = notNullOrUndefined(b[property])
            ? String(b[property])
            : b[property];
        if (!notNullOrUndefined(issueAProperty) ||
            !notNullOrUndefined(issueBProperty)) {
            return nullOrUndefinedComparison(issueAProperty, issueBProperty);
        }
        return issueAProperty.localeCompare(issueBProperty, undefined, {
            numeric: true
        });
    };
};

/**
 * axe-core issues can have multiple tags that reference different accessibility
 * standards (e.g., WCAG, Section 508, etc.). We only need to consider WCAG tags
 * that have an associated checkpoint (e.g., "1.1.1", "2.4.7") for sorting purposes,
 * allowing us to sort issues by their WCAG criteria in a meaningful order.
 */
function filterByWCAGCriteria(tagInfo) {
    if (!tagInfo) {
        return false;
    }
    return tagInfo.standard === 'WCAG' && !!tagInfo.checkpoint?.length;
}
/**
 * Sorts issues by accessibility criteria by their WCAG checkpoint value
 */
const sortByCriteria = (a, b) => {
    const allCriteriaIssueA = Array.isArray(a.tags)
        ? a.tags.map(accessibilityStandards.getTagInfo).filter(filterByWCAGCriteria)
        : [];
    const allCriteriaIssueB = Array.isArray(b.tags)
        ? b.tags.map(accessibilityStandards.getTagInfo).filter(filterByWCAGCriteria)
        : [];
    // Find the first criteria checkpoint for each issue and use it for comparison
    const criteriaA = allCriteriaIssueA[0]?.checkpoint ?? null;
    const criteriaB = allCriteriaIssueB[0]?.checkpoint ?? null;
    if (!notNullOrUndefined(criteriaA) || !notNullOrUndefined(criteriaB)) {
        return nullOrUndefinedComparison(criteriaA, criteriaB);
    }
    return criteriaA.localeCompare(criteriaB, undefined, {
        numeric: true
    });
};

function getCategoryOrder({ isManual, igt }) {
    if (isManual && !igt) {
        return 3;
    }
    if (igt) {
        return 2;
    }
    return 1;
}
/**
 * Sorts by issue discovery method, by automatic, then igt, then manual.
 */
const sortByDiscoveryMethod = (a, b) => {
    const isManualIssueA = a.isManual;
    const isManualIssueB = b.isManual;
    const igtIssueA = a.igt ?? null;
    const igtIssueB = b.igt ?? null;
    const categoryA = getCategoryOrder({
        isManual: isManualIssueA,
        igt: igtIssueA
    });
    const categoryB = getCategoryOrder({
        isManual: isManualIssueB,
        igt: igtIssueB
    });
    // If issue falls into different categories, sort by category order
    if (categoryA !== categoryB) {
        return categoryA - categoryB;
    }
    // When both issues are igts, sort alphanumerically
    if (igtIssueA && igtIssueB) {
        return igtIssueA.localeCompare(igtIssueB);
    }
    return 0;
};

const severityOrder = {
    critical: 1,
    serious: 2,
    moderate: 3,
    minor: 4
};
// Note: specifying unknown values to force unknown issue impacts to always come after minor issues
const UNKNOWN_IMPACT = 5;
/**
 * Sorts by impact value, from highest (critical) to lowest (minor).
 */
const sortByImpact = (a, b) => {
    // @ts-expect-error allow null or undefined as an index type
    const issueAImpact = severityOrder[a.impact] ?? UNKNOWN_IMPACT;
    // @ts-expect-error allow null or undefined as an index type
    const issueBImpact = severityOrder[b.impact] ?? UNKNOWN_IMPACT;
    return issueAImpact - issueBImpact;
};

const sortByLocation = (a, b) => {
    // An unresolvable location is "no value", not the empty string: comparing ''
    // directly would sort every unlocated row ahead of the located ones, the
    // opposite of every other sortable column.
    const locationA = resolveLocation(a);
    const locationB = resolveLocation(b);
    if (!locationA || !locationB) {
        return nullOrUndefinedComparison(locationA || null, locationB || null);
    }
    return locationA.localeCompare(locationB, undefined, {
        numeric: true
    });
};

/**
 * Sorts issues by timestamp value, from most recent to least recent
 */
const sortByTimestamp = (property) => {
    return function (a, b) {
        const issueATimestamp = a[property] ?? -1;
        const issueBTimestamp = b[property] ?? -1;
        const timeA = typeof issueATimestamp === 'string'
            ? new Date(issueATimestamp).getTime()
            : -1;
        const timeB = typeof issueBTimestamp === 'string'
            ? new Date(issueBTimestamp).getTime()
            : -1;
        if (timeA === timeB) {
            return 0;
        }
        else if (timeA === -1 || isNaN(timeA)) {
            return 1;
        }
        else if (timeB === -1 || isNaN(timeB)) {
            return -1;
        }
        return timeB - timeA;
    };
};

const SortDirections = {
    Ascending: 'ascending',
    Descending: 'descending',
    None: 'none'
};
function useIssuesSort(issues, options) {
    const { sortProperty, sortDirection = SortDirections.None } = options;
    // By default, all comparison functions should sort values ascending. When the direction is
    // descending this function will invert the sort direction using the same comparison function.
    const sortByDirection = (sortComparisonFunction, inverseNatural = false) => {
        return function SortByDirection(a, b) {
            const naturalOrder = inverseNatural ? -1 : 1;
            const directionMultiplier = sortDirection === SortDirections.Ascending ? 1 : -1;
            return sortComparisonFunction(a, b) * directionMultiplier * naturalOrder;
        };
    };
    // Properties that follow a consistent pattern: sortByAlphaNumeric(propertyName).
    // The switch below handles properties with unique sorting logic or those that
    // don't directly map to a single Issue property.
    const AlphanumericSortTypes = {
        [Properties.PageInfo.type]: 'testPageTitle',
        [Properties.ScreenTitle.type]: 'screenTitle',
        [Properties.Rule.type]: 'help',
        [Properties.Status.type]: 'status',
        [Properties.ElementSource.type]: 'source',
        [Properties.ScanName.type]: 'scanName'
    };
    const getSortComparisonFunction = React.useCallback((property) => {
        if (AlphanumericSortTypes[property]) {
            return sortByDirection(sortByAlphaNumeric(AlphanumericSortTypes[property]));
        }
        switch (property) {
            case Properties.Impact.type:
                return sortByDirection(sortByImpact);
            case Properties.Location.type:
                return sortByDirection(sortByLocation);
            case Properties.DateTime.type:
                return sortByDirection(sortByTimestamp('createdAt'));
            case Properties.Criteria.type:
                return sortByDirection(sortByCriteria);
            case Properties.DiscoveryMethod.type:
                return sortByDirection(sortByDiscoveryMethod);
            case AggregateProperties.IssueCount.type:
            case AggregateProperties.CriticalIssueCount.type:
            case AggregateProperties.NewIssueCount.type:
            case AggregateProperties.SeriousIssueCount.type:
            case AggregateProperties.PageCount.type:
                return sortByDirection(
                // @ts-expect-error alphaNumeric is expecting a regular property but aggregate
                // property types return a key that does not intersect with issue.
                // This should be safe because aggregate properties are handled separately in the data table
                // and have their own key mapping logic via getAggregateKey()
                sortByAlphaNumeric(getAggregateKey(sortProperty)), 
                // Aggregate counts should have a natural order of high to low
                true);
            default:
                throw new Error(`Unknown sort comparison function: ${property.toString()}`);
        }
    }, [sortProperty, sortDirection]);
    const sortedIssues = React.useMemo(() => {
        if (!sortProperty ||
            !sortDirection ||
            sortDirection === SortDirections.None) {
            return issues;
        }
        const sortComparisonFunction = getSortComparisonFunction(sortProperty);
        if (!sortComparisonFunction) {
            return issues;
        }
        return [...issues].sort(sortComparisonFunction);
    }, [issues, sortProperty, sortDirection, getSortComparisonFunction]);
    return sortedIssues;
}

const ISSUES_LIST_BREAKPOINT_PX = 768;
/**
 * Derived state for an issues list: filtering, grouping, sorting, pagination
 * and the table/list breakpoint. Layout is left entirely to the caller.
 */
function useIssuesList({ issues, properties, filter, groupBy, initialSort, paginationSize = Infinity, sortProperty: controlledSortProperty, sortDirection: controlledSortDirection, onSort }) {
    const containerRef = React.useRef(null);
    const [compact, setCompact] = React.useState(false);
    const handleResize = React.useCallback((entry) => {
        setCompact(entry.target.clientWidth <= ISSUES_LIST_BREAKPOINT_PX);
    }, []);
    useResizeObserver({
        element: containerRef,
        callback: handleResize
    });
    const filteredIssues = React.useMemo(() => {
        if (!filter || !Object.keys(filter).length) {
            return issues;
        }
        return issues.filter(issue => matchesIssueFilter(issue, filter));
    }, [issues, filter]);
    const aggregatedIssues = useAggregateIssues({
        issues: filteredIssues,
        groupBy
    });
    const rows = React.useMemo(() => {
        if (!groupBy) {
            return filteredIssues;
        }
        return aggregatedIssues.map(({ issues: groupedIssues, 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        groupBy: groupByIssues, 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        groupByValue, ...aggregateProperties }) => ({
            // Rule-level fields are identical across a group; instance-level ones
            // are not displayed on an aggregate row.
            ...groupedIssues[0],
            ...aggregateProperties
        }));
    }, [groupBy, filteredIssues, aggregatedIssues]);
    const firstSortableProperty = properties.find(property => property.sortable);
    const [internalSortProperty, setInternalSortProperty] = React.useState(
    /* c8 ignore next */
    initialSort?.type ?? firstSortableProperty?.type);
    const [internalSortDirection, setInternalSortDirection] = React.useState(SortDirections.Ascending);
    const sortProperty = controlledSortProperty ?? internalSortProperty;
    const sortDirection = controlledSortDirection ?? internalSortDirection;
    const setSort = onSort ??
        ((type, direction) => {
            setInternalSortProperty(type);
            setInternalSortDirection(direction);
        });
    /* c8 ignore start */
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test') {
        if (!sortProperty) {
            // eslint-disable-next-line no-console
            console.warn('You should specify at least one property as sortable or set "initialSort".');
        }
    }
    /* c8 ignore stop */
    const sortedIssues = useIssuesSort(rows, { sortProperty, sortDirection });
    const showPagination = rows.length > paginationSize && paginationSize > 0;
    const { pagination } = cauldronReact.usePagination({
        totalItems: rows.length,
        initialPage: 1,
        initialPageSize: showPagination ? paginationSize : rows.length
    });
    React.useEffect(() => {
        if (filteredIssues.length && showPagination) {
            pagination.onFirstPageClick();
        }
    }, [filteredIssues, showPagination]);
    const paginationStart = (pagination.currentPage - 1) * pagination.itemsPerPage;
    return {
        containerRef,
        compact,
        // The aggregate shape is guaranteed by `groupBy`, but survives neither
        // `.map()` nor `.slice()`.
        pageIssues: (showPagination
            ? sortedIssues.slice(paginationStart, paginationStart + pagination.itemsPerPage)
            : sortedIssues),
        total: rows.length,
        // 0, not 1, when the list is empty — "1 to 0 of 0" is an impossible range
        from: rows.length === 0 ? 0 : paginationStart + 1,
        to: Math.min(pagination.itemsPerPage * pagination.currentPage, rows.length),
        showPagination,
        pagination,
        sortProperty,
        sortDirection,
        toggleSort: (type, currentSortDirection) => setSort(type, type === sortProperty &&
            currentSortDirection !== SortDirections.Descending
            ? SortDirections.Descending
            : SortDirections.Ascending),
        setSort
    };
}

/**
 * Hook to isolate an element for screen readers by setting `aria-hidden="true"`
 * for any sibling or ancestor element that does not contain the target element.
 */
function useAriaIsolation(options) {
    const { element, active = true } = options;
    const affectedElements = React.useRef([]);
    const cleanup = () => {
        for (const affectedElement of affectedElements.current) {
            affectedElement.removeAttribute('aria-hidden');
            affectedElements.current = [];
        }
    };
    React.useEffect(() => {
        if (!active || typeof element === 'undefined' || element === null) {
            return;
        }
        const targetElement = element instanceof HTMLElement
            ? element
            : element.current;
        if (!(targetElement instanceof HTMLElement)) {
            // eslint-disable-next-line no-console
            console.warn('An element or ref was provided to useAriaIsolation that was not an HTMLElement.');
            return;
        }
        if (!active) {
            cleanup();
        }
        else {
            let parentNode = targetElement.parentNode;
            while (parentNode && parentNode.nodeName !== 'HTML') {
                Array.prototype.slice
                    .call(parentNode.children)
                    .forEach((child) => {
                    if (child !== targetElement && !child.contains(targetElement)) {
                        if (child.getAttribute('aria-hidden') === 'true') {
                            return;
                        }
                        affectedElements.current.push(child);
                        child.setAttribute('aria-hidden', 'true');
                    }
                });
                parentNode = parentNode.parentNode;
            }
        }
        return cleanup;
    }, [active, element]);
}

var modules_d6bb9f7a = {"issueFilter":"_issueFilter_658fb6a7","filters":"_filters_658fb6a7","activeFilter":"_activeFilter_658fb6a7","searchFieldCounter":"_searchFieldCounter_658fb6a7","emptyState":"_emptyState_658fb6a7","actions":"_actions_658fb6a7","issueFilterItem":"_issueFilterItem_658fb6a7","issueFilterItemLabel":"_issueFilterItemLabel_658fb6a7","issueFilterItemDescription":"_issueFilterItemDescription_658fb6a7"};

// Caps the overlay height to the space available between the trigger's bottom
// edge and the viewport bottom so the overlay always fits — and therefore
// stays — below its trigger rather than flipping above it. When space is tight
// the overlay shrinks and scrolls its contents internally instead of
// overflowing and covering the trigger.
const OVERLAY_OFFSET$1 = 3;
const VIEWPORT_BOTTOM_PADDING$1 = 16;
/**
 * A filter control as its own button and anchored overlay, owning the
 * apply and clear actions for the selections made inside it.
 */
function IssueFilterOverlay(props) {
    const { open: openProp, buttonLabel, overlayLabel, selectedIssuesCount, onClearAllFilters, onShowResults, onOpenChange, children } = props;
    const searchFieldProps = props.searchField
        ? props.searchFieldProps
        : undefined;
    const anchorButtonRef = React.useRef(null);
    const overlayRef = React.useRef(null);
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [maxOverlayHeight, setMaxOverlayHeight] = React.useState(null);
    const isControlled = typeof openProp === 'boolean';
    const isOpen = isControlled ? openProp : open;
    const chevronType = isOpen ? 'chevron-up' : 'chevron-down';
    React.useEffect(() => {
        if (!isOpen) {
            setMaxOverlayHeight(null);
            return;
        }
        const updateMaxHeight = () => {
            const trigger = anchorButtonRef.current;
            if (!trigger) {
                return;
            }
            const { bottom } = trigger.getBoundingClientRect();
            const available = window.innerHeight - bottom - OVERLAY_OFFSET$1 - VIEWPORT_BOTTOM_PADDING$1;
            setMaxOverlayHeight(Math.max(0, available));
        };
        updateMaxHeight();
        window.addEventListener('resize', updateMaxHeight);
        window.addEventListener('scroll', updateMaxHeight, true);
        return () => {
            window.removeEventListener('resize', updateMaxHeight);
            window.removeEventListener('scroll', updateMaxHeight, true);
        };
    }, [isOpen]);
    useAriaIsolation({
        element: overlayRef,
        active: isOpen
    });
    const handleOpenChange = React.useCallback((openState) => {
        if (typeof onOpenChange === 'function' && isControlled) {
            onOpenChange(openState);
        }
        else {
            setOpen(openState);
        }
    }, [isControlled]);
    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            handleOpenChange(true);
        }
    };
    const handleClickOutside = React.useCallback((event) => {
        if (event.target === anchorButtonRef.current ||
            anchorButtonRef.current?.contains(event.target)) {
            return;
        }
        handleOpenChange(false);
    }, []);
    if (!document || !('body' in document)) {
        return null;
    }
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsxs(cauldronReact.Button, { ref: anchorButtonRef, variant: "secondary", onClick: () => handleOpenChange(!isOpen), onKeyDown: handleKeyDown, "aria-haspopup": "dialog", "aria-expanded": isOpen, children: [buttonLabel, jsxRuntime.jsx(cauldronReact.Icon, { type: chevronType })] }), jsxRuntime.jsx(cauldronReact.ClickOutsideListener, { mouseEvent: !isOpen ? false : undefined, touchEvent: !isOpen ? false : undefined, onClickOutside: handleClickOutside, children: jsxRuntime.jsxs(cauldronReact.AnchoredOverlay, { ref: overlayRef, role: "dialog", "aria-label": overlayLabel, target: anchorButtonRef, className: modules_d6bb9f7a.issueFilter, open: isOpen, onOpenChange: handleOpenChange, placement: "bottom-start", offset: OVERLAY_OFFSET$1, focusTrap: true, focusTrapOptions: {
                        disabled: !isOpen,
                        returnFocus: true
                    }, style: {
                        display: isOpen ? undefined : 'none',
                        maxHeight: maxOverlayHeight !== null ? `${maxOverlayHeight}px` : undefined
                    }, portal: document.body, children: [searchFieldProps && (jsxRuntime.jsx("div", { className: modules_d6bb9f7a.search, children: jsxRuntime.jsx(cauldronReact.SearchField, { autoComplete: "off", ...searchFieldProps, placeholder: "" }) })), jsxRuntime.jsx("div", { className: modules_d6bb9f7a.filters, children: children }), jsxRuntime.jsxs("div", { className: modules_d6bb9f7a.actions, children: [jsxRuntime.jsx(cauldronReact.Button, { variant: "secondary", onClick: onClearAllFilters, disabled: selectedIssuesCount === null, children: t('Clear filters') }), jsxRuntime.jsx(cauldronReact.Button, { variant: "primary", onClick: onShowResults, children: selectedIssuesCount === null
                                        ? t('Show all results')
                                        : selectedIssuesCount === 1
                                            ? t('Show 1 result')
                                            : t('Show {{ resultCount, number }} results', {
                                                resultCount: selectedIssuesCount
                                            }) })] })] }) })] }));
}

/**
 * A filter control as a disclosure section inside the "All filters" drawer,
 * where the drawer owns apply and cancel. The open state stays local so
 * collapsing a section does not discard the selections staged in it.
 */
function IssueFilterSection(props) {
    const { buttonLabel, selectedValues, onShowResults, children } = props;
    const searchFieldProps = props.searchField
        ? props.searchFieldProps
        : undefined;
    const [open, setOpen] = React.useState(false);
    const stagedOnce = React.useRef(false);
    React.useEffect(() => {
        if (!stagedOnce.current) {
            stagedOnce.current = true;
            return;
        }
        onShowResults();
    }, [selectedValues]);
    return (jsxRuntime.jsxs(cauldronReact.Accordion, { open: open, onToggle: () => setOpen(!open), children: [jsxRuntime.jsx(cauldronReact.AccordionTrigger, { heading: { level: '3' }, children: buttonLabel }), jsxRuntime.jsxs(cauldronReact.AccordionContent, { children: [searchFieldProps && (jsxRuntime.jsx("div", { className: modules_d6bb9f7a.search, children: jsxRuntime.jsx(cauldronReact.SearchField, { autoComplete: "off", ...searchFieldProps }) })), jsxRuntime.jsx("div", { className: modules_d6bb9f7a.filters, children: children })] })] }));
}

/**
 * Renders a filter control in the chrome its parent asked for: its own button
 * and overlay in the filter bar, or a disclosure section in the "All filters"
 * drawer. Controls supply the inputs for both and leave the choice to whoever
 * renders them.
 */
function IssueFilter(props) {
    return props.variant === 'section' ? (jsxRuntime.jsx(IssueFilterSection, { ...props })) : (jsxRuntime.jsx(IssueFilterOverlay, { ...props }));
}

function IssueFilterItem({ children, value, description, ...props }) {
    const listboxOptionRef = React.useRef(null);
    const [active, setActive] = React.useState(false);
    const [selected, setSelected] = React.useState(false);
    const wasKeyboardNavigation = React.useRef(false);
    React.useEffect(() => {
        if (!listboxOptionRef.current) {
            return;
        }
        const handleMutation = ([mutation]) => {
            const target = mutation.target;
            const isActive = target.classList.contains(modules_d6bb9f7a.activeFilter);
            const isSelected = target.getAttribute('aria-selected') === 'true';
            setActive(isActive);
            setSelected(isSelected);
        };
        const handleListboxKeyDown = (e) => {
            const keyboardEvent = e;
            if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(keyboardEvent.key)) {
                wasKeyboardNavigation.current = true;
            }
        };
        const observer = new MutationObserver(handleMutation);
        observer.observe(listboxOptionRef.current, {
            attributes: true,
            attributeFilter: ['aria-selected', 'class']
        });
        const option = listboxOptionRef.current;
        const listbox = option.closest('[role="listbox"]');
        if (listbox) {
            listbox.addEventListener('keydown', handleListboxKeyDown);
        }
        return () => {
            observer.disconnect();
            if (listbox) {
                listbox.removeEventListener('keydown', handleListboxKeyDown);
            }
        };
    }, []);
    /**
     * We only need to scroll the active option into view when it becomes active
     * via keyboard navigation. Not doing this causes issues when the ListBox
     * becomes scrollable, it causes the focus to jump to the 0th item
     * when a item is clicked.
     */
    React.useLayoutEffect(() => {
        if (active && wasKeyboardNavigation.current && listboxOptionRef.current) {
            // Only scroll into view for keyboard navigation
            const rect = listboxOptionRef.current.getBoundingClientRect();
            /* c8 ignore next */
            listboxOptionRef.current.scrollIntoView({
                inline: 'nearest',
                /* c8 ignore next */
                block: rect.top <= 0 ? 'end' : 'nearest'
            });
        }
    }, [active]);
    return (jsxRuntime.jsxs(cauldronReact.ListboxOption, { ref: listboxOptionRef, activeClass: modules_d6bb9f7a.activeFilter, value: String(value), onMouseDown: () => {
            wasKeyboardNavigation.current = false;
        }, ...props, children: [jsxRuntime.jsx(cauldronReact.Icon, { type: selected ? 'checkbox-checked' : 'checkbox-unchecked' }), jsxRuntime.jsxs("span", { className: modules_d6bb9f7a.issueFilterItem, children: [jsxRuntime.jsx("span", { className: modules_d6bb9f7a.issueFilterItemLabel, children: children }), description && (jsxRuntime.jsx("span", { className: modules_d6bb9f7a.issueFilterItemDescription, children: description }))] })] }));
}

/** A date time format to be used with date-fns. This produces the following format: "January 1, 2020 12:00 AM" */
const DATE_TIME_FORMAT = 'MMMM d, yyyy h:mm a';

/**
 * useFilterLabels hook provides consistent labeling for filter fields across the application.
 * It handles both regular values and empty/null values with appropriate translations.
 *
 * @param filterKey - The filter field key (e.g., 'screenTitle', 'ruleId')
 * @param filterValue - The current filter value
 * @param issues - The issues the labels are looked up from
 * @param valuesWithNoIssuesFound - Values with no issues found, such as a scan of a screen with no problems
 * @returns Object containing badgeLabel, ariaLabel, and tooltipLabel
 */
function useFilterLabels(filterKey, filterValue, issues = [], valuesWithNoIssuesFound = []) {
    const { t } = useTranslation();
    const isFilterEmptyValue = filterValue === LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE ||
        isEmpty(filterValue);
    let emptyDisplayText = '';
    switch (filterKey) {
        case 'screenTitle':
            emptyDisplayText = t('No screen title');
            break;
        case 'scanId':
            emptyDisplayText = t('No scan name');
            break;
        case 'ruleId':
            emptyDisplayText = t('No rule');
            break;
        case 'impact':
            emptyDisplayText = t('No impact');
            break;
        case 'testPageTitle':
            emptyDisplayText = t('No page title');
            break;
        case 'testUrl':
            emptyDisplayText = t('No page URL');
            break;
        case 'testFilePath':
            emptyDisplayText = t('No test name');
            break;
        default:
            emptyDisplayText = t('No {{ fieldName }}', { fieldName: filterKey });
    }
    const displayFilterValue = isFilterEmptyValue
        ? emptyDisplayText
        : String(filterValue);
    switch (filterKey) {
        case 'screenTitle':
            return {
                badgeLabel: t('Screen title:'),
                emptyFilterLabel: emptyDisplayText,
                ariaLabel: isFilterEmptyValue
                    ? t('Remove empty screen title filter')
                    : t('Remove screen title: {{ title }} filter', {
                        title: displayFilterValue
                    }),
                tooltipLabel: isFilterEmptyValue
                    ? t('Empty screen title filter')
                    : t('Screen title: {{ title }}', { title: displayFilterValue })
            };
        case 'testFilePath': {
            const testName = getTestNameFromFilePath(displayFilterValue);
            return {
                badgeLabel: t('Test name:'),
                customDisplayLabel: testName,
                emptyFilterLabel: emptyDisplayText,
                ariaLabel: isFilterEmptyValue
                    ? t('Remove empty test name filter')
                    : t('Remove test name: {{ name }} filter', {
                        name: displayFilterValue
                    }),
                tooltipLabel: isFilterEmptyValue
                    ? t('Empty test name filter')
                    : t('Test name: {{ name }}', { name: displayFilterValue })
            };
        }
        case 'ruleId': {
            // Filtered by rule id, but the table, the picker and the drill-down all
            // name a rule by its help text, so the chip looks it up the same way the
            // scan name does.
            const ruleName = issues.find(({ ruleId }) => ruleId === filterValue)?.help ??
                displayFilterValue;
            return {
                badgeLabel: t('Rule:'),
                customDisplayLabel: ruleName,
                emptyFilterLabel: emptyDisplayText,
                ariaLabel: isFilterEmptyValue
                    ? t('Remove empty rule filter')
                    : t('Remove rule: {{ rule }} filter', { rule: ruleName }),
                tooltipLabel: isFilterEmptyValue
                    ? t('Empty rule filter')
                    : t('Rule: {{ rule }}', { rule: ruleName })
            };
        }
        case 'impact': {
            // Impact is filtered by its lowercase severity, which is not how it is
            // written anywhere the user can see it.
            const impactNames = {
                critical: t('Critical'),
                serious: t('Serious'),
                moderate: t('Moderate'),
                minor: t('Minor')
            };
            const impactName = impactNames[displayFilterValue] ?? displayFilterValue;
            return {
                badgeLabel: t('Impact:'),
                customDisplayLabel: impactName,
                emptyFilterLabel: emptyDisplayText,
                ariaLabel: isFilterEmptyValue
                    ? t('Remove empty impact filter')
                    : t('Remove impact: {{ impact }} filter', { impact: impactName }),
                tooltipLabel: isFilterEmptyValue
                    ? t('Empty impact filter')
                    : t('Impact: {{ impact }}', { impact: impactName })
            };
        }
        case 'scanId': {
            /**
             * When applying filters, we are filtering by the scanId, but we want to display the scanName
             * to the user. So we need to look up the scanName based on the scanId (filterValue).
             */
            const scan = issues.find(({ scanId }) => scanId === filterValue) ??
                valuesWithNoIssuesFound.find(({ scanId }) => scanId === filterValue);
            const scanName = scan?.scanName || t('No scan name');
            const date = scan?.createdAt
                ? dateFns.format(scan.createdAt, DATE_TIME_FORMAT)
                : null;
            let customDisplayLabel;
            if (!scan) {
                customDisplayLabel = scanName;
            }
            else {
                customDisplayLabel = `${scanName}${date ? `, ${date}` : ''}`;
            }
            return {
                badgeLabel: t('Scan name:'),
                customDisplayLabel: customDisplayLabel,
                emptyFilterLabel: emptyDisplayText,
                ariaLabel: isFilterEmptyValue
                    ? t('Remove empty scan name filter')
                    : t('Remove scan name: {{ name }} filter', {
                        name: customDisplayLabel
                    }),
                tooltipLabel: isFilterEmptyValue
                    ? t('Empty scan name filter')
                    : t('Scan name: {{ name }}', { name: customDisplayLabel })
            };
        }
        case 'testUrl': {
            // Filtered by URL, but the picker lists each page by its title, so the
            // chip has to look the title up the same way the scan name does. Titles
            // repeat across pages of a site, so the URL stays on the chip to tell
            // two same-titled pages apart.
            const pageTitle = issues.find(({ testUrl }) => isFilterEmptyValue ? isEmpty(testUrl) : testUrl === filterValue)?.testPageTitle;
            // A page with no URL has nothing to append, so its title alone names it.
            const urlSuffix = isFilterEmptyValue ? '' : `, ${displayFilterValue}`;
            const customDisplayLabel = pageTitle
                ? `${pageTitle}${urlSuffix}`
                : displayFilterValue;
            return {
                badgeLabel: t('Page title and URL:'),
                customDisplayLabel,
                emptyFilterLabel: emptyDisplayText,
                ariaLabel: isFilterEmptyValue
                    ? t('Remove empty page title and URL filter')
                    : t('Remove page title and URL: {{ page }} filter', {
                        page: customDisplayLabel
                    }),
                tooltipLabel: isFilterEmptyValue
                    ? t('Empty page title and URL filter')
                    : t('Page title and URL: {{ page }}', { page: customDisplayLabel })
            };
        }
        default:
            return {
                badgeLabel: filterKey,
                emptyFilterLabel: emptyDisplayText,
                ariaLabel: isFilterEmptyValue
                    ? t('Remove empty {{ filterKey }} filter', { filterKey })
                    : t('Remove {{ filterKey }}: {{ filterValue }} filter', {
                        filterKey,
                        filterValue: displayFilterValue
                    }),
                tooltipLabel: isFilterEmptyValue
                    ? t('Empty {{ filterKey }} filter', { filterKey })
                    : `${filterKey}: ${displayFilterValue}`
            };
    }
}

/**
 * useFilterItems will perform an aggregate count for each unique value
 * for an individual property within a collection of issues and return a
 * collection of objects to provide filterable display values for that property.
 */
function useFilterItems({ property, issues, filter, label, description, valuesWithNoIssuesFound = [] }) {
    const [selectedValues, setSelectedValues] = React.useState([]);
    const { emptyFilterLabel } = useFilterLabels(property, ''); // only need the empty label for a property
    const issuesMatchingOtherFilters = React.useMemo(() => {
        const otherFilters = { ...filter };
        delete otherFilters[property];
        const hasOtherFilters = Object.values(otherFilters).some(values => values?.length);
        return hasOtherFilters
            ? issues.filter(issue => matchesIssueFilter(issue, otherFilters))
            : issues;
    }, [issues, filter, property]);
    const filterItems = React.useMemo(() => {
        const keyOf = (issue) => {
            const issuePropertyValue = issue[property];
            return isEmpty(issuePropertyValue)
                ? LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE
                : issuePropertyValue;
        };
        const counts = new Map();
        for (const issue of issuesMatchingOtherFilters) {
            const issuePropertyKey = keyOf(issue);
            counts.set(issuePropertyKey, (counts.get(issuePropertyKey) ?? 0) + 1);
        }
        const issuePropertyCountMap = new Map();
        for (const issue of issues) {
            const issuePropertyKey = keyOf(issue);
            if (issuePropertyCountMap.has(issuePropertyKey)) {
                continue;
            }
            const issuePropertyValue = issue[property];
            const displayLabel = typeof label === 'function'
                ? label(issue)
                : isEmpty(issuePropertyValue)
                    ? emptyFilterLabel
                    : issuePropertyValue;
            issuePropertyCountMap.set(issuePropertyKey, {
                count: counts.get(issuePropertyKey) ?? 0,
                label: displayLabel,
                value: issuePropertyKey,
                description: typeof description === 'function' ? description(issue) : undefined
            });
        }
        for (const option of valuesWithNoIssuesFound) {
            const optionValue = option[property];
            if (!optionValue || issuePropertyCountMap.has(optionValue)) {
                continue;
            }
            issuePropertyCountMap.set(optionValue, {
                count: 0,
                label: label?.(option) ?? String(optionValue),
                value: optionValue,
                description: description?.(option)
            });
        }
        return issuePropertyCountMap;
    }, [
        issues,
        issuesMatchingOtherFilters,
        property,
        label,
        description,
        emptyFilterLabel,
        valuesWithNoIssuesFound
    ]);
    const onSelectionChange = React.useCallback(({ value }) => {
        setSelectedValues(value);
    }, []);
    const clearSelections = React.useCallback(() => {
        setSelectedValues([]);
    }, []);
    const totalSelectionCount = React.useMemo(() => {
        return selectedValues.reduce((count, selectedValue) => {
            // if for some reason the filter items is not included, default to 0
            return count + (filterItems.get(selectedValue)?.count ?? 0);
        }, 0);
    }, [selectedValues, filterItems]);
    return {
        filterItems: Array.from(filterItems.values()),
        selectedFilterValues: selectedValues,
        selectedIssuesCount: selectedValues.length === 0 ? null : totalSelectionCount,
        onSelectionChange,
        clearSelections
    };
}

// Impact levels are displayed in order of severity with highest first
const ImpactLevels = [
    'critical',
    'serious',
    'moderate',
    'minor'
];
function ImpactIssueFilter({ issues, filter, onFilterChange, variant }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const initialFilterValues = React.useRef([]);
    const lastSyncedFilterValues = React.useRef(null);
    const [buttonLabel, setButtonLabel] = React.useState(t('Impact'));
    const issuesWithImpact = React.useMemo(() => issues.filter(issue => !!issue.impact), [issues]);
    const { 
    /* c8 ignore next */
    filterItems = [], selectedFilterValues, selectedIssuesCount, onSelectionChange, clearSelections } = useFilterItems({
        property: 'impact',
        issues: issuesWithImpact,
        filter
    });
    const listboxId = `filter-listbox-${utils.useId()}`;
    const isControlled = typeof filter !== 'undefined';
    const impactTranslations = React.useMemo(() => ({
        critical: t('Critical'),
        serious: t('Serious'),
        moderate: t('Moderate'),
        minor: t('Minor')
    }), [t]);
    const impactCount = React.useMemo(() => ({
        critical: filterItems.find(filterItem => filterItem.value === 'critical')
            ?.count ?? 0,
        serious: filterItems.find(filterItem => filterItem.value === 'serious')?.count ??
            0,
        moderate: filterItems.find(filterItem => filterItem.value === 'moderate')
            ?.count ?? 0,
        minor: filterItems.find(filterItem => filterItem.value === 'minor')?.count ?? 0
    }), [filterItems]);
    const handleOpenChange = React.useCallback((isOpen) => {
        if (!isOpen) {
            // Closing discards selections the user never committed
            onSelectionChange({ value: initialFilterValues.current });
        }
        setOpen(isOpen);
    }, [onSelectionChange]);
    const handleShowResults = React.useCallback(() => {
        if (typeof onFilterChange === 'function') {
            initialFilterValues.current = selectedFilterValues;
            onFilterChange(selectedFilterValues.length
                ? {
                    impact: selectedFilterValues
                }
                : null);
        }
        setButtonLabel(selectedFilterValues.length
            ? t(`Impact ({{ impactCount }})`, {
                impactCount: selectedFilterValues.length
            })
            : t('Impact'));
        setOpen(false);
    }, [onFilterChange, selectedFilterValues]);
    React.useEffect(() => {
        if (open || !isControlled) {
            return;
        }
        const applied = (filter?.impact ?? []);
        const previouslySynced = lastSyncedFilterValues.current;
        const alreadySynced = previouslySynced !== null &&
            applied.length === previouslySynced.length &&
            applied.every((value, index) => value === previouslySynced[index]);
        if (alreadySynced) {
            return;
        }
        lastSyncedFilterValues.current = applied;
        initialFilterValues.current = applied;
        onSelectionChange({ value: applied });
        setButtonLabel(applied.length
            ? t(`Impact ({{ impactCount }})`, { impactCount: applied.length })
            : t('Impact'));
    }, [open, isControlled, filter, onSelectionChange, t]);
    return (jsxRuntime.jsx(IssueFilter, { variant: variant, open: open, buttonLabel: buttonLabel, overlayLabel: t('Impact Filters'), selectedIssuesCount: selectedIssuesCount, selectedValues: selectedFilterValues, onClearAllFilters: clearSelections, onShowResults: handleShowResults, onOpenChange: handleOpenChange, children: jsxRuntime.jsx(cauldronReact.Listbox, { id: listboxId, "aria-label": t('Impacts'), onSelectionChange: onSelectionChange, value: selectedFilterValues, multiselect: true, children: ImpactLevels.map(impact => (jsxRuntime.jsxs(IssueFilterItem, { value: impact, children: [impactTranslations[impact], " (", impactCount[impact], ")"] }, impact))) }) }));
}

function PageUrlFilter({ issues, filter, onFilterChange, variant }) {
    const { t } = useTranslation();
    const listBoxId = `filter-page-url-listbox-${utils.useId()}`;
    const isControlled = typeof filter !== 'undefined';
    const [searchValue, setSearchValue] = React__default.default.useState('');
    const [open, setOpen] = React__default.default.useState(false);
    const [buttonLabel, setButtonLabel] = React__default.default.useState(t('Page title and URL'));
    const initialFilterValues = React__default.default.useRef([]);
    const lastSyncedFilterValues = React__default.default.useRef(null);
    const { filterItems, selectedFilterValues, selectedIssuesCount, onSelectionChange, clearSelections } = useFilterItems({
        // Keyed on the URL alone: it identifies the page on its own, whereas
        // keying on both testUrl and testPageTitle would filter on their
        // cross-product instead of the pairs the user actually picked.
        property: 'testUrl',
        issues,
        filter,
        label: ({ testPageTitle, testUrl }) => testPageTitle || testUrl || t('No page URL'),
        // An untitled page already shows its URL as the label, so repeating it as
        // the description would print the same URL twice.
        description: ({ testPageTitle, testUrl }) => testPageTitle ? testUrl : undefined
    });
    React__default.default.useEffect(() => {
        if (!open) {
            setSearchValue('');
        }
    }, [open]);
    const pageUrlFilterItems = React__default.default.useMemo(() => {
        return filterItems.sort((a, b) => a.label.localeCompare(b.label));
    }, [filterItems]);
    const existingItemsFiltered = React__default.default.useCallback(({ label, description }) => {
        const query = searchValue.toLowerCase();
        // The field offers to search the URL as well as the title, and two pages
        // of one site often share a title, so the URL is the only way to tell
        // them apart.
        return (label.toLowerCase().includes(query) ||
            `${description ?? ''}`.toLowerCase().includes(query));
    }, [searchValue]);
    React__default.default.useEffect(() => {
        if (open || !isControlled) {
            return;
        }
        const applied = filter?.testUrl ?? [];
        const previouslySynced = lastSyncedFilterValues.current;
        const alreadySynced = previouslySynced !== null &&
            applied.length === previouslySynced.length &&
            applied.every((value, index) => value === previouslySynced[index]);
        if (alreadySynced) {
            return;
        }
        lastSyncedFilterValues.current = applied;
        initialFilterValues.current = applied;
        onSelectionChange({ value: applied });
        setButtonLabel(applied.length
            ? t(`Page title and URL ({{ count }})`, { count: applied.length })
            : t('Page title and URL'));
    }, [open, isControlled, filter, onSelectionChange, t]);
    const handleOnOpenChange = React__default.default.useCallback((isOpen) => {
        if (!isOpen) {
            // Closing discards selections the user never committed
            onSelectionChange({ value: initialFilterValues.current });
        }
        setOpen(isOpen);
    }, [onSelectionChange]);
    const handleOnShowResultsChange = React__default.default.useCallback(() => {
        // A page with no URL is keyed to the empty filter value, so it has to
        // survive alongside the string values or that option cannot be applied.
        const selectedValues = selectedFilterValues.filter(v => typeof v === 'string' || v === LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE);
        const count = selectedValues.length;
        initialFilterValues.current = selectedValues;
        onFilterChange(count ? { testUrl: selectedValues } : null);
        setButtonLabel(count
            ? t('Page title and URL ({{ count }})', { count })
            : t('Page title and URL'));
        setOpen(false);
    }, [onFilterChange, selectedFilterValues]);
    const filterItemToMap = React__default.default.useCallback(({ value, label, description, count }) => value && (jsxRuntime.jsxs(IssueFilterItem, { value: value, description: description, onClick: () => {
            const isSelected = selectedFilterValues.includes(value);
            const newSelections = isSelected
                ? selectedFilterValues.filter(v => v !== value)
                : [...selectedFilterValues, value];
            onSelectionChange({
                value: newSelections
            });
        }, children: [label, " (", count, ")"] }, value.toString())), [selectedFilterValues]);
    const filterItemsMatched = searchValue.length
        ? pageUrlFilterItems.filter(existingItemsFiltered).map(filterItemToMap)
        : pageUrlFilterItems.map(filterItemToMap);
    const showEmptyState = Boolean(searchValue.length && !filterItemsMatched.length);
    return (jsxRuntime.jsx(IssueFilter, { variant: variant, open: open, overlayLabel: t('Page title and URL Filter'), selectedIssuesCount: selectedIssuesCount, selectedValues: selectedFilterValues, onClearAllFilters: clearSelections, onOpenChange: handleOnOpenChange, onShowResults: handleOnShowResultsChange, buttonLabel: buttonLabel, searchField: true, searchFieldProps: {
            // The listbox is swapped out for the empty state, so only point at it
            // while it exists — a dangling `aria-controls` is worse than none.
            'aria-controls': showEmptyState ? undefined : listBoxId,
            'aria-invalid': showEmptyState ? true : undefined,
            label: t('Search by page title and URL'),
            value: searchValue,
            onChange: setSearchValue,
            trailingChildren: searchValue.length ? (jsxRuntime.jsx("span", { className: modules_d6bb9f7a.searchFieldCounter, children: t(`{{ count }} of {{ total }}`, {
                    count: filterItemsMatched.length,
                    total: pageUrlFilterItems.length
                }) })) : null
        }, children: showEmptyState ? (jsxRuntime.jsx("div", { className: modules_d6bb9f7a.emptyState, "aria-live": "polite", children: t('No results found for "{{ searchValue }}". Try another search.', {
                searchValue
            }) })) : (jsxRuntime.jsx(cauldronReact.Listbox, { id: listBoxId, "aria-label": t('Pages'), value: selectedFilterValues.map(String), multiselect: true, children: filterItemsMatched })) }));
}

function RuleIssueFilter({ issues, filter, onFilterChange, variant }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const initialFilterValues = React.useRef([]);
    const lastSyncedFilterValues = React.useRef(null);
    const [buttonLabel, setButtonLabel] = React.useState(t('Rule'));
    const { filterItems, selectedFilterValues, selectedIssuesCount, onSelectionChange, clearSelections } = useFilterItems({
        property: 'ruleId',
        issues,
        filter,
        label: ({ ruleId = '', ruleName, help }) => ruleName || help || ruleId
    });
    const listboxId = `filter-listbox-${utils.useId()}`;
    const isControlled = typeof filter !== 'undefined';
    const ruleFilterItems = React.useMemo(() => {
        // order alphanumerically by the label of the rule
        return filterItems.sort((a, b) => a.label.localeCompare(b.label));
    }, [filterItems]);
    const matchingItemsFilter = React.useCallback(({ label }) => {
        return label.toLowerCase().includes(searchValue.toLowerCase());
    }, [ruleFilterItems, searchValue]);
    const handleOpenChange = React.useCallback((isOpen) => {
        if (!isOpen) {
            // Closing discards selections the user never committed
            onSelectionChange({ value: initialFilterValues.current });
        }
        setOpen(isOpen);
    }, [onSelectionChange]);
    const handleShowResults = React.useCallback(() => {
        if (typeof onFilterChange === 'function') {
            initialFilterValues.current = selectedFilterValues.filter((value) => typeof value === 'string');
            onFilterChange(selectedFilterValues.length
                ? {
                    ruleId: selectedFilterValues
                }
                : null);
        }
        setButtonLabel(selectedFilterValues.length
            ? t(`Rule ({{ ruleCount }})`, {
                ruleCount: selectedFilterValues.length
            })
            : t('Rule'));
        setOpen(false);
    }, [onFilterChange, selectedFilterValues]);
    const renderFilterItem = React.useCallback(({ value, label, count }) => (jsxRuntime.jsxs(IssueFilterItem, { value: value, children: [label, " (", count, ")"] }, String(value))), []);
    React.useEffect(() => {
        if (!open) {
            setSearchValue('');
        }
    }, [open]);
    React.useEffect(() => {
        if (open || !isControlled) {
            return;
        }
        const applied = (filter?.ruleId ?? []);
        const previouslySynced = lastSyncedFilterValues.current;
        const alreadySynced = previouslySynced !== null &&
            applied.length === previouslySynced.length &&
            applied.every((value, index) => value === previouslySynced[index]);
        if (alreadySynced) {
            return;
        }
        lastSyncedFilterValues.current = applied;
        initialFilterValues.current = applied;
        onSelectionChange({ value: applied });
        setButtonLabel(applied.length
            ? t(`Rule ({{ ruleCount }})`, { ruleCount: applied.length })
            : t('Rule'));
    }, [open, isControlled, filter, onSelectionChange, t]);
    const matchingRuleFilterItems = searchValue.length
        ? ruleFilterItems.filter(matchingItemsFilter).map(renderFilterItem)
        : ruleFilterItems.map(renderFilterItem);
    return (jsxRuntime.jsx(IssueFilter, { variant: variant, open: open, buttonLabel: buttonLabel, overlayLabel: t('Rule Filters'), selectedIssuesCount: selectedIssuesCount, selectedValues: selectedFilterValues, onClearAllFilters: clearSelections, onShowResults: handleShowResults, onOpenChange: handleOpenChange, searchField: true, searchFieldProps: {
            'aria-controls': listboxId,
            'aria-invalid': searchValue.length && !matchingRuleFilterItems.length
                ? true
                : undefined,
            label: t('Search by rule'),
            value: searchValue,
            onChange: setSearchValue,
            trailingChildren: searchValue.length ? (jsxRuntime.jsx("span", { className: modules_d6bb9f7a.searchFieldCounter, children: t(`{{ count }} of {{ total }}`, {
                    count: matchingRuleFilterItems.length,
                    total: ruleFilterItems.length
                }) })) : null
        }, children: !matchingRuleFilterItems.length ? (jsxRuntime.jsx("div", { className: modules_d6bb9f7a.emptyState, "aria-live": "polite", children: t('No results found for "{{ searchValue }}". Try another search.', {
                searchValue
            }) })) : (jsxRuntime.jsx(cauldronReact.Listbox, { id: listboxId, "aria-label": t('Rules'), onSelectionChange: onSelectionChange, value: selectedFilterValues.map(String), multiselect: true, children: matchingRuleFilterItems })) }));
}

function ScanNameFilter({ issues, filter, valuesWithNoIssuesFound, onFilterChange, variant }) {
    const { t } = useTranslation();
    const listBoxId = `filter-scan-name-listbox-${utils.useId()}`;
    const isControlled = typeof filter !== 'undefined';
    const [searchValue, setSearchValue] = React__default.default.useState('');
    const [open, setOpen] = React__default.default.useState(false);
    const [buttonLabel, setButtonLabel] = React__default.default.useState(t('Scan name'));
    const initialFilterValues = React__default.default.useRef([]);
    const lastSyncedFilterValues = React__default.default.useRef(null);
    const { filterItems, selectedFilterValues, selectedIssuesCount, onSelectionChange, clearSelections } = useFilterItems({
        /**
         * It's important to call out that we're using 'scanId' as the property
         * to filter on, but we're displaying the 'scanName' as the label for the
         * filter item. This is because scan names are not guaranteed to be unique,
         * whereas scan IDs are unique identifiers for each scan.
         *
         * So, if two scans have the same name, they will still be treated as
         * separate filter items because they have different IDs. This ensures that
         * users can accurately filter issues by the specific scan they are interested
         * in, even if multiple scans share the same name.
         */
        property: 'scanId',
        issues,
        filter,
        valuesWithNoIssuesFound,
        label: ({ scanName }) => scanName || t('No scan name'),
        description: ({ createdAt }) => {
            if (!createdAt) {
                return;
            }
            return dateFns.format(createdAt, DATE_TIME_FORMAT);
        }
    });
    React__default.default.useEffect(() => {
        if (!open) {
            setSearchValue('');
        }
    }, [open]);
    const scanNameFilterItems = React__default.default.useMemo(() => {
        return filterItems.sort((a, b) => a.label.localeCompare(b.label));
    }, [filterItems]);
    const existingItemsFiltered = React__default.default.useCallback(({ label }) => {
        return label.toLowerCase().includes(searchValue.toLowerCase());
    }, [searchValue]);
    React__default.default.useEffect(() => {
        if (open || !isControlled) {
            return;
        }
        const applied = filter?.scanId ?? [];
        const previouslySynced = lastSyncedFilterValues.current;
        const alreadySynced = previouslySynced !== null &&
            applied.length === previouslySynced.length &&
            applied.every((value, index) => value === previouslySynced[index]);
        if (alreadySynced) {
            return;
        }
        lastSyncedFilterValues.current = applied;
        initialFilterValues.current = applied;
        onSelectionChange({ value: applied });
        setButtonLabel(applied.length
            ? t(`Scan name ({{ count }})`, { count: applied.length })
            : t('Scan name'));
    }, [open, isControlled, filter, onSelectionChange, t]);
    const handleOnOpenChange = React__default.default.useCallback((isOpen) => {
        if (!isOpen) {
            // Closing discards selections the user never committed
            onSelectionChange({ value: initialFilterValues.current });
        }
        setOpen(isOpen);
    }, [onSelectionChange]);
    const handleOnShowResultsChange = React__default.default.useCallback(() => {
        // A scan with no id is keyed to the empty filter value, so it has to
        // survive alongside the string values or that option cannot be applied.
        const selectedValues = selectedFilterValues.filter(v => typeof v === 'string' || v === LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE);
        const count = selectedValues.length;
        initialFilterValues.current = selectedValues;
        onFilterChange(count ? { scanId: selectedValues } : null);
        setButtonLabel(count ? t('Scan name ({{ count }})', { count }) : t('Scan name'));
        setOpen(false);
    }, [onFilterChange, selectedFilterValues]);
    const filterItemToMap = React__default.default.useCallback(({ value, label, description, count }) => value && (jsxRuntime.jsxs(IssueFilterItem, { value: value, description: description, onClick: () => {
            const isSelected = selectedFilterValues.includes(value);
            const newSelections = isSelected
                ? selectedFilterValues.filter(v => v !== value)
                : [...selectedFilterValues, value];
            onSelectionChange({
                value: newSelections
            });
        }, children: [label, " (", count, ")"] }, value.toString())), [selectedFilterValues]);
    const filterItemsMatched = searchValue.length
        ? scanNameFilterItems.filter(existingItemsFiltered).map(filterItemToMap)
        : scanNameFilterItems.map(filterItemToMap);
    return (jsxRuntime.jsx(IssueFilter, { variant: variant, open: open, overlayLabel: t('Scan Name Filters'), selectedIssuesCount: selectedIssuesCount, selectedValues: selectedFilterValues, onClearAllFilters: clearSelections, onOpenChange: handleOnOpenChange, onShowResults: handleOnShowResultsChange, buttonLabel: buttonLabel, searchField: true, searchFieldProps: {
            'aria-controls': listBoxId,
            'aria-invalid': searchValue.length && !filterItemsMatched.length ? true : undefined,
            label: t('Search by scan name'),
            value: searchValue,
            onChange: setSearchValue,
            trailingChildren: searchValue.length ? (jsxRuntime.jsx("span", { className: modules_d6bb9f7a.searchFieldCounter, children: t(`{{ count }} of {{ total }}`, {
                    count: filterItemsMatched.length,
                    total: scanNameFilterItems.length
                }) })) : null
        }, children: !filterItemsMatched.length ? (jsxRuntime.jsx("div", { className: modules_d6bb9f7a.emptyState, "aria-live": "polite", children: t('No results found for "{{ searchValue }}". Try another search.', {
                searchValue
            }) })) : (jsxRuntime.jsx(cauldronReact.Listbox, { id: listBoxId, "aria-label": t('Scan Names'), value: selectedFilterValues.map(String), multiselect: true, children: filterItemsMatched })) }));
}

function ScreenTitleIssueFilter({ issues, filter, onFilterChange, variant }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const initialFilterValues = React.useRef([]);
    const lastSyncedFilterValues = React.useRef(null);
    const [buttonLabel, setButtonLabel] = React.useState(t('Screen title'));
    const { filterItems, selectedFilterValues, selectedIssuesCount, onSelectionChange, clearSelections } = useFilterItems({
        property: 'screenTitle',
        issues,
        filter
    });
    const listboxId = `filter-listbox-${utils.useId()}`;
    const isControlled = typeof filter !== 'undefined';
    const screenTitleFilterItems = React.useMemo(() => {
        return filterItems.sort((a, b) => a.label.localeCompare(b.label));
    }, [filterItems]);
    const matchingItemsFilter = React.useCallback(({ label }) => {
        return label.toLowerCase().includes(searchValue.toLowerCase());
    }, [searchValue]);
    const handleOpenChange = React.useCallback((isOpen) => {
        if (!isOpen) {
            // Closing discards selections the user never committed
            onSelectionChange({ value: initialFilterValues.current });
        }
        setOpen(isOpen);
    }, [onSelectionChange]);
    const handleShowResults = React.useCallback(() => {
        if (typeof onFilterChange === 'function') {
            initialFilterValues.current = selectedFilterValues.filter((value) => value !== undefined);
            onFilterChange(selectedFilterValues.length
                ? {
                    screenTitle: selectedFilterValues
                }
                : null);
            setButtonLabel(selectedFilterValues.length
                ? t(`Screen title ({{ count }})`, {
                    count: selectedFilterValues.length
                })
                : t('Screen title'));
        }
        setOpen(false);
    }, [onFilterChange, selectedFilterValues]);
    const renderFilterItem = React.useCallback(({ value, label, count }) => value && (jsxRuntime.jsxs(IssueFilterItem, { value: value, onClick: () => {
            const newSelections = selectedFilterValues.includes(value)
                ? selectedFilterValues.filter(selectedValue => selectedValue !== value)
                : [...selectedFilterValues, value];
            onSelectionChange({
                value: newSelections
            });
        }, children: [label, " (", count, ")"] }, value.toString())), [selectedFilterValues]);
    React.useEffect(() => {
        if (open || !isControlled) {
            return;
        }
        const applied = (filter?.screenTitle ?? []);
        const previouslySynced = lastSyncedFilterValues.current;
        const alreadySynced = previouslySynced !== null &&
            applied.length === previouslySynced.length &&
            applied.every((value, index) => value === previouslySynced[index]);
        if (alreadySynced) {
            return;
        }
        lastSyncedFilterValues.current = applied;
        initialFilterValues.current = applied;
        onSelectionChange({ value: applied });
        setButtonLabel(applied.length
            ? t(`Screen title ({{ count }})`, { count: applied.length })
            : t('Screen title'));
    }, [open, isControlled, filter, onSelectionChange, t]);
    React.useEffect(() => {
        if (!open) {
            setSearchValue('');
        }
    }, [open]);
    const matchingScreenTitleFilterItems = searchValue.length
        ? screenTitleFilterItems.filter(matchingItemsFilter).map(renderFilterItem)
        : screenTitleFilterItems.map(renderFilterItem);
    return (jsxRuntime.jsx(IssueFilter, { variant: variant, open: open, buttonLabel: buttonLabel, overlayLabel: t('Screen title Filters'), selectedIssuesCount: selectedIssuesCount, selectedValues: selectedFilterValues, onClearAllFilters: clearSelections, onShowResults: handleShowResults, onOpenChange: handleOpenChange, searchField: true, searchFieldProps: {
            'aria-controls': listboxId,
            'aria-invalid': searchValue.length && !matchingScreenTitleFilterItems.length
                ? true
                : undefined,
            label: t('Search by screen title'),
            value: searchValue,
            onChange: setSearchValue,
            trailingChildren: searchValue.length ? (jsxRuntime.jsx("span", { className: modules_d6bb9f7a.searchFieldCounter, children: t(`{{ count }} of {{ total }}`, {
                    count: matchingScreenTitleFilterItems.length,
                    total: screenTitleFilterItems.length
                }) })) : null
        }, children: !matchingScreenTitleFilterItems.length ? (jsxRuntime.jsx("div", { className: modules_d6bb9f7a.emptyState, "aria-live": "polite", children: t('No results found for "{{ searchValue }}". Try another search.', {
                searchValue
            }) })) : (jsxRuntime.jsx(cauldronReact.Listbox, { id: listboxId, "aria-label": t('Screen Titles'), value: selectedFilterValues.map(String), multiselect: true, children: matchingScreenTitleFilterItems })) }));
}

function TestNameFilter({ issues, filter, onFilterChange, variant }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const initialFilterValues = React.useRef([]);
    const lastSyncedFilterValues = React.useRef(null);
    const [buttonLabel, setButtonLabel] = React.useState(t('Test name'));
    const { filterItems, selectedFilterValues, selectedIssuesCount, onSelectionChange, clearSelections } = useFilterItems({
        property: 'testFilePath',
        issues,
        filter,
        label: ({ testFilePath }) => getTestNameFromFilePath(testFilePath) || t('No test name'),
        description: ({ testFilePath }) => {
            const testName = getTestNameFromFilePath(testFilePath);
            const path = testFilePath?.trim();
            return path && path !== testName ? path : undefined;
        }
    });
    const listboxId = `filter-test-name-listbox-${utils.useId()}`;
    const isControlled = typeof filter !== 'undefined';
    const testNameFilterItems = React.useMemo(() => {
        return filterItems.sort((a, b) => a.label.localeCompare(b.label) ||
            String(a.value).localeCompare(String(b.value)));
    }, [filterItems]);
    const matchingItemsFilter = React.useCallback(({ label, description }) => {
        const search = searchValue.toLowerCase();
        return (label.toLowerCase().includes(search) ||
            Boolean(description?.toLowerCase().includes(search)));
    }, [searchValue]);
    const handleOpenChange = React.useCallback((isOpen) => {
        if (!isOpen) {
            onSelectionChange({ value: initialFilterValues.current });
        }
        setOpen(isOpen);
    }, [onSelectionChange]);
    const handleShowResults = React.useCallback(() => {
        const selectedValues = selectedFilterValues.filter((value) => value !== null && value !== undefined);
        const count = selectedValues.length;
        initialFilterValues.current = selectedValues;
        if (typeof onFilterChange === 'function') {
            onFilterChange(count ? { testFilePath: selectedValues } : null);
        }
        setButtonLabel(count ? t('Test name ({{ count }})', { count }) : t('Test name'));
        setOpen(false);
    }, [onFilterChange, selectedFilterValues]);
    const renderFilterItem = React.useCallback(({ value, label, description, count }) => value && (jsxRuntime.jsxs(IssueFilterItem, { value: value, description: description, onClick: () => {
            const newSelections = selectedFilterValues.includes(value)
                ? selectedFilterValues.filter(selectedValue => selectedValue !== value)
                : [...selectedFilterValues, value];
            onSelectionChange({
                value: newSelections
            });
        }, children: [label, " (", count, ")"] }, value.toString())), [selectedFilterValues]);
    React.useEffect(() => {
        if (open || !isControlled) {
            return;
        }
        const applied = (filter?.testFilePath ?? []);
        const previouslySynced = lastSyncedFilterValues.current;
        const alreadySynced = previouslySynced !== null &&
            applied.length === previouslySynced.length &&
            applied.every((value, index) => value === previouslySynced[index]);
        if (alreadySynced) {
            return;
        }
        lastSyncedFilterValues.current = applied;
        initialFilterValues.current = applied;
        onSelectionChange({ value: applied });
        setButtonLabel(applied.length
            ? t('Test name ({{ count }})', { count: applied.length })
            : t('Test name'));
    }, [open, isControlled, filter, onSelectionChange, t]);
    React.useEffect(() => {
        if (!open) {
            setSearchValue('');
        }
    }, [open]);
    const isSearching = Boolean(searchValue.length);
    const matchingTestNameFilterItems = isSearching
        ? testNameFilterItems.filter(matchingItemsFilter)
        : testNameFilterItems;
    const nodesToRender = matchingTestNameFilterItems.map(renderFilterItem);
    const isEmpty = !nodesToRender.length;
    // An empty overlay with no search term is a data state, not a failed search,
    // so it must not tell the user to change a search they never typed.
    const emptyStateMessage = isSearching
        ? t('No results found for "{{ searchValue }}". Try another search.', {
            searchValue
        })
        : t('No test names to filter by.');
    return (jsxRuntime.jsx(IssueFilter, { variant: variant, open: open, buttonLabel: buttonLabel, overlayLabel: t('Test name Filters'), selectedIssuesCount: selectedIssuesCount, selectedValues: selectedFilterValues, onClearAllFilters: clearSelections, onShowResults: handleShowResults, onOpenChange: handleOpenChange, searchField: true, searchFieldProps: {
            // The listbox is swapped out for the empty state, so only point at it
            // while it exists — a dangling `aria-controls` is worse than none.
            'aria-controls': isEmpty ? undefined : listboxId,
            'aria-invalid': isSearching && isEmpty ? true : undefined,
            label: t('Search by test name'),
            value: searchValue,
            onChange: setSearchValue,
            trailingChildren: isSearching ? (jsxRuntime.jsx("span", { className: modules_d6bb9f7a.searchFieldCounter, children: t(`{{ count }} of {{ total }}`, {
                    count: matchingTestNameFilterItems.length,
                    total: testNameFilterItems.length
                }) })) : null
        }, children: isEmpty ? (jsxRuntime.jsx("div", { className: modules_d6bb9f7a.emptyState, "aria-live": "polite", children: emptyStateMessage })) : (jsxRuntime.jsx(cauldronReact.Listbox, { id: listboxId, "aria-label": t('Test names'), value: selectedFilterValues.map(String), multiselect: true, children: nodesToRender })) }));
}

const CONTROLS = new Map([
    [Properties.Rule.type, { Filter: RuleIssueFilter, keys: ['ruleId'] }],
    [Properties.Impact.type, { Filter: ImpactIssueFilter, keys: ['impact'] }],
    [
        Properties.ScanName.type,
        {
            Filter: ScanNameFilter,
            keys: ['scanId'],
            supportsValuesWithNoIssuesFound: true
        }
    ],
    [
        Properties.ScreenTitle.type,
        { Filter: ScreenTitleIssueFilter, keys: ['screenTitle'] }
    ],
    [Properties.PageInfo.type, { Filter: PageUrlFilter, keys: ['testUrl'] }],
    [Properties.TestName.type, { Filter: TestNameFilter, keys: ['testFilePath'] }]
]);
/**
 * Maps requested properties onto their filter components, in order, dropping
 * duplicates and any property without a control.
 */
function resolveFilterControls(controls) {
    const seenTypes = new Set();
    return controls.flatMap(property => {
        if (seenTypes.has(property.type)) {
            return [];
        }
        seenTypes.add(property.type);
        const control = CONTROLS.get(property.type);
        /* c8 ignore start */
        if (!control &&
            process.env.NODE_ENV !== 'production' &&
            process.env.NODE_ENV !== 'test') {
            // eslint-disable-next-line no-console
            console.warn(`There is no filter control for ${String(property.type)}.`);
        }
        /* c8 ignore stop */
        if (!control) {
            return [];
        }
        const { Filter, keys, supportsValuesWithNoIssuesFound } = control;
        return [
            {
                property,
                Filter,
                keys,
                valuesWithNoIssuesFound: supportsValuesWithNoIssuesFound
                    ? (property.valuesWithNoIssuesFound ?? [])
                    : []
            }
        ];
    });
}
/**
 * Whether some control picks only values with no issues found, so an empty
 * result means axe-clean. A picked value that is not one of them, such as a
 * stale id from the URL, never counts.
 */
const isFilteredToValuesWithNoIssuesFound = (issues, controls, filter) => resolveFilterControls(controls).some(({ keys, valuesWithNoIssuesFound }) => keys.some(key => {
    const picked = filter[key] ?? [];
    return (picked.length > 0 &&
        picked.every(value => valuesWithNoIssuesFound.some(option => option[key] === value)) &&
        !issues.some(issue => picked.includes(issue[key])));
}));

// Note: for now <Issues> only displays some filters — other controls will
// come later. IssuesFilterBar's registry supports more types than this;
// broadening this list broadens every <Issues> consumer.
const SUPPORTED_FILTER_TYPES = [
    Properties.ScreenTitle.type,
    Properties.ScanName.type
];
/**
 * Selects which of the configured properties get a filter control in
 * <Issues>. Matches on type, so a control can extend a `Properties` object
 * (for example with `valuesWithNoIssuesFound`). The property→component mapping itself
 * lives in IssuesFilterBar.
 */
function useIssueFiltersControls(configuration) {
    return React.useMemo(() => {
        const uniqueColumnTypes = new Set();
        return configuration.filter(property => {
            if (uniqueColumnTypes.has(property.type)) {
                return false;
            }
            uniqueColumnTypes.add(property.type);
            return SUPPORTED_FILTER_TYPES.includes(property.type);
        });
    }, [configuration]);
}

var modules_71d8192f = {"issueCount":"_issueCount_4665001d"};

function IssuesCount({ from, to, total, unit = 'rules', label, announce, className, ...props }) {
    const values = {
        currentPageNumber: from,
        totalPageNumber: to,
        totalRuleCount: total
    };
    return (jsxRuntime.jsx("span", { className: classnames__default.default(modules_71d8192f.issueCount, className), "aria-live": announce ? 'polite' : undefined, "aria-atomic": announce ? true : undefined, ...props, children: label ||
            (unit === 'groups' ? (total === 1 ? (jsxRuntime.jsx(Trans, { defaults: "Showing <strong>1</strong> to <strong>1</strong> of <strong>1</strong> group" })) : (jsxRuntime.jsx(Trans, { defaults: "Showing <strong>{{ currentPageNumber, number }}</strong> to <strong>{{ totalPageNumber, number }}</strong> of <strong>{{ totalRuleCount, number }}</strong> groups", values: values }))) : unit === 'issues' ? (total === 1 ? (jsxRuntime.jsx(Trans, { defaults: "Showing <strong>1</strong> to <strong>1</strong> of <strong>1</strong> issue" })) : (jsxRuntime.jsx(Trans, { defaults: "Showing <strong>{{ currentPageNumber, number }}</strong> to <strong>{{ totalPageNumber, number }}</strong> of <strong>{{ totalRuleCount, number }}</strong> issues", values: values }))) : total === 1 ? (jsxRuntime.jsx(Trans, { defaults: "Showing <strong>1</strong> to <strong>1</strong> of <strong>1</strong> rule" })) : (jsxRuntime.jsx(Trans, { defaults: "Showing <strong>{{ currentPageNumber, number }}</strong> to <strong>{{ totalPageNumber, number }}</strong> of <strong>{{ totalRuleCount, number }}</strong> rules", values: values }))) }));
}

const IssuesEmptyState = React.forwardRef(({ onClearFilters, headingLevel = 2, ...props }, ref) => {
    const { t } = useTranslation();
    const Heading = `h${headingLevel}`;
    return (jsxRuntime.jsx(cauldronReact.EmptyState, { ref: ref, ...props, heading: jsxRuntime.jsx(Heading, { children: t('No results match your applied filters') }), description: t('Try adjusting or clearing your applied filters.'), primaryActions: jsxRuntime.jsx(cauldronReact.Button, { variant: "primary", onClick: onClearFilters, children: t('Clear filters') }) }));
});
IssuesEmptyState.displayName = 'IssuesEmptyState';

let node;
let assertiveNode;
let politeNode;
let timeoutId;
let instanceCount = 0;
const Assertiveness = {
    Assertive: 'assertive',
    Polite: 'polite'
};
// Delay between clearing and announcing ensures assistive technologies detect the content change.
// Without this, rapid consecutive announcements or announcing the same message twice may be
// ignored as screen readers optimize by not announcing "unchanged" content. The delay forces
// a DOM mutation that screen readers will recognize as new content to announce.
const ANNOUNCEMENT_DELAY_TIMEOUT = 250;
const createLiveRegion = (assertiveness) => {
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', assertiveness);
    return liveRegion;
};
// Live regions must exist in the DOM before content changes occur for assistive technologies
// to announce them. We mount the container node on component initialization rather than
// using React's render cycle to ensure the region is established before any announcements.
// see: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Guides/Live_regions
const initializeLiveRegion = () => {
    if (typeof document !== 'undefined' && !node?.isConnected) {
        node = document.createElement('div');
        node.classList.add('Offscreen');
        document.body.prepend(node);
        assertiveNode = createLiveRegion(Assertiveness.Assertive);
        node.appendChild(assertiveNode);
        politeNode = createLiveRegion(Assertiveness.Polite);
        node.appendChild(politeNode);
    }
};
function useLiveAnnouncer() {
    React.useEffect(() => {
        instanceCount++;
        initializeLiveRegion();
        return () => {
            instanceCount--;
            if (instanceCount === 0) {
                /* c8 ignore next */
                node?.remove();
            }
            /* c8 ignore next */
            const isConnected = !node?.isConnected;
            if (isConnected) {
                node = null;
                assertiveNode = null;
                politeNode = null;
                instanceCount = 0;
            }
        };
    }, []);
    const clear = assertiveness => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        if (assertiveNode &&
            (!assertiveness || assertiveness === Assertiveness.Assertive)) {
            assertiveNode.innerHTML = '';
        }
        if (politeNode &&
            (!assertiveness || assertiveness === Assertiveness.Polite)) {
            politeNode.innerHTML = '';
        }
    };
    const announce = (announcement, assertiveness) => {
        // Reinitialize the live region if it was removed from the DOM but the hook is still active.
        // This can occur when the DOM node is unexpectedly removed by a third-party source.
        if (!node?.isConnected && instanceCount > 0) {
            initializeLiveRegion();
        }
        clear(assertiveness);
        timeoutId = setTimeout(() => {
            timeoutId = null;
            if (assertiveness === Assertiveness.Assertive && assertiveNode) {
                const textNode = document.createTextNode(announcement);
                assertiveNode.appendChild(textNode);
            }
            if (assertiveness === Assertiveness.Polite && politeNode) {
                const textNode = document.createTextNode(announcement);
                politeNode.appendChild(textNode);
            }
        }, ANNOUNCEMENT_DELAY_TIMEOUT);
    };
    return {
        announce,
        clear
    };
}

var modules_2b280b8b = {"drawer":"_drawer_7c05750b","header":"_header_7c05750b","heading":"_heading_7c05750b","sections":"_sections_7c05750b","footer":"_footer_7c05750b"};

function AllIssueFilters({ issues, controls, filter, onFilterChange, onOpenChange }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const handleChangeOpen = (isOpen) => {
        setOpen(isOpen);
        onOpenChange?.(isOpen);
    };
    const [staged, setStaged] = React.useState(filter);
    const headingId = `all-issue-filters-${utils.useId()}`;
    const resolved = resolveFilterControls(controls);
    const appliedCount = countAppliedFilters(filter);
    const stage = (keys, emitted) => setStaged(previous => {
        const next = { ...previous };
        for (const key of keys) {
            delete next[key];
        }
        return Object.assign(next, emitted ?? {});
    });
    const stagedCount = Object.values(staged).flat().length;
    const stagedResults = open && stagedCount
        ? issues.filter(issue => matchesIssueFilter(issue, staged)).length
        : null;
    const showResultsLabel = stagedResults === null
        ? t('Show results')
        : stagedResults === 1
            ? t('Show 1 result')
            : t('Show {{ resultCount, number }} results', {
                resultCount: stagedResults
            });
    const appliedFiltersLabel = appliedCount
        ? t('All filters ({{ filterCount }})', { filterCount: appliedCount })
        : t('All filters');
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsxs(cauldronReact.Button, { variant: "secondary", onClick: () => {
                    setStaged(filter);
                    handleChangeOpen(true);
                }, children: [jsxRuntime.jsx(cauldronReact.Icon, { type: "filter" }), appliedFiltersLabel] }), jsxRuntime.jsxs(cauldronReact.Drawer, { position: "right", open: open, onClose: () => handleChangeOpen(false), role: "dialog", "aria-modal": "true", "aria-labelledby": headingId, className: modules_2b280b8b.drawer, children: [jsxRuntime.jsxs("div", { className: modules_2b280b8b.header, children: [jsxRuntime.jsx("h2", { id: headingId, className: modules_2b280b8b.heading, children: t('All filters') }), jsxRuntime.jsxs(cauldronReact.Button, { variant: "secondary", onClick: () => handleChangeOpen(false), "aria-label": t('Close filters'), children: [jsxRuntime.jsx(cauldronReact.Icon, { type: "close" }), t('Close')] })] }), open && (jsxRuntime.jsx("div", { className: modules_2b280b8b.sections, children: resolved.map(({ property, Filter, keys, valuesWithNoIssuesFound }) => (jsxRuntime.jsx(Filter, { variant: "section", issues: issues, filter: staged, valuesWithNoIssuesFound: valuesWithNoIssuesFound, onFilterChange: emitted => stage(keys, emitted) }, property.type.toString()))) })), jsxRuntime.jsxs("div", { className: modules_2b280b8b.footer, children: [jsxRuntime.jsx(cauldronReact.Button, { variant: "secondary", onClick: () => handleChangeOpen(false), children: t('Cancel') }), jsxRuntime.jsx(cauldronReact.Button, { variant: "primary", onClick: () => {
                                    onFilterChange(staged);
                                    handleChangeOpen(false);
                                }, children: showResultsLabel })] })] })] }));
}

var modules_ff4b410f = {"AppliedIssueFilters":"_AppliedIssueFilters_4f42d559","filters":"_filters_4f42d559","label":"_label_4f42d559","button":"_button_4f42d559","appliedFilterKey":"_appliedFilterKey_4f42d559","appliedFilterlabel":"_appliedFilterlabel_4f42d559","closeIcon":"_closeIcon_4f42d559"};

function AppliedIssueFilterBadge({ filterKey, filterValue, onRemoveFilter, 
// Note: using buttonRef over forwardRef as forwardRef doesn't offer an easy way
// to combine generics while forwarding the ref. forwardRef is going away anyway in React 19.
buttonRefCallback, issues = [], valuesWithNoIssuesFound }) {
    const ref = React.useRef(null);
    const [hasOverflow, setHasOverflow] = React.useState(false);
    const { ariaLabel, badgeLabel, tooltipLabel, emptyFilterLabel, customDisplayLabel } = useFilterLabels(filterKey, filterValue, issues, valuesWithNoIssuesFound);
    const handleRemoveFilter = () => {
        onRemoveFilter(filterKey, filterValue);
    };
    const isEmptyValue = isEmpty(filterValue) ||
        filterValue === LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE;
    const displayBadgeValue = isEmptyValue
        ? emptyFilterLabel
        : (customDisplayLabel ?? filterValue);
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsxs(cauldronReact.Button, { ref: element => {
                    ref.current = element;
                    if (typeof buttonRefCallback === 'function') {
                        buttonRefCallback(element);
                    }
                }, className: modules_ff4b410f.button, variant: "badge", "aria-label": ariaLabel, onClick: handleRemoveFilter, children: [jsxRuntime.jsx(cauldronReact.BadgeLabel, { className: modules_ff4b410f.appliedFilterKey, children: badgeLabel }), jsxRuntime.jsx(cauldronReact.TextEllipsis, { className: modules_ff4b410f.appliedFilterlabel, onOverflowChange: setHasOverflow, hideTooltip: true, children: displayBadgeValue }), jsxRuntime.jsx(cauldronReact.Icon, { className: modules_ff4b410f.closeIcon, type: "close" })] }, filterKey), hasOverflow && (jsxRuntime.jsx(cauldronReact.Tooltip, { target: ref, association: "none", children: tooltipLabel }))] }));
}

function AppliedIssueFilter({ className, filter = {}, issues = [], valuesWithNoIssuesFound, onRemoveFilter, onClearFilters, ...props }) {
    const { t } = useTranslation();
    const appliedFiltersRef = React.useRef(new Map());
    if (!Object.keys(filter).length) {
        return null;
    }
    const handleRemoveFilter = (filterKey, filterValue) => {
        return () => {
            if (typeof onRemoveFilter === 'function') {
                onRemoveFilter(filterKey, filterValue);
            }
            const refKey = `${filterKey}-${String(filterValue)}`;
            const keys = Array.from(appliedFiltersRef.current.keys());
            const elementsToFocus = Array.from(appliedFiltersRef.current.values());
            // Focus on the first element unless the first element was the element removed
            elementsToFocus[refKey === keys[0] ? 1 : 0]?.focus();
        };
    };
    const appliedFiltersBadgesCount = Object.values(filter).flat().length;
    const handleClearAllFilters = () => {
        if (typeof onClearFilters === 'function') {
            onClearFilters();
        }
    };
    return (jsxRuntime.jsx("div", { className: classnames__default.default(modules_ff4b410f.AppliedIssueFilters, className), ...props, children: jsxRuntime.jsxs("div", { className: modules_ff4b410f.filters, children: [jsxRuntime.jsx("span", { className: modules_ff4b410f.label, children: t('Applied filters:') }), Object.entries(filter).map(([key, value]) => value.map(filterValue => (jsxRuntime.jsx(AppliedIssueFilterBadge, { buttonRefCallback: element => {
                        const refKey = `${key}-${String(filterValue)}`;
                        if (element) {
                            appliedFiltersRef.current.set(refKey, element);
                        }
                        else {
                            appliedFiltersRef.current.delete(refKey);
                        }
                    }, filterKey: key, filterValue: filterValue, onRemoveFilter: handleRemoveFilter(key, filterValue), issues: issues, valuesWithNoIssuesFound: valuesWithNoIssuesFound }, `${key}-${String(filterValue)}`)))), appliedFiltersBadgesCount >= 2 && (jsxRuntime.jsx(cauldronReact.Button, { className: modules_ff4b410f.button, variant: "secondary", onClick: handleClearAllFilters, children: t('Clear filters') }))] }) }));
}

var modules_a2363f64 = {"container":"_container_c5c1106d","label":"_label_c5c1106d","appliedFilters":"_appliedFilters_c5c1106d","compact":"_compact_c5c1106d"};

const IssuesFilterBar = React.forwardRef(({ issues, controls, filter, onFilterChange, onAllFiltersOpenChange, compact, className }, ref) => {
    const { t } = useTranslation();
    const { announce } = useLiveAnnouncer();
    const regionRef = React.useRef(null);
    const id = utils.useId();
    const labelId = `issue-filters-label-${id}`;
    const descriptionId = `issue-filters-${id}`;
    // Clearing every filter returns focus to the region; the announcement is
    // delayed with it so the focus move does not swallow it.
    const announceCleared = () => {
        setTimeout(() => {
            announce(t('No applied filters'), 'polite');
            regionRef.current?.focus();
        }, 0);
    };
    React.useImperativeHandle(ref, () => ({
        focusAndAnnounceCleared: announceCleared
    }));
    const filterControlsResolved = resolveFilterControls(controls);
    if (!filterControlsResolved.length) {
        return null;
    }
    const totalAppliedFilters = countAppliedFilters(filter);
    // The region's description and the live announcement have to report the
    // same three states, so both read this (dequelabs/launchpad#838).
    const filterCountLabel = (count) => count === 0
        ? t('No applied filters')
        : count === 1
            ? t('1 applied filter')
            : t('{{ filterCount }} applied filters', { filterCount: count });
    const announceCount = (count) => announce(filterCountLabel(count), 'polite');
    const handleEmit = (keys, emitted) => {
        const updated = { ...filter };
        for (const key of keys) {
            delete updated[key];
        }
        Object.assign(updated, emitted ?? {});
        announceCount(countAppliedFilters(updated));
        onFilterChange(updated, 'control');
    };
    const handleDrawerApply = (updated) => {
        announceCount(countAppliedFilters(updated));
        onFilterChange(updated, 'allFilters');
    };
    const handleRemove = (filterKey, filterValue) => {
        const remaining = filter[filterKey]?.filter(value => value !== filterValue);
        const updated = { ...filter, [filterKey]: remaining };
        if (!remaining?.length) {
            delete updated[filterKey];
        }
        // A removal always announces the remaining count; only emptying the
        // filter entirely also moves focus back to the region.
        if (Object.keys(updated).length) {
            announceCount(countAppliedFilters(updated));
        }
        else {
            announceCleared();
        }
        onFilterChange(updated, 'chip');
    };
    return (jsxRuntime.jsxs("div", { ref: regionRef, role: "region", "aria-labelledby": labelId, "aria-describedby": descriptionId, className: classnames__default.default(modules_a2363f64.container, { [modules_a2363f64.compact]: compact }, className), tabIndex: -1, children: [jsxRuntime.jsx(cauldronReact.Offscreen, { id: labelId, children: t('Issue filter') }), jsxRuntime.jsx(cauldronReact.Offscreen, { id: descriptionId, children: filterCountLabel(totalAppliedFilters) }), jsxRuntime.jsx("span", { className: modules_a2363f64.label, children: t('Filter by:') }), compact ? (jsxRuntime.jsx(AllIssueFilters, { issues: issues, controls: controls, filter: filter, onFilterChange: handleDrawerApply, onOpenChange: onAllFiltersOpenChange })) : (filterControlsResolved.map(({ property, Filter, keys, valuesWithNoIssuesFound }) => (jsxRuntime.jsx(Filter, { issues: issues, filter: filter, valuesWithNoIssuesFound: valuesWithNoIssuesFound, onFilterChange: emitted => handleEmit(keys, emitted) }, property.type.toString())))), jsxRuntime.jsx(AppliedIssueFilter, { className: modules_a2363f64.appliedFilters, filter: filter, issues: issues, valuesWithNoIssuesFound: filterControlsResolved.flatMap(({ valuesWithNoIssuesFound }) => valuesWithNoIssuesFound), onClearFilters: () => {
                    announceCleared();
                    onFilterChange({}, 'clearAll');
                }, onRemoveFilter: handleRemove })] }));
});
IssuesFilterBar.displayName = 'IssuesFilterBar';

const useIssueHeaders = (issues) => {
    const { t } = useTranslation();
    const issueHasUrlAndTitle = React.useMemo(() => issues.some(issue => issue.helpUrl && issue.testPageTitle), [issues]);
    const translatedPropertyHeaders = React.useMemo(() => ({
        [Properties.Rule.type]: t('Rule'),
        [Properties.DateTime.type]: t('Date discovered'),
        [Properties.Impact.type]: t('Impact'),
        [Properties.PageInfo.type]: issueHasUrlAndTitle
            ? t('Page title and URL')
            : t('URL'),
        [Properties.ScreenTitle.type]: t('Screen title'),
        [Properties.ScanName.type]: t('Scan name'),
        [Properties.Location.type]: t('Location'),
        [Properties.Status.type]: t('Status'),
        [Properties.Criteria.type]: t('Criteria'),
        [Properties.ElementSource.type]: t('Element source'),
        [Properties.DiscoveryMethod.type]: t('Discovery method'),
        [AggregateProperties.IssueCount.type]: t('Total issues'),
        [AggregateProperties.CriticalIssueCount.type]: t('Critical issues'),
        [AggregateProperties.NewIssueCount.type]: t('New issues'),
        [AggregateProperties.SeriousIssueCount.type]: t('Serious issues'),
        [AggregateProperties.PageCount.type]: t('Pages')
    }), [t, issueHasUrlAndTitle]);
    return translatedPropertyHeaders;
};

const useWCAGCheckpoints = (issues) => React.useMemo(() => issues.map(issue => ({
    ...issue,
    wcagCheckpoints: getCheckpoints(issue.tags).filter(isWCAGCheckpointTagInfo)
})), [issues]);

var modules_f26b87b7 = {"rule":"_rule_40b4f4f9"};

const IssuesDataList = ({ issues, properties, accessibilityStandard, rowOffset = 0, ...props }) => {
    const issuesWithWcagCheckpoints = useWCAGCheckpoints(issues);
    const translatedPropertyHeaders = useIssueHeaders(issues);
    const explicitLinkIndex = properties.findIndex(property => property.rowHeader);
    const linkIndex = explicitLinkIndex === -1 ? 0 : explicitLinkIndex;
    return (jsxRuntime.jsx("div", { ...props, children: issuesWithWcagCheckpoints.map((issueGroup, issueIndex) => {
            return (jsxRuntime.jsx(cauldronReact.DescriptionList, { collapsed: true, children: properties.map((property, propertyIndex) => {
                    return (jsxRuntime.jsxs(cauldronReact.DescriptionListItem, { className: 
                        // Allow for issue property specific styles to be applied, where
                        // needed. For example this allows for us to set a max-width for
                        // text values when a rule name is displayed.
                        modules_f26b87b7[property.type.description?.toLowerCase()] ?? undefined, children: [jsxRuntime.jsx(cauldronReact.DescriptionTerm, { children: property.label ?? translatedPropertyHeaders[property.type] }), jsxRuntime.jsx(cauldronReact.DescriptionDetails, { children: renderProperty(property, issueGroup, {
                                    renderLink: property.renderLink ?? propertyIndex === linkIndex,
                                    accessibilityStandard,
                                    rowNumber: rowOffset + issueIndex + 1
                                }) })] }, `${property.type.toString()}-${propertyIndex}-${issueGroup.ruleId}`));
                }) }, issueGroup.issueId || issueIndex));
        }) }));
};
IssuesDataList.displayName = 'IssuesDataList';

const IssuesDataTable = ({ issues, properties, accessibilityStandard, className, rowOffset = 0, sortDirection = SortDirections.None, sortProperty, onSort, ...props }) => {
    const translatedTableColumnHeaders = useIssueHeaders(issues);
    const explicitRowHeader = properties.findIndex(column => column.rowHeader);
    const rowHeaderIndex = explicitRowHeader === -1 ? 0 : explicitRowHeader;
    const gridColumns = properties.map(column => ({
        width: column.width ?? 'auto',
        maxWidth: column.maxWidth ?? 'auto',
        align: column.align ?? 'start'
    }));
    const handleSort = (type) => {
        return () => {
            if (typeof onSort === 'function') {
                onSort(type, sortDirection || SortDirections.None);
            }
        };
    };
    const getSortDirection = (property) => {
        if (!property.sortable) {
            return undefined;
        }
        return property.type === sortProperty ? sortDirection : SortDirections.None;
    };
    return (jsxRuntime.jsxs(cauldronReact.Table, { className: classnames__default.default(className), layout: "grid", columns: gridColumns, ...props, children: [jsxRuntime.jsx(cauldronReact.TableHead, { children: jsxRuntime.jsx(cauldronReact.TableRow, { children: properties.length &&
                        properties.map((property, columnIndex) => (jsxRuntime.jsx(cauldronReact.TableHeader, { scope: "col", sortDirection: getSortDirection(property), ...(property.sortable
                                ? {
                                    // Omit sortAscendingAnnouncement / sortDescendingAnnouncement:
                                    // on Cauldron 6.x they leak into each cell's accessible name; on
                                    // 7.x they're deprecated no-ops. Sort state is conveyed via
                                    // aria-sort.
                                    onSort: handleSort(property.type)
                                }
                                : {}), children: property.label ?? translatedTableColumnHeaders[property.type] }, `${property.type.toString()}-${columnIndex}`))) }) }), jsxRuntime.jsx(cauldronReact.TableBody, { children: issues.map((issue, rowIndex) => (jsxRuntime.jsx(cauldronReact.TableRow, { children: properties.map((column, columnIndex) => {
                        const key = `${issue.issueId || rowIndex}-${columnIndex}`;
                        const children = renderProperty(column, issue, {
                            renderLink: column.renderLink ?? columnIndex === rowHeaderIndex,
                            accessibilityStandard,
                            rowNumber: rowOffset + rowIndex + 1
                        });
                        return columnIndex === rowHeaderIndex ? (jsxRuntime.jsx(cauldronReact.TableHeader, { scope: "row", variant: "cell", children: children }, key)) : (jsxRuntime.jsx(cauldronReact.TableCell, { children: children }, key));
                    }) }, issue.issueId || rowIndex))) })] }));
};
IssuesDataTable.displayName = 'IssuesDataTable';

function IssuesTable({ issues, properties, compact, accessibilityStandard, rowOffset, sortProperty, sortDirection, onSort }) {
    if (compact) {
        return (jsxRuntime.jsx(IssuesDataList, { issues: issues, properties: properties, accessibilityStandard: accessibilityStandard, rowOffset: rowOffset }, `${sortProperty?.toString() || 'unknown'}-${sortDirection}`));
    }
    return (jsxRuntime.jsx(IssuesDataTable, { issues: issues, properties: properties, accessibilityStandard: accessibilityStandard, rowOffset: rowOffset, sortProperty: sortProperty, sortDirection: sortDirection, onSort: onSort }));
}

// Keeps the menu short enough to fit between the trigger's bottom edge and the
// viewport bottom in typical cases, reducing the need for floating-ui's flip
// middleware and helping the menu open below the trigger when space allows.
const OVERLAY_OFFSET = 4; // ActionMenu hardcodes offset 4 on its AnchoredOverlay
const VIEWPORT_BOTTOM_PADDING = 16;
function useSortLabels() {
    const { t } = useTranslation();
    return {
        [Properties.Rule.type]: {
            ascending: t('Rule A to Z'),
            descending: t('Rule Z to A')
        },
        [Properties.Impact.type]: {
            ascending: t('Impact critical to minor'),
            descending: t('Impact minor to critical')
        },
        [Properties.DateTime.type]: {
            ascending: t('Date discovered most recent to oldest'),
            descending: t('Date discovered oldest to most recent')
        },
        [Properties.ScreenTitle.type]: {
            ascending: t('Screen title A to Z'),
            descending: t('Screen title Z to A')
        },
        [Properties.ScanName.type]: {
            ascending: t('Scan name A to Z'),
            descending: t('Scan name Z to A')
        },
        [Properties.TestName.type]: {
            ascending: t('Test name A to Z'),
            descending: t('Test name Z to A')
        },
        [Properties.Location.type]: {
            ascending: t('Location A to Z'),
            descending: t('Location Z to A')
        },
        [Properties.PageInfo.type]: {
            ascending: t('Page title A to Z'),
            descending: t('Page title Z to A')
        },
        [Properties.Status.type]: {
            ascending: t('Status A to Z'),
            descending: t('Status Z to A')
        },
        [Properties.Criteria.type]: {
            ascending: t('Criteria 1.x to 4.x'),
            descending: t('Criteria 4.x to 1.x')
        },
        [Properties.ElementSource.type]: {
            ascending: t('Element source A to Z'),
            descending: t('Element source Z to A')
        },
        [Properties.DiscoveryMethod.type]: {
            ascending: t('Discovery method automatic to manual'),
            descending: t('Discovery method manual to automatic')
        },
        [AggregateProperties.IssueCount.type]: {
            ascending: t('Total issues most to least'),
            descending: t('Total issues least to most')
        },
        [AggregateProperties.CriticalIssueCount.type]: {
            ascending: t('Critical issues most to least'),
            descending: t('Critical issues least to most')
        },
        [AggregateProperties.SeriousIssueCount.type]: {
            ascending: t('Serious issues most to least'),
            descending: t('Serious issues least to most')
        },
        [AggregateProperties.NewIssueCount.type]: {
            ascending: t('New issues most to least'),
            descending: t('New issues least to most')
        },
        [AggregateProperties.PageCount.type]: {
            ascending: t('Page count most to least'),
            descending: t('Page count least to most')
        }
    };
}
const SortByActionMenu = ({ properties, sortProperty, sortDirection, onSort, style, ...props }) => {
    const { t } = useTranslation();
    const sortLabels = useSortLabels();
    const triggerRef = React.useRef(null);
    const [maxHeight, setMaxHeight] = React.useState(null);
    const sortableProperties = Array.from(new Set(properties
        .filter(property => !!property.sortable)
        .map(property => property.type)));
    React.useEffect(() => {
        const update = () => {
            const trigger = triggerRef.current;
            if (!trigger) {
                return;
            }
            const { bottom } = trigger.getBoundingClientRect();
            setMaxHeight(Math.max(0, window.innerHeight - bottom - OVERLAY_OFFSET - VIEWPORT_BOTTOM_PADDING));
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, []);
    if (!sortableProperties.length) {
        return null;
    }
    if (!document || !('body' in document)) {
        return null;
    }
    const handleSort = (nextSortColumn, nextSortDirection) => {
        if (typeof onSort === 'function') {
            return () => onSort(nextSortColumn, nextSortDirection);
        }
        return undefined;
    };
    return (jsxRuntime.jsx(cauldronReact.ActionMenu, { trigger: triggerProps => (jsxRuntime.jsxs(cauldronReact.Button, { variant: "secondary", ...triggerProps, ref: el => {
                // Forward to ActionMenu's internal ref (used for focus + click-outside)
                // and capture for measuring trigger position.
                triggerProps.ref.current = el;
                triggerRef.current = el;
            }, children: [jsxRuntime.jsx(cauldronReact.Icon, { type: "sort-triangle" }), sortProperty
                    ? sortLabels[sortProperty][sortDirection]
                    : t('Sort Field'), jsxRuntime.jsx(cauldronReact.Icon, { type: "chevron-down" })] })), portal: document.body, ...props, style: { maxHeight: maxHeight ?? undefined, ...style }, children: jsxRuntime.jsx(cauldronReact.ActionList, { selectionType: "single", children: sortableProperties.map((sortablePropertyType, index) => {
                const labels = sortLabels[sortablePropertyType];
                if (!labels) {
                    throw new Error(`Unknown column: ${sortablePropertyType.toString()}`);
                }
                const key = sortablePropertyType.toString();
                return (jsxRuntime.jsxs(React.Fragment, { children: [index > 0 && jsxRuntime.jsx(cauldronReact.ActionListSeparator, {}), jsxRuntime.jsx(cauldronReact.ActionListItem, { actionKey: `${key}-asc`, selected: sortablePropertyType === sortProperty &&
                                sortDirection === SortDirections.Ascending, onAction: handleSort(sortablePropertyType, SortDirections.Ascending), children: labels.ascending }), jsxRuntime.jsx(cauldronReact.ActionListItem, { actionKey: `${key}-desc`, selected: sortablePropertyType === sortProperty &&
                                sortDirection === SortDirections.Descending, onAction: handleSort(sortablePropertyType, SortDirections.Descending), children: labels.descending })] }, key));
            }) }) }));
};

var modules_ab4849b6 = {"Issues":"_Issues_cb705deb","sortByLabel":"_sortByLabel_cb705deb","issueSortBy":"_issueSortBy_cb705deb","issueFilters":"_issueFilters_cb705deb","stack":"_stack_cb705deb","issueCount":"_issueCount_cb705deb","compact":"_compact_cb705deb"};

const Issues = ({ issues, properties, accessibilityStandard, issuesPaginationLabel, className, paginationSize = Infinity, displayFilterControls, initialSort, groupBy, onIssuesFilterChange, filter: filterProp, showFiltersEmptyState = false, headingLevel, sortProperty, sortDirection, onSort, ...props }) => {
    const { t } = useTranslation();
    const issueCountInfoId = `issue-count-${utils.useId()}`;
    const [internalFilter, setInternalFilter] = React.useState({});
    const filter = filterProp ?? internalFilter;
    const filterBarRef = React.useRef(null);
    const list = useIssuesList({
        issues,
        properties,
        filter,
        groupBy,
        initialSort,
        paginationSize,
        sortProperty,
        sortDirection,
        onSort
    });
    const filterControls = useIssueFiltersControls(Array.isArray(displayFilterControls)
        ? displayFilterControls
        : displayFilterControls === true ||
            typeof displayFilterControls === 'undefined'
            ? properties
            : []);
    const hasIssues = issues.length > 0;
    const cleanScanResults = list.total === 0 &&
        isFilteredToValuesWithNoIssuesFound(issues, filterControls, filter);
    // Only reachable when issues exist — with none at all the AxeClean branch
    // renders instead — so `total === 0` here means filters matched nothing.
    const noFilterResults = showFiltersEmptyState && list.total === 0 && !cleanScanResults;
    const handleFilterChange = (updated) => {
        if (typeof filterProp === 'undefined') {
            setInternalFilter(updated);
        }
        if (typeof onIssuesFilterChange === 'function') {
            onIssuesFilterChange(updated);
        }
    };
    // Mirrors the filter bar's own "Clear all" behaviour: the empty state's
    // button is about to unmount along with the rest of the empty state, so
    // focus and the announcement must be driven from here instead.
    const handleEmptyStateClearFilters = () => {
        filterBarRef.current?.focusAndAnnounceCleared();
        handleFilterChange({});
    };
    const countLabel = (jsxRuntime.jsx(IssuesCount, { className: modules_ab4849b6.issueCount, from: list.from, to: list.to, total: list.total, label: issuesPaginationLabel }));
    // The table is surrounded by a panel, but the compacted data list should not be
    const WrappingComponent = list.compact ? 'div' : cauldronReact.Panel;
    return (jsxRuntime.jsx(WrappingComponent, { ref: list.containerRef, className: classnames__default.default(modules_ab4849b6.Issues, className, {
            [modules_ab4849b6.compact]: list.compact
        }), "aria-label": t('Issues'), "aria-describedby": hasIssues && !noFilterResults && !cleanScanResults
            ? issueCountInfoId
            : undefined, ...props, role: "region", children: !hasIssues ? (jsxRuntime.jsx(cauldronReact.Panel, { children: jsxRuntime.jsx(AxeClean, { headingLevel: headingLevel }) })) : (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [list.compact && properties.some(property => !!property.sortable) && (jsxRuntime.jsxs("div", { className: modules_ab4849b6.issueSortBy, children: [jsxRuntime.jsx("span", { className: modules_ab4849b6.sortByLabel, children: t('Sort by:') }), jsxRuntime.jsx(SortByActionMenu, { properties: properties, sortProperty: list.sortProperty, sortDirection: list.sortDirection, onSort: list.setSort })] })), jsxRuntime.jsx(IssuesFilterBar, { ref: filterBarRef, className: modules_ab4849b6.issueFilters, issues: issues, controls: filterControls, filter: filter, onFilterChange: handleFilterChange }), jsxRuntime.jsxs("div", { className: modules_ab4849b6.stack, children: [jsxRuntime.jsx(IssuesCount, { className: classnames__default.default(modules_ab4849b6.issueCount, {
                                Offscreen: noFilterResults || cleanScanResults
                            }), id: issueCountInfoId, from: list.from, to: list.to, total: list.total, label: cleanScanResults
                                ? t('No automated accessibility issues detected!')
                                : noFilterResults
                                    ? t('No results match your applied filters')
                                    : issuesPaginationLabel, announce: !list.showPagination }), cleanScanResults ? (jsxRuntime.jsx(AxeClean, { headingLevel: headingLevel })) : noFilterResults ? (jsxRuntime.jsx(IssuesEmptyState, { headingLevel: headingLevel, onClearFilters: handleEmptyStateClearFilters })) : (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx(IssuesTable, { issues: list.pageIssues, properties: properties, compact: list.compact, accessibilityStandard: accessibilityStandard, sortProperty: list.sortProperty, sortDirection: list.sortDirection, onSort: list.toggleSort }), list.showPagination && (jsxRuntime.jsx(cauldronReact.Pagination, { ...list.pagination, statusLabel: countLabel }))] }))] })] })) }));
};

var modules_b1c93370 = {"IssuesListSummary":"_IssuesListSummary_08c4ae33","divider":"_divider_08c4ae33","score":"_score_08c4ae33","scoreContainer":"_scoreContainer_08c4ae33","title":"_title_08c4ae33","inlineStat":"_inlineStat_08c4ae33","impactList":"_impactList_08c4ae33","impactItem":"_impactItem_08c4ae33","stackedStat":"_stackedStat_08c4ae33","definitionTerm":"_definitionTerm_08c4ae33","bodyValue":"_bodyValue_08c4ae33","bigValue":"_bigValue_08c4ae33"};

const SCORE_DOCS_URL = 'https://docs.deque.com/devtools-mobile/en/accessibility-score';
/** The card shell. Draws a divider between each stat it is given. */
const IssuesSummaryPanel = ({ children, className, ...props }) => {
    // toArray drops the `false` that a `{condition && <Stat />}` child leaves.
    const shown = React.Children.toArray(children);
    return (jsxRuntime.jsx(cauldronReact.Panel, { ...props, className: classnames__default.default(modules_b1c93370.IssuesListSummary, className), children: shown.map((stat, index) => (jsxRuntime.jsxs(React.Fragment, { children: [index > 0 && jsxRuntime.jsx("hr", { role: "presentation", className: modules_b1c93370.divider }), stat] }, index))) }));
};
const IssuesSummaryStat = ({ label, value, variant = 'default' }) => {
    const featured = variant === 'featured';
    return (jsxRuntime.jsxs("dl", { className: featured ? modules_b1c93370.inlineStat : modules_b1c93370.stackedStat, children: [jsxRuntime.jsx("dt", { className: modules_b1c93370.definitionTerm, children: label }), jsxRuntime.jsx("dd", { className: featured ? modules_b1c93370.bigValue : modules_b1c93370.bodyValue, children: value })] }));
};
const IssuesSummaryImpactStats = ({ counts }) => {
    const { t } = useTranslation();
    return (jsxRuntime.jsx("dl", { className: modules_b1c93370.impactList, children: [
            { label: t('Critical'), value: counts.critical },
            { label: t('Serious'), value: counts.serious },
            { label: t('Moderate'), value: counts.moderate },
            { label: t('Minor'), value: counts.minor }
        ].map(({ label, value }) => (jsxRuntime.jsxs("div", { className: modules_b1c93370.impactItem, children: [jsxRuntime.jsx("dt", { className: modules_b1c93370.definitionTerm, children: label }), jsxRuntime.jsx("dd", { className: modules_b1c93370.bodyValue, children: value })] }, label))) }));
};
const IssuesSummaryScore = ({ value }) => {
    const { t } = useTranslation();
    const titleId = utils.useId();
    return (jsxRuntime.jsxs("div", { className: modules_b1c93370.score, children: [jsxRuntime.jsxs("div", { className: modules_b1c93370.scoreContainer, children: [jsxRuntime.jsx("span", { id: titleId, className: modules_b1c93370.title, children: t('Accessibility score') }), jsxRuntime.jsxs(cauldronReact.Link, { href: SCORE_DOCS_URL, target: "_blank", rel: "noopener noreferrer", "aria-label": t('Learn what an accessibility score is in a new tab'), children: [t("What's this?"), " ", jsxRuntime.jsx(cauldronReact.Icon, { type: "external-link", "aria-hidden": "true" })] })] }), jsxRuntime.jsxs("span", { className: modules_b1c93370.bigValue, "aria-labelledby": titleId, children: [value.toFixed(0), "%"] })] }));
};
/**
 * The card as the pre-grouping issues list shows it. Compose
 * `IssuesSummaryPanel` directly when you need a different set or order.
 */
const IssuesListSummary = ({ accessibilityScore, totalScans, totalIssues, impactCounts, ...props }) => {
    const { t } = useTranslation();
    return (jsxRuntime.jsxs(IssuesSummaryPanel, { ...props, children: [Number.isFinite(accessibilityScore) && (jsxRuntime.jsx(IssuesSummaryScore, { value: accessibilityScore })), jsxRuntime.jsx(IssuesSummaryStat, { label: t('Total scans'), value: totalScans, variant: "featured" }), jsxRuntime.jsx(IssuesSummaryImpactStats, { counts: impactCounts }), jsxRuntime.jsx(IssuesSummaryStat, { label: t('Total issues'), value: totalIssues })] }));
};
IssuesListSummary.displayName = 'IssuesListSummary';

var modules_37a5ff73 = {"skeleton":"_skeleton_c19d5abd","uiSkeletonShimmer":"_uiSkeletonShimmer_c19d5abd","rect":"_rect_c19d5abd","text":"_text_c19d5abd","circle":"_circle_c19d5abd"};

const Skeleton = ({ width, height, borderRadius, variant = 'rect', className, style, 'aria-hidden': ariaHidden = true, ...rest }) => (jsxRuntime.jsx("span", { className: classnames__default.default(modules_37a5ff73.skeleton, modules_37a5ff73[variant], className), style: {
        ...style,
        ...(width !== undefined && { width }),
        ...(height !== undefined && { height }),
        ...(borderRadius !== undefined && { borderRadius })
    }, "aria-hidden": ariaHidden, ...rest }));

const supportedLocales = ['en', 'fr', 'it', 'de', 'es', 'ja', 'fi'];

var modules_54a334e8 = {"container":"_container_1bcf8300"};

const CriteriaFilter = React__default.default.forwardRef(({ onClick }, ref) => {
    const { t } = useTranslation();
    return (jsxRuntime.jsxs(cauldronReact.Button, { ref: ref, variant: "secondary", onClick: onClick, children: [t('WCAG'), jsxRuntime.jsx(cauldronReact.Icon, { type: "chevron-down" })] }));
});
CriteriaFilter.displayName = 'CriteriaFilter';

const Container = ({ issues }) => {
    const onFilterChange = () => { };
    return (jsxRuntime.jsxs("div", { className: modules_54a334e8.container, children: [jsxRuntime.jsx(RuleIssueFilter, { issues: issues, onFilterChange: onFilterChange }), jsxRuntime.jsx(ImpactIssueFilter, { issues: issues, onFilterChange: onFilterChange }), jsxRuntime.jsx(CriteriaFilter, {}), jsxRuntime.jsx(PageUrlFilter, { issues: issues, onFilterChange: onFilterChange }), jsxRuntime.jsx(ScreenTitleIssueFilter, { issues: issues, onFilterChange: onFilterChange })] }));
};

const FILTER_VALUE_DELIMITER = ',';
/**
 * Serializes a single issue filter value to a URL-safe string representation
 */
function serializeIssuesFilterValue(value) {
    // "Blank" values could potentially be a special filter value, null, or undefined
    // and they will all need to be serialized to an empty string for its URL representation
    if (value === LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE ||
        value === null ||
        typeof value === 'undefined') {
        return '';
    }
    if (Array.isArray(value)) {
        return value.map(serializeIssuesFilterValue).join(FILTER_VALUE_DELIMITER);
    }
    if (typeof value === 'object') {
        throw new Error('Objects values are currently unsupported in issue filter serialization.');
    }
    return encodeURIComponent(String(value));
}
function decodeQueryStringPart(part) {
    try {
        return decodeURIComponent(part);
    }
    catch {
        return part;
    }
}
function deserializeIssuesFilterValue(value) {
    if (value === '') {
        return LAUNCHPAD_UI_ISSUES_EMPTY_FILTER_VALUE;
    }
    if (value.indexOf(FILTER_VALUE_DELIMITER) >= 0) {
        return value
            .split(FILTER_VALUE_DELIMITER)
            .map(deserializeIssuesFilterValue);
    }
    return decodeQueryStringPart(value);
}
/**
 * Splits a raw query string into its key/value pairs, leaving every value
 * encoded so the literal `,` delimiter between array values stays
 * distinguishable from an escaped `%2C` within a single value.
 */
function parseEncodedSearchEntries(search) {
    return search
        .replace(/^\?/, '')
        .split('&')
        .filter(pair => pair !== '')
        .map((pair) => {
        const delimiterIndex = pair.indexOf('=');
        if (delimiterIndex === -1) {
            return [decodeQueryStringPart(pair), ''];
        }
        return [
            decodeQueryStringPart(pair.slice(0, delimiterIndex)),
            pair.slice(delimiterIndex + 1)
        ];
    });
}
/**
 * Joins already-encoded key/value pairs into a query string. Values are written
 * verbatim because `URLSearchParams.toString()` would escape the `,` delimiter,
 * collapsing an array value into a single value.
 */
function buildEncodedSearchString(entries) {
    if (entries.length === 0) {
        return '';
    }
    return `?${entries.map(([key, value]) => `${key}=${value}`).join('&')}`;
}
/**
 * Serializes a issues filter object into URL search parameters using only valid keys.
 */
function serializeIssuesFilterToURLSearchParams(filter, allowedKeys = []) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) {
        if (!allowedKeys.includes(key)) {
            continue;
        }
        const serializedValue = serializeIssuesFilterValue(value);
        params.set(key, serializedValue);
    }
    return params;
}
/**
 * De-serializes an encoded URL query string back into an issues filter object
 * using only valid keys.
 */
function deserializeURLSearchParamsToIssuesFilter(search, allowedKeys = []) {
    if (!search) {
        return {};
    }
    const issuesFilter = {};
    for (const [key, value] of parseEncodedSearchEntries(search)) {
        if (!allowedKeys.includes(key)) {
            continue;
        }
        const deserializedValue = deserializeIssuesFilterValue(value);
        // @ts-expect-error the value may not match the expected key, this is a risk
        // from de-serializing from query params without schema validation
        issuesFilter[key] = Array.isArray(deserializedValue)
            ? deserializedValue
            : [deserializedValue];
    }
    return issuesFilter;
}

const useIssuesFilter = (issues, initialIssuesFilters = {}) => {
    const [issuesFilter, setIssuesFilters] = React.useState(initialIssuesFilters);
    const filteredIssues = React.useMemo(() => {
        if (!issuesFilter || !Object.keys(issuesFilter).length) {
            return issues;
        }
        return issues.filter(issue => matchesIssueFilter(issue, issuesFilter));
    }, [issuesFilter, issues]);
    const clearIssuesFilters = () => setIssuesFilters({});
    return {
        issues: filteredIssues,
        issuesFilter,
        setIssuesFilters,
        clearIssuesFilters
    };
};

// Exporting history so it can be utilized by both the hook
// and to utilize in tests for mock
var history = history$1.createBrowserHistory();

function getQueryString(searchParams) {
    return buildEncodedSearchString(Array.from(searchParams.entries()));
}
/**
 * A React hook that synchronizes issues filter state with URL search parameters.
 *
 * This hook provides bidirectional synchronization between filtering state and URL query parameters,
 * allowing filters to be persisted in the URL for sharing, bookmarking, and browser history navigation.
 */
function useIssuesFilterURLSearchParamsState(initialState = {}, { writeToUrl = false, readFromUrl = false, allowedKeys = [] } = {}) {
    const [filter, setFilters] = React.useState(initialState || {});
    // The `history` package does not automatically update the location after initialisation
    // so we need to update it manually with correct window.location.
    // Sync history with browser's actual location.
    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            const windowPath = window.location.pathname +
                window.location.search +
                window.location.hash;
            const historyPath = history.location.pathname +
                history.location.search +
                history.location.hash;
            if (windowPath !== historyPath) {
                // Sync history with window location
                history.replace(windowPath);
            }
        }
    }, []);
    React.useEffect(() => {
        let unlisten;
        if (readFromUrl) {
            // Sync filters with the current url
            setFilters(deserializeURLSearchParamsToIssuesFilter(location.search, allowedKeys));
            // Ensure that url changes update the filter
            unlisten = history.listen(({ location }) => {
                setFilters(deserializeURLSearchParamsToIssuesFilter(location.search, allowedKeys));
            });
        }
        return unlisten;
    }, [readFromUrl]);
    const updateIssuesFilters = (issuesFilter, { replace = false } = {}) => {
        if (writeToUrl) {
            const nextSearchParams = serializeIssuesFilterToURLSearchParams(issuesFilter, allowedKeys);
            const currentLocation = typeof window !== 'undefined' ? window.location : history.location;
            const currentSearchParams = new URLSearchParams();
            for (const [key, value] of parseEncodedSearchEntries(currentLocation.search)) {
                currentSearchParams.append(key, value);
            }
            // Merge filter parameters with existing URL parameters.
            // This preserves non-filter query params (e.g., utm_source, pagination, etc.)
            // while updating only the filter-related parameters.
            for (const [key, value] of nextSearchParams.entries()) {
                currentSearchParams.set(key, value);
            }
            // If an existing parameter does not exist in the updated filter we need to
            // remove it from the current URL search params
            const nextSearchKeys = Array.from(nextSearchParams.keys());
            const currentSearchKeys = Array.from(currentSearchParams.keys());
            const diff = currentSearchKeys.filter(key => !nextSearchKeys.includes(key) &&
                allowedKeys.includes(key));
            diff.forEach(key => currentSearchParams.delete(key));
            if (getQueryString(currentSearchParams) !== currentLocation.search) {
                const nextPath = `${currentLocation.pathname}${getQueryString(currentSearchParams)}`;
                if (replace) {
                    history.replace(nextPath);
                }
                else {
                    history.push(nextPath);
                }
            }
        }
        setFilters(issuesFilter);
    };
    const searchParams = serializeIssuesFilterToURLSearchParams(filter, allowedKeys);
    return {
        filter,
        setFilter: updateIssuesFilters,
        searchParams,
        queryString: getQueryString(searchParams)
    };
}

exports.AggregateProperties = AggregateProperties;
exports.AppliedIssueFilters = AppliedIssueFilter;
exports.AxeClean = AxeClean;
exports.CriteriaFilter = CriteriaFilter;
exports.CustomProperty = CustomProperty;
exports.GroupByProperties = GroupByProperties;
exports.ISSUES_LIST_BREAKPOINT_PX = ISSUES_LIST_BREAKPOINT_PX;
exports.ImpactFilter = ImpactIssueFilter;
exports.Issue = Issue;
exports.IssueActions = Actions;
exports.IssueDetails = IssueDetails;
exports.IssueFilters = Container;
exports.IssueFlyout = IssueFlyout;
exports.IssueHeader = IssueHeader;
exports.IssueLayout = IssueLayout;
exports.IssueMetadata = IssueMetadata;
exports.IssuePage = IssuePage;
exports.Issues = Issues;
exports.IssuesCount = IssuesCount;
exports.IssuesDataList = IssuesDataList;
exports.IssuesDataTable = IssuesDataTable;
exports.IssuesEmptyState = IssuesEmptyState;
exports.IssuesFilterBar = IssuesFilterBar;
exports.IssuesListSummary = IssuesListSummary;
exports.IssuesSummaryImpactStats = IssuesSummaryImpactStats;
exports.IssuesSummaryPanel = IssuesSummaryPanel;
exports.IssuesSummaryScore = IssuesSummaryScore;
exports.IssuesSummaryStat = IssuesSummaryStat;
exports.IssuesTable = IssuesTable;
exports.LocationRenderFunction = LocationRenderFunction;
exports.PageUrlFilter = PageUrlFilter;
exports.Properties = Properties;
exports.RuleDetails = RuleDetails;
exports.RuleFilter = RuleIssueFilter;
exports.ScanNameFilter = ScanNameFilter;
exports.ScreenTitleFilter = ScreenTitleIssueFilter;
exports.ScreenshotConfiguration = ScreenshotConfiguration;
exports.Skeleton = Skeleton;
exports.SortByActionMenu = SortByActionMenu;
exports.SortDirections = SortDirections;
exports.TestNameFilter = TestNameFilter;
exports.Trans = Trans;
exports.buildEncodedSearchString = buildEncodedSearchString;
exports.configure = configure;
exports.deserializeURLSearchParamsToIssuesFilter = deserializeURLSearchParamsToIssuesFilter;
exports.getCheckpoints = getCheckpoints;
exports.i18n = instance;
exports.isFilteredToValuesWithNoIssuesFound = isFilteredToValuesWithNoIssuesFound;
exports.isWCAGCheckpointTagInfo = isWCAGCheckpointTagInfo;
exports.matchesIssueFilter = matchesIssueFilter;
exports.parseEncodedSearchEntries = parseEncodedSearchEntries;
exports.resolveLocation = resolveLocation;
exports.supportedLocales = supportedLocales;
exports.useAccessibilityStandards = useAccessibilityStandards;
exports.useAggregateIssues = useAggregateIssues;
exports.useAriaIsolation = useAriaIsolation;
exports.useCSSPropertyValue = useCSSPropertyValue;
exports.useCheckpointsTagInfo = useCheckpointsTagInfo;
exports.useIntersectionObserver = useIntersectionObserver;
exports.useIssueHeaders = useIssueHeaders;
exports.useIssuesFilter = useIssuesFilter;
exports.useIssuesFilterURLSearchParamsState = useIssuesFilterURLSearchParamsState;
exports.useIssuesList = useIssuesList;
exports.useIssuesSort = useIssuesSort;
exports.useLiveAnnouncer = useLiveAnnouncer;
exports.useResizeObserver = useResizeObserver;
exports.useSharedRef = useSharedRef;
exports.useTranslation = useTranslation;
exports.useWCAGCheckpoints = useWCAGCheckpoints;
exports.useWCAGCheckpointsTagInfo = useWCAGCheckpointsTagInfo;
exports.useWCAGStandard = useWCAGStandard;
